/*******************************************************************************************************
 **                                                                                                    **
 **                                             VARIABLES                                              **
 **                                                                                                    **
 *******************************************************************************************************/

// Adam test!!

// Vehicle schema - single source of truth for all vehicle fields
const VEHICLE_SCHEMA = {
	ymm: null,
	fitmentYmm: null,
	vin: null,
	paintCode: null,
	year: null,
	make: null,
	model: null,
	submodel: null,
	engine: null,
	bodyType: null,
	decodedBodyTypes: [],
	state: null,
	plate: null,
	decodedByBumper: null,
	lastCollectionImage: null,
	troublesome_format: null,
	troublesome_description: null,
};

const BANNED_PAINT_CODES = [
	{
		code: "S2G",
		label: "Matte Gray",
		alternativeCode: "SG2",
		alternativeLabel: "Gloss Gray",
	},
	{
		code: "P8U",
		label: "Matte Blue",
		alternativeCode: null,
		alternativeLabel: null,
	},
];

function normalizeMakeToken(value) {
	return String(value || "")
		.toLowerCase()
		.replace(/[^a-z0-9]/g, "");
}

function makeListContains(list, make) {
	const target = normalizeMakeToken(make);
	if (!target) return false;
	return String(list || "")
		.split(",")
		.map(normalizeMakeToken)
		.some((item) => item && (item === target || target.includes(item)));
}

function isBumperDotcomMake(make) {
	return makeListContains(bumperMakesGlobal || window.BUMPERDOTCOM_MAKES_LIST, make);
}

function isMatchPaintByVinCode(value) {
	const v = String(value || "").trim();
	return (
		v.toUpperCase() === ".VIN" ||
		v.toUpperCase() === "VIN" ||
		/match\s+(paint\s+)?by\s+vin/i.test(v) ||
		/match\s+paint\s+by\s+vin/i.test(v)
	);
}

function toPaintVehicleHandle(value) {
	return String(value || "")
		.toLowerCase()
		.replace(/[./]/g, "-")
		.replace(/\s+/g, "-")
		.replace(/[^a-z0-9-]/g, "")
		.replace(/--+/g, "-")
		.replace(/-+$/, "");
}

function buildVehiclePaintHandles(vehicle) {
	if (!vehicle) return [];
	const handles = [];
	const addHandle = (value) => {
		const handle = toPaintVehicleHandle(value);
		if (handle && handles.indexOf(handle) === -1) handles.push(handle);
	};

	addHandle(vehicle.fitmentYmm);
	addHandle(vehicle.ymm);
	if (vehicle.year && vehicle.make && vehicle.model) {
		addHandle([vehicle.year, vehicle.make, vehicle.model].join(" "));
	}
	if (vehicle.year && vehicle.make && vehicle.model && vehicle.submodel) {
		addHandle(
			[vehicle.year, vehicle.make, vehicle.model, vehicle.submodel].join(" "),
		);
	}

	return handles;
}

function normalizeVehiclePaintCodeToken(value) {
	return String(value || "")
		.split("+")[0]
		.trim()
		.toLowerCase()
		.replace(/[./]/g, "-")
		.replace(/\s+/g, "-")
		.replace(/[^a-z0-9-]/g, "")
		.replace(/--+/g, "-")
		.replace(/^-+|-+$/g, "");
}

function getVehiclePaintCodeAliases(value) {
	const normalized = normalizeVehiclePaintCodeToken(value);
	if (!normalized) return [];
	const aliases = [normalized];
	if (normalized.indexOf("wa") === 0 && normalized.length > 2) {
		aliases.push(normalized.slice(2));
	} else {
		aliases.push("wa" + normalized);
	}
	return aliases.filter((alias, index) => aliases.indexOf(alias) === index);
}

function getAuthoritativePaintCodesForVehicle(vehicle) {
	const map = window.__PARTIFY_YMM_PAINT_MAP;
	if (!map || typeof map !== "object") return null;
	const handles = buildVehiclePaintHandles(vehicle);
	const matchedHandle = handles.find(
		(handle) => Array.isArray(map[handle]) && map[handle].length,
	);
	return matchedHandle ? map[matchedHandle] : null;
}

function isPaintCodeValidForVehicle(vehicle, paintCode) {
	if (!paintCode || isMatchPaintByVinCode(paintCode)) return true;
	const paintCodes = getAuthoritativePaintCodesForVehicle(vehicle);
	if (!paintCodes) return null;
	const targets = getVehiclePaintCodeAliases(paintCode);
	if (!targets.length) return false;
	return paintCodes.some((code) => {
		const aliases = getVehiclePaintCodeAliases(code);
		return targets.some((target) => aliases.indexOf(target) !== -1);
	});
}

const authoritativeVehiclePaintCodeCache = {};
const AUTHORITATIVE_PAINT_LOOKUP_TIMEOUT_MS = 3500;

function getVehiclePaintCodeLookupConfig() {
	if (window.shopCurrency !== "USD") return null;
	return {
		url: "/api/2024-01/graphql.json",
		token: "12ccfcd74b8bc4e56ce0762f51fcf438",
	};
}

async function fetchAuthoritativePaintCodesForVehicleHandle(handle) {
	if (!handle) return null;
	if (Object.prototype.hasOwnProperty.call(authoritativeVehiclePaintCodeCache, handle)) {
		return authoritativeVehiclePaintCodeCache[handle];
	}
	const config = getVehiclePaintCodeLookupConfig();
	if (!config) return null;
	const codes = [];
	let fallbackValue = "";
	let cursor = null;
	let hasNextPage = true;
	let guard = 0;
	try {
		while (hasNextPage && guard < 50) {
			guard += 1;
			const after = cursor ? ", after: " + JSON.stringify(cursor) : "";
			const query =
				'{ metaobject(handle: {type: "year_make_model_lookup", handle: ' +
				JSON.stringify(handle) +
				'}) { pc: field(key: "paint_codes") { value references(first: 250' +
				after +
				') { pageInfo { hasNextPage endCursor } nodes { ... on Metaobject { code: field(key: "paint_code") { value } } } } } } }';
			const controller =
				typeof AbortController !== "undefined" ? new AbortController() : null;
			const timeoutId = controller
				? setTimeout(
						() => controller.abort(),
						AUTHORITATIVE_PAINT_LOOKUP_TIMEOUT_MS,
					)
				: null;
			let response;
			try {
				response = await fetch(config.url, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"X-Shopify-Storefront-Access-Token": config.token,
					},
					body: JSON.stringify({ query }),
					signal: controller?.signal,
				});
			} finally {
				if (timeoutId) clearTimeout(timeoutId);
			}
			const json = await response.json();
			const field = json?.data?.metaobject?.pc;
			if (!field) break;
			const refs = field.references?.nodes || [];
			if (refs.length) {
				codes.push(
					...refs
						.map((node) => node?.code?.value)
						.filter(Boolean),
				);
			} else if (!cursor && field.value) {
				fallbackValue = field.value;
			}
			const pageInfo = field.references?.pageInfo;
			cursor = pageInfo?.endCursor || null;
			hasNextPage = Boolean(pageInfo?.hasNextPage && cursor);
		}
		if (!codes.length && fallbackValue) {
			try {
				const parsed = JSON.parse(fallbackValue);
				if (Array.isArray(parsed)) codes.push(...parsed);
			} catch (error) {
				codes.push(...fallbackValue.split(/[\s,]+/));
			}
		}
	} catch (error) {
		authoritativeVehiclePaintCodeCache[handle] = null;
		return null;
	}
	const normalizedCodes = codes.map((code) => String(code).trim()).filter(Boolean);
	authoritativeVehiclePaintCodeCache[handle] = normalizedCodes.length
		? normalizedCodes
		: null;
	return authoritativeVehiclePaintCodeCache[handle];
}

async function fetchAuthoritativePaintCodesForVehicle(vehicle) {
	const localCodes = getAuthoritativePaintCodesForVehicle(vehicle);
	if (localCodes) return localCodes;
	const handles = buildVehiclePaintHandles(vehicle);
	for (const handle of handles) {
		const codes = await fetchAuthoritativePaintCodesForVehicleHandle(handle);
		if (codes) return codes;
	}
	return null;
}

async function isPaintCodeValidForVehicleAsync(vehicle, paintCode) {
	const localValidation = isPaintCodeValidForVehicle(vehicle, paintCode);
	if (localValidation !== null) return localValidation;
	const paintCodes = await fetchAuthoritativePaintCodesForVehicle(vehicle);
	if (!paintCodes) return null;
	const targets = getVehiclePaintCodeAliases(paintCode);
	if (!targets.length) return false;
	return paintCodes.some((code) => {
		const aliases = getVehiclePaintCodeAliases(code);
		return targets.some((target) => aliases.indexOf(target) !== -1);
	});
}

function getMatchByVinVariantOption() {
	const combinedVariantSelect = document.getElementById("variant-selector");
	if (!combinedVariantSelect) return null;
	return Array.from(combinedVariantSelect.options).find((option) => {
		const label = String(option.label || option.textContent || "").trim();
		return (
			option.value &&
			(label.startsWith("Match Paint by VIN") ||
				label.startsWith("Pintura de combinaciÃ³n por VIN"))
		);
	});
}

function truthyFlag(value) {
	return (
		value === true ||
		value === 1 ||
		String(value || "")
			.trim()
			.toLowerCase() === "true" ||
		String(value || "").trim() === "1"
	);
}

function hasVinOrPlateContext(vehicle) {
	if (!vehicle) return false;
	const vin = String(vehicle.vin || "")
		.trim()
		.toUpperCase();
	return !!(
		vin.length === 17 ||
		String(vehicle.plate || vehicle.licensePlate || "").trim() ||
		truthyFlag(vehicle.decodedByBumper)
	);
}

function getRawDecodedVehiclePaintCode(vehicle) {
	const troublesomePaintCode = String(vehicle?.troublesome_format || "").trim();
	if (troublesomePaintCode && !isMatchPaintByVinCode(troublesomePaintCode)) {
		return troublesomePaintCode;
	}
	return String(vehicle?.paintCode || "").trim();
}

function shouldForceMatchByVinForVehicle(vehicle) {
	const rawPaintCode = getRawDecodedVehiclePaintCode(vehicle);
	return !!(
		vehicle &&
		isBumperDotcomMake(vehicle.make || vehicle.ymm || vehicle.vehicle) &&
		hasVinOrPlateContext(vehicle) &&
		(!rawPaintCode || isMatchPaintByVinCode(rawPaintCode))
	);
}

function normalizeMatchByVinVehiclePaint(vehicle) {
	if (!vehicle || typeof vehicle !== "object") return vehicle;
	if (shouldForceMatchByVinForVehicle(vehicle)) {
		vehicle.paintCode = ".VIN";
		vehicle.decodedByBumper = true;
	}
	return vehicle;
}

const decodeMaxAttempts = 3;

let globalLocale = "en";

let attemptedVinsInGarage = [];
let attemptedVinsVinVerification = [];
let attemptedVinsOEM = [];
let attemptedOEMVins = [];
let attemptedDecodedVins = [];
let attemptedDecodedLicensePlates = [];

let productPageTroublesomeReferences = {};

let finalVinVerificationSubmissionVin = "";
let garageFirstVehicleData = "";
let themeName = "";
let autoSelectedColor = "";
let autoSelectedOption = "";
let storedDecodedVin = "";
let firstVehicleColor = "";
let decodedPaintedOptionPainted = "";
let decodedPaintedOptionPrice = "";
let licenseOptionMsg = "";
let vinOptionMsg = "";
let vinWord = "";
let licensePlateWord = "";
let chromeDataMakesGlobal = "";
let bumperMakesGlobal = "";
let troublesomeMakesGlobal = "";
let excludedMakesGlobal = "";
let licenseGarageLatest = "";
let matchPaintByVinTranslationKey = "";
let customerId = "";

let garageVinSubmissionBool = false;
let successfulVinVerificationVin = false;
let successfulVinDecoding = false;
let alreadyLogged = false;
let garageToCollections = false;
let decodedVINHasAvailableStock = false;
let autoSelectedBanned = false;
let isCurrentVehicleTroublesome = false;
let storedPaintOptionIsDTM = false;
let matchingPaintByVinVisible = false;
let twoToneSelected = false;

function sanitizeVehicleLookupVin(value) {
	return String(value || "")
		.toUpperCase()
		.replace(/[IOQ\s:;()!@#$%^?'"&*\-_=+.`~<>{}\[\]|,\/\\]/gi, "");
}

function normalizeVehicleLookupVin(value) {
	return sanitizeVehicleLookupVin(value).slice(0, 17);
}

function getVehicleLookupVinError(value) {
	const vin = normalizeVehicleLookupVin(value);
	if (!vin) return "Enter your VIN.";
	if (vin.length !== 17) {
		return `A VIN is 17 characters. Yours is ${vin.length}.`;
	}
	return null;
}

function normalizeVehicleLookupPlate(state, value, restrictedStates = []) {
	return cleanLicensePlate(
		String(state || "").toUpperCase(),
		String(value || "").toUpperCase(),
		Array.isArray(restrictedStates) ? restrictedStates : [],
	);
}

window.PartifyVehicleLookup = {
	...(window.PartifyVehicleLookup || {}),
	normalizeVin: normalizeVehicleLookupVin,
	validateVin: getVehicleLookupVinError,
	normalizePlate: normalizeVehicleLookupPlate,
};

window.PartifyPaintDecoderRequest = window.PartifyPaintDecoderRequest || {
	token: 0,
	status: "idle",
	owner: null,
	resolvedToken: null,
	begin(owner = "decoder") {
		this.status = "pending";
		this.owner = owner;
		this.resolvedToken = null;
		return ++this.token;
	},
	current(token) {
		return token === this.token;
	},
	finish(token, resolved) {
		if (!this.current(token)) return false;
		this.status = resolved ? "resolved" : "idle";
		this.resolvedToken = resolved ? token : null;
		return true;
	},
	invalidate(owner = null) {
		const token = ++this.token;
		this.status = "idle";
		this.owner = owner;
		this.resolvedToken = null;
		return token;
	},
	claim(owner) {
		const token = ++this.token;
		this.status = "resolved";
		this.owner = owner;
		this.resolvedToken = token;
		return token;
	},
	snapshot() {
		return {
			token: this.token,
			status: this.status,
			owner: this.owner,
			resolvedToken: this.resolvedToken,
		};
	},
	matches(snapshot) {
		return !!(
			snapshot &&
			snapshot.token === this.token &&
			snapshot.status === this.status &&
			snapshot.owner === this.owner &&
			snapshot.resolvedToken === this.resolvedToken
		);
	},
	ownsResolved(snapshot, owners = []) {
		return !!(
			this.matches(snapshot) &&
			this.status === "resolved" &&
			this.resolvedToken === this.token &&
			(!owners.length || owners.includes(this.owner))
		);
	},
	canRestoreStoredPaint(snapshot) {
		return (
			this.ownsResolved(snapshot, ["vin", "plate", "stored"]) ||
			(this.matches(snapshot) &&
				this.token === 0 &&
				this.status === "idle" &&
				this.owner == null)
		);
	},
};

function isCurrentPaintDecoderRequest(requestToken) {
	return requestToken == null || window.PartifyPaintDecoderRequest.current(requestToken);
}

document.addEventListener("change", (event) => {
	if (!event.isTrusted || !event.target?.matches) return;
	const request = window.PartifyPaintDecoderRequest;
	if (
		event.target.matches('input[name="select_paint_option"], input[name="fake_select_paint_option"]')
	) {
		const ownerById = {
			"checkbox-get-paint-code-with-vin": "vin",
			"checkbox-get-paint-code-with-license": "plate",
			"checkbox-select-paint-option": "manual",
			"checkbox-select-unpainted": "unpainted",
			"checkbox-paint-code-local-storage": "stored",
			"fake-checkbox-get-paint-code-with-license-or-vin": "lookup",
		};
		const owner = ownerById[event.target.id] || "paint-option";
		if (owner === "unpainted" || owner === "stored") {
			request.claim(owner);
		} else {
			request.invalidate(owner);
		}
		return;
	}
	if (
		event.target.matches("#variant-selector") &&
		document.getElementById("checkbox-select-paint-option")?.checked
	) {
		const option = event.target.options?.[event.target.selectedIndex];
		const paintCode = String(option?.getAttribute("data-variant-title") || "").trim();
		if (option?.value && paintCode && paintCode !== "Unpainted") {
			request.claim("manual");
		} else {
			request.invalidate("manual");
		}
	}
});

document.addEventListener("click", (event) => {
	if (
		event.isTrusted &&
		event.target?.closest?.(".select-code-fail-safe")
	) {
		window.PartifyPaintDecoderRequest.invalidate("manual");
	}
});

// Flag to ensure we only kick off one background sync from the
// customer account metafield into localStorage per page load.
let accountGarageSyncStarted = false;
let directPdpVehicleSelection = null;
let pendingMismatchDecodedVehicle = null;
let pdpVinEditingFromVehicleKey = "";
const garageHydrationTombstones = new Set();

// Global variables for mismatch tracking
let isMismatch = false;
let mismatchDifferences = {}; // e.g., { year: {user: '2020', decoded: '2021'}, ... }
let decodedVehicleData = {};
let vinAttachedToOrderTranslationPayload = {};

// unresolved-mismatch flags
window.MISMATCH_JSON_LOOKUP_MISS = false;
window.MISMATCH_UNRESOLVED = false;

const vinInputLibrary = document.querySelector("#vin-textbox");
const vinTextboxContainer = document.getElementById("vin-textbox-container");
const paintOptionGroup = document.getElementById("paint-options-group");
const storedDecodedPaintCodeMsgGlobal = document.querySelector(
	".stored-decoded-paint-msg",
);
const selectCodeOptionLabel = document.getElementById(
	"paint-code-select-code-option",
);
const licenseOptionLabel = document.getElementById("paint-code-license-option");
const storedOptionLabel = document.getElementById("paint-code-stored-option");
const failSafeSelectCodeOptionLabel = document.querySelector(
	".select-code-fail-safe",
);
const checkboxStoredCodeText = document.querySelector(".checkbox-stored-code");

/*******************************************************************************************************
 **                                                                                                    **
 **                                           GARAGE LOGIC                                             **
 **                                                                                                    **
 *******************************************************************************************************/
function normalizeSearchTerm(entry) {
	// Migration for string entries
	if (typeof entry === "string") {
		return { ...VEHICLE_SCHEMA, ymm: entry };
	}

	// Migration for object entries with ymm as object
	if (entry && typeof entry.ymm === "object" && entry.ymm !== null) {
		entry = { ...VEHICLE_SCHEMA, ...entry.ymm };
	}

	return normalizeMatchByVinVehiclePaint({ ...VEHICLE_SCHEMA, ...entry });
}

// Ensure that every field value in a search term is a string.
// Null/undefined become "" so callers can treat missing values
// via standard truthiness checks without worrying about types.
function normalizeSearchTermToStrings(term) {
	const out = {};
	for (const key in term) {
		if (!Object.prototype.hasOwnProperty.call(term, key)) continue;
		const value = term[key];
		if (key === "decodedBodyTypes") {
			out[key] = normalizeDecodedBodyTypeList(value);
		} else if (value === null || value === undefined) {
			out[key] = "";
		} else if (typeof value === "string") {
			out[key] = value;
		} else {
			out[key] = String(value);
		}
	}
	return out;
}

function getCanonicalGarageVehicleKey(vehicle) {
	if (!vehicle || typeof vehicle !== "object") return "";
	const vin = String(vehicle.vin || "").trim().toUpperCase();
	if (vin.length === 17) return `VIN:${vin}`;
	const ymm = String(
		vehicle.fitmentYmm ||
			vehicle.ymm ||
			[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" "),
	)
		.trim()
		.toLowerCase()
		.replace(/\s+/g, " ");
	return ymm ? `YMM:${ymm}` : "";
}

function readStoredGarageTerms() {
	try {
		const terms = JSON.parse(localStorage.getItem("searchTerms")) || [];
		return Array.isArray(terms)
			? terms.map(normalizeSearchTerm).map(normalizeSearchTermToStrings)
			: [];
	} catch (error) {
		return [];
	}
}

function recordRemovedGarageVehicleIdentities(previousTerms, nextTerms) {
	const nextKeys = new Set(
		(nextTerms || []).map(getCanonicalGarageVehicleKey).filter(Boolean),
	);
	(previousTerms || []).forEach((vehicle) => {
		const key = getCanonicalGarageVehicleKey(vehicle);
		if (key && !nextKeys.has(key)) garageHydrationTombstones.add(key);
	});
}

function setVehicleVinCountry(vehicle, vin, wmiCountry = "") {
	const previousVin = String(vehicle.vin || "").trim().toUpperCase();
	const nextVin = String(vin).trim().toUpperCase();
	if (previousVin !== nextVin) delete vehicle.wmiCountry;
	vehicle.vin = nextVin;
	if (wmiCountry) vehicle.wmiCountry = wmiCountry;
}

function getVinDecodeExcludedMakes() {
	return String(excludedMakesGlobal || "")
		.split(",")
		.map((m) => m.trim().toLowerCase())
		.filter(Boolean);
}

function isVinDecodeExcludedMake(make) {
	const normalizedMake = String(make || "").trim().toLowerCase();
	if (!normalizedMake) return false;
	return getVinDecodeExcludedMakes().includes(normalizedMake);
}

function isCurrentProductVinDecodeExcludedMake() {
	const path = String(window.location?.pathname || "").toLowerCase();
	if (!path.includes("/products/")) return false;
	return getVinDecodeExcludedMakes().some((make) => {
		const handleMake = make.replace(/\s+/g, "-");
		return path.includes(make) || path.includes(handleMake);
	});
}

function saveVinToFirstSearchTerm(
	vin,
	state = "",
	plate = "",
	location = 5,
	wmiCountry = "",
	decodedBodyTypeData = {},
) {
	if (wmiCountry && typeof wmiCountry === "object") {
		decodedBodyTypeData = wmiCountry;
		wmiCountry = "";
	}
	const vinVal = String(vin || "").trim().toUpperCase();
	if (!vinVal) return getSearchTerms();

	let terms = getSearchTerms();
	if (!Array.isArray(terms)) terms = [];
	if (!terms[0]) terms[0] = { ...VEHICLE_SCHEMA, ymm: "" };

	setVehicleVinCountry(terms[0], vinVal, wmiCountry);
	if (state) terms[0].state = state;
	if (plate) terms[0].plate = plate;
	applyDecodedBodyTypesToVehicle(terms[0], decodedBodyTypeData);

	setSearchTerms(terms, location);
	return terms;
}

window.isVinDecodeExcludedMake = isVinDecodeExcludedMake;
window.isCurrentProductVinDecodeExcludedMake =
	isCurrentProductVinDecodeExcludedMake;
window.saveVinToFirstSearchTerm = saveVinToFirstSearchTerm;

/* ══════════════════════════════════════════════════════════════
   SHARED VEHICLE-CHANGE DEDUP / BATCHER  (used by all modules)
   ══════════════════════════════════════════════════════════════ */
(function initVehicleChangeBatcher() {
	if (window.__partifyVehicleChangeBatcher) return;

	var _lastVehicleId = null;
	var _lastRunAt = 0;
	var _timer = null;
	var _callbacks = [];

	function normalizeVehicleId(v) {
		if (!v) return "__none__";
		if (v.vin && String(v.vin).length === 17)
			return "VIN:" + String(v.vin).toUpperCase();
		return (
			"YMM:" +
			(v.year || "") +
			":" +
			(v.make || "") +
			":" +
			(v.model || "")
		)
			.toLowerCase()
			.replace(/\s+/g, " ");
	}

	/**
	 * Register a callback that fires at most once per vehicleId per debounce window.
	 * Returns an unsubscribe function.
	 */
	function onVehicleChange(fn) {
		_callbacks.push(fn);
		return function () {
			_callbacks = _callbacks.filter(function (c) {
				return c !== fn;
			});
		};
	}

	/**
	 * Call this from any event listener. It batches multiple events in the
	 * same tick into one execution, and ignores repeats for the same
	 * vehicleId within a 200 ms window.
	 */
	function schedule(vehicle, source) {
		var id = normalizeVehicleId(vehicle);
		var now = Date.now();

		if (id === _lastVehicleId && now - _lastRunAt < 200) return;

		if (_timer) clearTimeout(_timer);
		_timer = setTimeout(function () {
			_timer = null;
			_lastVehicleId = id;
			_lastRunAt = Date.now();
			for (var i = 0; i < _callbacks.length; i++) {
				try {
					_callbacks[i](vehicle, source);
				} catch (e) {
					console.error("[VehicleChangeBatcher] callback error", e);
				}
			}
		}, 80);
	}

	window.__partifyVehicleChangeBatcher = {
		normalizeVehicleId: normalizeVehicleId,
		onVehicleChange: onVehicleChange,
		schedule: schedule,
	};
})();

function getPartifyCustomerId() {
	const id =
		window.igCustomerId ||
		(window.__PARTIFY_CUSTOMER__ && window.__PARTIFY_CUSTOMER__.id);

	return id ? String(id) : "";
}

async function getValidatedAccountVehicleIdentity(vehicle) {
	if (!vehicle.year || !vehicle.make || !vehicle.model) {
		return { specificity: 0, identity: {
			year: vehicle.year || "", make: vehicle.make || "", model: vehicle.model || "",
			submodel: "", engine: "", fitmentYmm: "",
			ymm: [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" "),
		} };
	}
	if (!window.__PARTIFY_YMM_BASE__) return null;
	const key = (value) => toStringValue(value).toLowerCase();
	try {
		const data = await loadYmmData(vehicle.year);
		const rows = data.vehicles || [];
		let model = toStringValue(vehicle.model);
		const hasModel = () => rows.some(
			(row) => key(row.make) === key(vehicle.make) && key(row.model) === key(model),
		);
		if (!hasModel()) {
			const aliases = await loadVinLookup();
			const alias = aliases.find((row) =>
				key(row.Model).replace(/[^a-z0-9]/g, "") === key(model).replace(/[^a-z0-9]/g, ""),
			);
			if (alias?.["Easysearch Model"]) model = alias["Easysearch Model"];
		}
		// The native validator owns alias/submodel/engine rules. Prechecking its
		// model prevents account hydration from setting decoder mismatch flags.
		const validated = hasModel() ? await validateVehicleDataNative(vehicle) : null;
		const identity = {
			year: validated?.year || toStringValue(vehicle.year),
			make: validated?.make || toStringValue(vehicle.make),
			model: validated?.model || toStringValue(vehicle.model),
			submodel: validated?.submodel || "",
			engine: validated?.submodel ? validated.engine || "" : "",
			fitmentYmm: "",
		};
		identity.ymm = [identity.year, identity.make, identity.model, identity.submodel, identity.engine].filter(Boolean).join(" ");
		let specificity = validated?.canResolveYMM
			? 1 + Number(Boolean(identity.submodel)) + Number(Boolean(identity.engine))
			: 0;
		if (vehicle.fitmentYmm) {
			const mapping = await resolveFullVinLookupMapping(vehicle);
			if (mapping && normalizeFullYmmLookupKey(mapping.fitmentYmm) === normalizeFullYmmLookupKey(vehicle.fitmentYmm)) {
				const fitmentYear = toStringValue(mapping.fitmentYmm).split(" ")[0];
				const fitmentRows = (await loadYmmData(fitmentYear)).vehicles || [];
				for (const row of fitmentRows) {
					const fields = [fitmentYear, row.make, row.model, row.submodel, row.engine];
					for (const length of [3, 4, 5]) {
						if (normalizeFullYmmLookupKey(fields.slice(0, length).filter(Boolean).join(" ")) === normalizeFullYmmLookupKey(mapping.fitmentYmm)) {
							identity.fitmentYmm = mapping.fitmentYmm;
							identity.ymm = mapping.fitmentYmm;
							specificity = Math.max(specificity, 1 + Number(length > 3 && Boolean(row.submodel)) + Number(length > 4 && Boolean(row.engine)));
						}
					}
				}
			} else if (specificity && normalizeFullYmmLookupKey(vehicle.fitmentYmm) === normalizeFullYmmLookupKey(identity.ymm)) {
				identity.fitmentYmm = identity.ymm;
			}
		}
		return { identity, specificity };
	} catch (_) {
		// Missing validation data is unknown, not evidence that an accepted
		// local identity is invalid or that a remote record is more specific.
		return null;
	}
}

async function mergeSameVinAccountVehicle(local, remote) {
	const identityFields = ["year", "make", "model", "submodel", "engine", "ymm", "fitmentYmm"];
	if (identityFields.every((field) => local[field] === remote[field])) return remote;
	const [localValidation, remoteValidation] = await Promise.all([
		getValidatedAccountVehicleIdentity(local),
		getValidatedAccountVehicleIdentity(remote),
	]);
	const identity = !localValidation || !remoteValidation
		? Object.fromEntries(identityFields.map((field) => [field, local[field]]))
		: (localValidation.specificity > remoteValidation.specificity ? localValidation : remoteValidation).identity;
	// Only identity ownership changes. Account paint and other metadata retain
	// their existing precedence, including fields genuinely absent locally.
	return { ...remote, ...identity };
}

// NOTE: This function must stay synchronous because it is
// used in many places (including compiled theme scripts)
// that expect an Array, not a Promise.
//
// To hydrate from the logged-in customer's account metafield
// we kick off a background fetch that, when it resolves,
// overwrites localStorage and emits `searchTermsUpdated`.
function getSearchTerms() {
	// Start background sync from account metafield (fire-and-forget).
	try {
		if (
			window.igCustomerLoggedIn &&
			window.igCustomerId &&
			!accountGarageSyncStarted &&
			typeof fetchCustomerAccountData === "function"
		) {
			accountGarageSyncStarted = true;
			const hydrationCustomerId = window.igCustomerId.toString();
			fetchCustomerAccountData(hydrationCustomerId)
				.then(async (accountData) => {
					if (
						window.shopCurrency === "USD" &&
						(!window.igCustomerLoggedIn || String(window.igCustomerId) !== hydrationCustomerId)
					) return;
					if (
						window.PartifyGarageScope &&
						!window.PartifyGarageScope.isCurrentCustomerOwner(
							window.igCustomerId,
						)
					) {
						console.error(
							"[GarageScope] Skipped account hydration for an unsafe active Garage scope",
						);
						return;
					}
					if (!accountData || !accountData.data?.customer?.metafield?.value)
						return;
					let garageJson;
					try {
						garageJson = JSON.parse(accountData.data.customer.metafield.value);
					} catch (e) {
						console.error("Failed to parse customer garage metafield", e);
						return;
					}
					if (!garageJson || !Array.isArray(garageJson.vehicles)) return;
					let remoteTerms = garageJson.vehicles || [];
					remoteTerms = remoteTerms
						.map(normalizeSearchTerm)
						.map(normalizeSearchTermToStrings);
					const remoteTermCountBeforeTombstones = remoteTerms.length;
					remoteTerms = remoteTerms.filter(
						(vehicle) =>
							!garageHydrationTombstones.has(
								getCanonicalGarageVehicleKey(vehicle),
							),
					);
					const removedStaleRemoteVehicles =
						remoteTerms.length !== remoteTermCountBeforeTombstones;

					/* ── MERGE instead of overwrite (Fix D: preserve local vehicles) ──
					   Identity: VIN (uppercase) when present, else normalized year-make-model.
					   Remote vehicles take priority for duplicates, local-only vehicles are kept. */
					let localTerms = [];
					try {
						localTerms =
							JSON.parse(localStorage.getItem("searchTerms")) || [];
						localTerms = localTerms
							.map(normalizeSearchTerm)
							.map(normalizeSearchTermToStrings);
					} catch (e) {
						localTerms = [];
					}

					const seen = new Map();
					function vehicleKey(v) {
						return getCanonicalGarageVehicleKey(v);
					}
					if (window.shopCurrency === "USD") {
						const localSnapshot = JSON.stringify(localTerms);
						const tombstoneSnapshot = JSON.stringify([...garageHydrationTombstones]);
						const localByVin = new Map(localTerms
							.filter((vehicle) => vehicleKey(vehicle).startsWith("VIN:"))
							.map((vehicle) => [vehicleKey(vehicle), vehicle]));
						remoteTerms = await Promise.all(remoteTerms.map((remote) => {
							const local = localByVin.get(vehicleKey(remote));
							return local ? mergeSameVinAccountVehicle(local, remote) : remote;
						}));
						// Cached validation reads must not hydrate a different owner or
						// overwrite a Garage selection/removal made while they were pending.
						if (
							!window.igCustomerLoggedIn || String(window.igCustomerId) !== hydrationCustomerId ||
							(window.PartifyGarageScope && !window.PartifyGarageScope.isCurrentCustomerOwner(hydrationCustomerId)) ||
							JSON.stringify(readStoredGarageTerms()) !== localSnapshot ||
							JSON.stringify([...garageHydrationTombstones]) !== tombstoneSnapshot
						) return;
					}
					const activeLocalVehicle = localTerms[0] || null;
					const activeLocalKey = vehicleKey(activeLocalVehicle);
					const shouldPreserveActivePdpVehicle = Boolean(
						window.shopCurrency === "USD" &&
						["/products/", "/product/", "/shop/"].some((path) =>
							window.location.pathname.includes(path),
						) &&
							directPdpVehicleSelection &&
							!["manual", "unpainted"].includes(
								window.PartifyPaintDecoderRequest?.owner,
							) &&
							activeLocalKey === directPdpVehicleSelection.key,
					);
					const activePdpVehicleKey = shouldPreserveActivePdpVehicle
						? activeLocalKey
						: "";
					const guestCarryoverActiveVehicle =
						window.PartifyGarageScope &&
						typeof window.PartifyGarageScope
							.getGuestCarryoverActiveVehicle === "function"
							? window.PartifyGarageScope.getGuestCarryoverActiveVehicle(
									window.igCustomerId,
								)
							: null;
					const normalizedGuestCarryoverActiveVehicle =
						guestCarryoverActiveVehicle
							? normalizeSearchTermToStrings(
									normalizeSearchTerm(guestCarryoverActiveVehicle),
								)
							: null;
					const guestCarryoverActiveKey = normalizedGuestCarryoverActiveVehicle
						? vehicleKey(normalizedGuestCarryoverActiveVehicle)
						: "";
					const shouldPreserveGuestActiveVehicle = Boolean(
						guestCarryoverActiveKey &&
							localTerms.length &&
							vehicleKey(localTerms[0]) === guestCarryoverActiveKey,
					);
					// Remote first (higher priority)
					remoteTerms.forEach(function (v) {
						var k = vehicleKey(v);
						if (!seen.has(k)) seen.set(k, v);
					});
					// Local vehicles that are NOT duplicates of remote
					var hasLocalOnlyVehicles = false;
					var preservedActivePdpVehicle = false;
					localTerms.forEach(function (v) {
						var k = vehicleKey(v);
						if (!seen.has(k)) {
							seen.set(k, v);
							hasLocalOnlyVehicles = true;
							if (k === activePdpVehicleKey) preservedActivePdpVehicle = true;
						} else if (k === activePdpVehicleKey) {
							// A newer explicit PDP decode owns the canonical vehicle over a
							// stale account snapshot, regardless of its final paint result.
							seen.set(k, v);
							preservedActivePdpVehicle = true;
						}
					});
					var merged = Array.from(seen.values());
					const guestActiveIndex = shouldPreserveGuestActiveVehicle
						? merged.findIndex(
								(vehicle) => vehicleKey(vehicle) === guestCarryoverActiveKey,
							)
						: -1;
					const didReorderGuestActiveVehicle = guestActiveIndex > 0;
					if (didReorderGuestActiveVehicle) {
						const [activeGuestVehicle] = merged.splice(guestActiveIndex, 1);
						merged.unshift(activeGuestVehicle);
					}
					if (preservedActivePdpVehicle) {
						const activePaintIndex = merged.findIndex(
							(vehicle) => vehicleKey(vehicle) === activePdpVehicleKey,
						);
						if (activePaintIndex > 0) {
							const [activePaintVehicle] = merged.splice(activePaintIndex, 1);
							merged.unshift(activePaintVehicle);
						}
					}
					directPdpVehicleSelection = null;
					garageHydrationTombstones.clear();

					localStorage.setItem("searchTerms", JSON.stringify(merged));
					/* Use the shared batcher so the merge fires only ONE
					   downstream update, not separate searchTermsUpdated +
					   partify:vehicle:changed like before. */
					window.dispatchEvent(
						new CustomEvent("searchTermsUpdated", {
							detail: {
								source: "account-hydration",
								shouldSyncCustomerGarage:
									hasLocalOnlyVehicles ||
									didReorderGuestActiveVehicle ||
									preservedActivePdpVehicle ||
									removedStaleRemoteVehicles,
							},
						}),
					);
					if (window.__partifyVehicleChangeBatcher) {
						window.__partifyVehicleChangeBatcher.schedule(
							merged.length ? merged[0] : null,
							"getSearchTerms-merge",
						);
					}
				})
				.catch((err) => {
					console.error("Error fetching customer garage metafield", err);
				});
		}
	} catch (e) {
		console.error("Error starting garage metafield sync", e);
	}

	// Always read from localStorage synchronously so callers
	// immediately get an Array.
	let terms = [];
	try {
		terms = JSON.parse(localStorage.getItem("searchTerms")) || [];
	} catch (e) {
		console.error("Failed to parse localStorage searchTerms", e);
		terms = [];
	}
	terms = terms.map(normalizeSearchTerm).map(normalizeSearchTermToStrings);

	// Write back ONLY when normalization actually changed the data.
	// This prevents triggering the Storage.prototype.setItem monkey-patch
	// (hero banner) on every read, which was the primary feedback loop.
	var normalizedJson = JSON.stringify(terms);
	var rawValue = localStorage.getItem("searchTerms");
	if (normalizedJson !== rawValue) {
		localStorage.setItem("searchTerms", normalizedJson);
	}
	return terms;
}

function removeVINsLessThan17Chars() {
	let terms = getSearchTerms();
	let shouldRefresh = false;
	terms = terms.map((term) => {
		if (!term.vin || (typeof term.vin === "string" && term.vin.length === 17)) {
			return term;
		} else {
			shouldRefresh = true;
			return { ...term, vin: "" };
		}
	});
	if (!shouldRefresh) return;

	setSearchTerms(terms, 1);
	if (shouldRefresh) {
		let refreshCount = parseInt(
			localStorage.getItem("vinRefreshCount") || "0",
			10,
		);
		if (refreshCount < 3) {
			localStorage.setItem("vinRefreshCount", refreshCount + 1);
			window.location.reload();
		} else {
			localStorage.removeItem("vinRefreshCount");
		}
	}
}

function setSearchTerms(terms, location) {
	const previousTerms = readStoredGarageTerms();
	const uniqueTerms = [];
	const seen = new Set();
	const currentPage = window.location.href || "";
	for (const term of terms) {
		const normalized = normalizeSearchTerm(term);
		const trimmedYmm = normalized.ymm.trim();
		if (!seen.has(trimmedYmm)) {
			uniqueTerms.push({
				...normalized,
				ymm: trimmedYmm,
				currentPage: currentPage,
				lastCollectionImage: "",
			});
			seen.add(trimmedYmm);
		}
	}
	if ([9, 15, 16].includes(Number(location))) {
		recordRemovedGarageVehicleIdentities(previousTerms, uniqueTerms);
	}
	localStorage.setItem("searchTerms", JSON.stringify(uniqueTerms));
	// Emit change event — but batch via the shared dedup helper so
	// downstream consumers (homepage hub, hero banner, PDP gating)
	// only process one update per vehicle switch.
	window.dispatchEvent(new Event("searchTermsUpdated"));
	if (window.__partifyVehicleChangeBatcher) {
		window.__partifyVehicleChangeBatcher.schedule(
			uniqueTerms.length ? uniqueTerms[0] : null,
			"setSearchTerms",
		);
	}
}

// --- Always adds a new vehicle and moves to front ---
function moveVehicleToFront(vehicleData) {
	let currentSearchTerms = getSearchTerms();
	// vehicleData can be a string (ymm) or an object
	let ymm = typeof vehicleData === "string" ? vehicleData : vehicleData.ymm;

	let state = typeof vehicleData === "string" ? null : vehicleData.state;
	let plate = typeof vehicleData === "string" ? null : vehicleData.plate;
	let vin = typeof vehicleData === "string" ? null : vehicleData.vin;
	const wmiCountry = typeof vehicleData === "object" ? vehicleData.wmiCountry : "";
	const hasExplicitFitmentYmm =
		typeof vehicleData === "object" &&
		Object.prototype.hasOwnProperty.call(vehicleData, "fitmentYmm");

	// Find index: prefer VIN match, then exact ymm, then year+make+model prefix
	const incomingVin = vin ? String(vin).toUpperCase().trim() : null;
	const incomingYear =
		typeof vehicleData === "object" && vehicleData.year
			? String(vehicleData.year).trim()
			: null;
	const incomingMake =
		typeof vehicleData === "object" && vehicleData.make
			? String(vehicleData.make).trim().toLowerCase()
			: null;
	const incomingModel =
		typeof vehicleData === "object" && vehicleData.model
			? String(vehicleData.model).trim().toLowerCase()
			: null;

	let vehicleIndex = currentSearchTerms.findIndex((obj) => {
		// 1. If both have a 17-char VIN, use it as the canonical identity
		if (incomingVin && obj.vin && String(obj.vin).length === 17) {
			return String(obj.vin).toUpperCase() === incomingVin;
		}
		// 2. Exact ymm string
		if (obj.ymm === ymm) return true;
		// 3. Same year+make+model regardless of submodel/engine suffix
		// This prevents cross-path duplicates when submodel resolves differently
		if (
			incomingYear &&
			incomingMake &&
			incomingModel &&
			obj.year &&
			obj.make &&
			obj.model
		) {
			return (
				String(obj.year).trim() === incomingYear &&
				String(obj.make).trim().toLowerCase() === incomingMake &&
				String(obj.model).trim().toLowerCase() === incomingModel
			);
		}
		return false;
	});

	let vehicleObj = normalizeSearchTerm(vehicleData);
	if (vehicleIndex > -1) {
		vehicleObj = currentSearchTerms.splice(vehicleIndex, 1)[0];
		// If either ymm contains the other, set ymm to the longer one
		if (vehicleObj.ymm !== ymm) {
			if (vehicleObj.ymm.length < ymm.length) {
				vehicleObj.ymm = ymm;
			}
		}
		// Always overwrite submodel/engine with incoming values — old entry may be stale
		const incomingNormalized = normalizeSearchTerm(vehicleData);
		vehicleObj.submodel = incomingNormalized.submodel || vehicleObj.submodel;
		vehicleObj.engine = incomingNormalized.engine || vehicleObj.engine;
		if (hasExplicitFitmentYmm) {
			vehicleObj.fitmentYmm = incomingNormalized.fitmentYmm || null;
			if (incomingNormalized.fitmentYmm) {
				vehicleObj.ymm = incomingNormalized.fitmentYmm;
			}
		} else if (vehicleObj.fitmentYmm) {
			vehicleObj.fitmentYmm = null;
			vehicleObj.ymm = ymm;
		}
	} else {
		vehicleObj = normalizeSearchTerm(vehicleData);
	}

	// If currentSearchTerms.state does not exist add it
	if (state) {
		vehicleObj.state = state;
	}

	if (plate) {
		vehicleObj.plate = plate;
	}

	if (vin) {
		setVehicleVinCountry(vehicleObj, vin, wmiCountry);
	}
	applyDecodedBodyTypesToVehicle(vehicleObj, vehicleData);
	currentSearchTerms.unshift(vehicleObj);
	if (currentSearchTerms.length > 5) currentSearchTerms.pop();
	setSearchTerms(currentSearchTerms, 2);
	return currentSearchTerms;
}

async function setSelectValueWhenReady(selectId, value, timeout = 3000) {
	const select = document.getElementById(selectId);
	let newValue = "";
	if (!select) return newValue;

	const start = Date.now();
	return new Promise((resolve) => {
		function trySet() {
			const match = Array.from(select.options).find(
				(opt) => opt.value.toLowerCase().trim() === value.toLowerCase().trim(),
			);
			if (match) {
				select.value = match.value;
				newValue = match.value;
				select.dispatchEvent(new Event("change", { bubbles: true }));
				resolve(newValue);
			} else if (Date.now() - start > timeout) {
				resolve(newValue); // Timed out, value not set
			} else {
				setTimeout(trySet, 50);
			}
		}
		trySet();
	});
}

function setFitmentObject(obj) {
	localStorage.setItem("easysearch-preselect-fitment", JSON.stringify(obj));
	localStorage.setItem("easysearch-preselect", JSON.stringify(obj));
}

/**
 * Decoder-level YMM capture.
 *
 * When a plate/VIN is decoded at the VIN-decoder level and the garage is EMPTY,
 * create a garage vehicle from the decoded Year/Make/Model (+ decoded VIN), using the
 * same persistence path as a garage add: searchTerms (via moveVehicleToFront, which
 * fires searchTermsUpdated for logged-in account sync), the
 * easysearch-preselect fitment object, and a garage-popup refresh. No navigation.
 * YMM only — submodel/engine are intentionally left blank.
 *
 * No-ops when the garage already has a vehicle (that "decoded differs from existing"
 * case is owned by the mismatch flow) or when year/make/model are incomplete.
 *
 * @param {Object} decoded - { year, make, model, fitmentYmm, vin, paintCode, state, plate, decodedByBumper }
 * @returns {boolean} true if a vehicle was created, false if it no-opped.
 */
function ensureDecodedVehicleInGarage(decoded) {
	const existing = getSearchTerms();
	if (
		Array.isArray(existing) &&
		existing.length > 0 &&
		existing[0] &&
		existing[0].model
	) {
		return false;
	}

	const year = decoded && decoded.year ? String(decoded.year).trim() : "";
	const make = decoded && decoded.make ? String(decoded.make).trim() : "";
	const model = decoded && decoded.model ? String(decoded.model).trim() : "";
	if (!year || !make || !model) return false;
	const submodel =
		decoded && decoded.submodel ? String(decoded.submodel).trim() : "";
	const engine = decoded && decoded.engine ? String(decoded.engine).trim() : "";

	const vin =
		decoded && decoded.vin ? String(decoded.vin).trim().toUpperCase() : "";
	const structuredYmm = [year, make, model, submodel, engine]
		.filter(Boolean)
		.join(" ");
	const fitmentYmm =
		decoded && decoded.fitmentYmm ? String(decoded.fitmentYmm).trim() : "";
	const displayYmm = fitmentYmm || structuredYmm;

	const vehicleObj = {
		...VEHICLE_SCHEMA,
		ymm: displayYmm,
		fitmentYmm: fitmentYmm || null,
		year,
		make,
		model,
		submodel,
		engine,
		vin: vin || null,
		paintCode: decoded && decoded.paintCode ? decoded.paintCode : null,
		bodyType: decoded && decoded.bodyType ? decoded.bodyType : null,
		decodedBodyTypes: normalizeDecodedBodyTypeList(
			decoded && decoded.decodedBodyTypes,
		),
		state: decoded && decoded.state ? decoded.state : null,
		plate: decoded && decoded.plate ? decoded.plate : null,
		decodedByBumper:
			decoded && typeof decoded.decodedByBumper !== "undefined"
				? decoded.decodedByBumper
				: null,
		troublesome_format:
			decoded && decoded.troublesome_format ? decoded.troublesome_format : null,
		troublesome_description:
			decoded && decoded.troublesome_description
				? decoded.troublesome_description
				: null,
	};

	// Adds + persists (setSearchTerms fires the account sync event).
	moveVehicleToFront(vehicleObj);

	// Fitment object — same easysearch-preselect-delimiter shape the garage add writes.
	const fitVal = [year, make, model, submodel, engine]
		.filter(Boolean)
		.map((f) => f + "easysearch-preselect-delimiter")
		.join("");
	setFitmentObject({
		value: fitVal,
		expires: String(Date.now() + 365 * 24 * 60 * 60 * 1000),
	});

	// Refresh the garage popup if available (no navigation).
	if (
		window.GarageLogic &&
		typeof window.GarageLogic.updateGaragePopup === "function"
	) {
		window.GarageLogic.updateGaragePopup(getSearchTerms());
	}

	return true;
}

// Exposed for the decoder flow and e2e tests.
window.ensureDecodedVehicleInGarage = ensureDecodedVehicleInGarage;

/**
 * Decode a VIN entered in the Ridge touch-up configurators and make that
 * decoded vehicle the active Garage vehicle without navigating away from the
 * PDP. Unlike ensureDecodedVehicleInGarage(), this intentionally also handles
 * a non-empty Garage: the customer's newly entered VIN is the authoritative
 * vehicle for the current touch-up purchase.
 */
async function decodeAndActivateTouchupVin(rawVin) {
	const vin = normalizeVehicleLookupVin(rawVin);
	if (vin.length !== 17) throw new Error("Enter a complete 17-character VIN.");

	const requestToken = window.PartifyPaintDecoderRequest.begin("vin");
	let decoded;
	try {
		decoded = await fetchVehicleDataByVin(
			"",
			"",
			vin,
			3,
			"We could not decode that VIN. Check it and try again.",
			"VIN lookup is temporarily unavailable.",
			null,
			"",
			"VIN lookup failed for ",
			"Attempts remaining: ",
			false,
			requestToken,
		);
	} catch (error) {
		if (!window.PartifyPaintDecoderRequest.current(requestToken)) return null;
		window.PartifyPaintDecoderRequest.finish(requestToken, false);
		throw error;
	}

	if (!window.PartifyPaintDecoderRequest.current(requestToken)) return null;
	if (!decoded || !decoded.validVin || !decoded.year || !decoded.make || !decoded.model) {
		window.PartifyPaintDecoderRequest.finish(requestToken, false);
		throw new Error("We could not decode that VIN. Check it and try again.");
	}

	const activeVehicle = activateDecodedVehicleInGarage(decoded, { vin });
	window.PartifyPaintDecoderRequest.finish(requestToken, true);
	return activeVehicle;
}

window.PartifyTouchupVinGarageSync = {
	decodeAndActivate: decodeAndActivateTouchupVin,
};

function applyDecodedVehiclePaintValidation(
	paintCode,
	decodedByBumper,
	vehiclePaintCodeValid,
) {
	let validatedPaintCode = paintCode;
	let validatedByBumper = decodedByBumper;
	if (vehiclePaintCodeValid === false) {
		validatedPaintCode = ".VIN";
		validatedByBumper = false;
	}
	return {
		paintCode: validatedPaintCode,
		decodedByBumper: validatedByBumper,
	};
}

function validateDecodedVehiclePaint(vehicle, paintCode, decodedByBumper) {
	const vehiclePaintCodeValid =
		!paintCode || isMatchPaintByVinCode(paintCode)
			? true
			: isPaintCodeValidForVehicle(vehicle, paintCode);
	return applyDecodedVehiclePaintValidation(
		paintCode,
		decodedByBumper,
		vehiclePaintCodeValid,
	);
}

function buildGaragePaintValidationVehicle(vehicle) {
	const year = String(vehicle?.year || "").trim();
	const make = String(vehicle?.make || "").trim();
	const model = String(vehicle?.model || "").trim();
	const fitmentYmm = String(vehicle?.fitmentYmm || "").trim();
	return {
		...vehicle,
		year,
		make,
		model,
		fitmentYmm: fitmentYmm || null,
		ymm: fitmentYmm || [year, make, model].filter(Boolean).join(" "),
	};
}

async function validateDecodedVehiclePaintAsync(
	vehicle,
	paintCode,
	decodedByBumper,
	requireAuthoritativeMatch = false,
) {
	let vehiclePaintCodeValid =
		!paintCode || isMatchPaintByVinCode(paintCode)
			? true
			: await isPaintCodeValidForVehicleAsync(vehicle, paintCode);
	if (requireAuthoritativeMatch && vehiclePaintCodeValid === null) {
		vehiclePaintCodeValid = false;
	}
	return applyDecodedVehiclePaintValidation(
		paintCode,
		decodedByBumper,
		vehiclePaintCodeValid,
	);
}

async function resolveFinalGarageVehiclePaint(
	vehicle,
	paintCode,
	decodedByBumper,
) {
	const validationVehicle = buildGaragePaintValidationVehicle(vehicle);
	let finalPaintCode = String(paintCode || "").trim();
	if (
		!finalPaintCode &&
		!isVinDecodeExcludedMake(validationVehicle.make) &&
		window.shopCurrency === "USD"
	) {
		finalPaintCode = ".VIN";
	}
	return validateDecodedVehiclePaintAsync(
		validationVehicle,
		finalPaintCode,
		decodedByBumper,
		isBumperDotcomMake(validationVehicle.make) &&
			hasVinOrPlateContext(validationVehicle) &&
			!isMatchPaintByVinCode(finalPaintCode),
	);
}

function getGaragePersistedPaintState(
	paintCode,
	decodedByBumper,
	isTroublesomeVehicle,
) {
	return {
		paintCode: truthyFlag(isTroublesomeVehicle) ? null : paintCode || null,
		decodedByBumper: decodedByBumper || null,
	};
}

/**
 * Make an already-decoded vehicle the active Garage vehicle without navigating.
 * This is the shared persistence path for deliberate VIN/plate selection flows.
 */
function activateDecodedVehicleInGarage(decoded, options = {}) {
	if (!decoded || typeof decoded !== "object") return null;
	const year = String(decoded.year || "").trim();
	const make = String(decoded.make || "").trim();
	const model = String(decoded.model || "").trim();
	if (!year || !make || !model) return null;

	const submodel = String(decoded.submodel || "").trim();
	const engine = String(decoded.engine || "").trim();
	const fitmentYmm = String(decoded.fitmentYmm || "").trim();
	const displayYmm =
		fitmentYmm || [year, make, model, submodel, engine].filter(Boolean).join(" ");
	const vinSource = Object.prototype.hasOwnProperty.call(options, "vin")
		? options.vin
		: decoded.vin;
	const stateSource = Object.prototype.hasOwnProperty.call(options, "state")
		? options.state
		: decoded.state;
	const plateSource = Object.prototype.hasOwnProperty.call(options, "plate")
		? options.plate
		: decoded.plate;
	const vin = String(vinSource || "").trim().toUpperCase();
	const preservePaintDecision = options.preservePaintDecision === true;
	const preserveDecodedFields = options.preserveDecodedFields === true;
	const decodedPaintCode = String(
		preservePaintDecision
			? decoded.paintCode || ""
			: decoded.paintCode || decoded.troublesome_format || "",
	).trim();
	const vehicle = {
		...VEHICLE_SCHEMA,
		...(preserveDecodedFields ? decoded : {}),
		ymm: displayYmm,
		fitmentYmm: fitmentYmm || null,
		year,
		make,
		model,
		submodel,
		engine,
		vin: vin || null,
		paintCode: decodedPaintCode || (preservePaintDecision ? null : ".VIN"),
		bodyType: decoded.bodyType || null,
		decodedBodyTypes: normalizeDecodedBodyTypeList(decoded.decodedBodyTypes),
		state: stateSource || null,
		plate: plateSource || null,
		decodedByBumper:
			typeof decoded.decodedByBumper === "undefined" ? null : decoded.decodedByBumper,
		troublesome_format: decoded.troublesome_format || null,
		troublesome_description: decoded.troublesome_description || null,
		...(decoded.wmiCountry ? { wmiCountry: decoded.wmiCountry } : {}),
	};
	applyDecodedBodyTypesToVehicle(vehicle, {
		bodyType: decoded.bodyType,
		decodedBodyTypes: decoded.decodedBodyTypes,
	});

	moveVehicleToFront(vehicle);
	const terms = getSearchTerms();
	if (terms[0]) {
		terms[0] = { ...terms[0], ...vehicle };
		setSearchTerms(terms, 5);
	}

	const fitValue = [year, make, model, submodel, engine]
		.filter(Boolean)
		.map((field) => field + "easysearch-preselect-delimiter")
		.join("");
	setFitmentObject({
		value: fitValue,
		expires: String(Date.now() + 365 * 24 * 60 * 60 * 1000),
	});
	if (
		window.GarageLogic &&
		typeof window.GarageLogic.updateGaragePopup === "function"
	) {
		window.GarageLogic.updateGaragePopup(getSearchTerms());
	}

	return getSearchTerms()[0] || vehicle;
}

async function validateAndActivateDecodedVehicleInGarage(decoded, options = {}) {
	if (!decoded || typeof decoded !== "object") return null;
	const year = String(decoded.year || "").trim();
	const make = String(decoded.make || "").trim();
	const model = String(decoded.model || "").trim();
	if (!year || !make || !model) return null;
	const submodel = String(decoded.submodel || "").trim();
	const engine = String(decoded.engine || "").trim();
	const fitmentYmm = String(decoded.fitmentYmm || "").trim();
	const requestToken = options.requestToken;
	if (!isCurrentPaintDecoderRequest(requestToken)) return null;
	const validationVehicle = {
		...decoded,
		year,
		make,
		model,
		submodel,
		engine,
		fitmentYmm: fitmentYmm || null,
		vin: Object.prototype.hasOwnProperty.call(options, "vin")
			? options.vin
			: decoded.vin,
		state: Object.prototype.hasOwnProperty.call(options, "state")
			? options.state
			: decoded.state,
		plate: Object.prototype.hasOwnProperty.call(options, "plate")
			? options.plate
			: decoded.plate,
	};
	const isTroublesomeVehicle = truthyFlag(decoded.isCurrentVehicleTroublesome);
	const validatedPaint = isTroublesomeVehicle
		? {
				paintCode: decoded.paintCode,
				decodedByBumper: decoded.decodedByBumper,
			}
		: await resolveFinalGarageVehiclePaint(
				validationVehicle,
				decoded.paintCode,
				decoded.decodedByBumper,
			);
	if (!isCurrentPaintDecoderRequest(requestToken)) return null;
	const persistedPaint = getGaragePersistedPaintState(
		validatedPaint.paintCode,
		validatedPaint.decodedByBumper,
		isTroublesomeVehicle,
	);
	return activateDecodedVehicleInGarage(
		{
			...decoded,
			paintCode: persistedPaint.paintCode,
			decodedByBumper: persistedPaint.decodedByBumper,
		},
		{
			...options,
			preservePaintDecision: true,
			preserveDecodedFields: true,
		},
	);
}

window.PartifyGarageVehicleActivation = {
	activateDecoded: activateDecodedVehicleInGarage,
	resolveFinalPaint: resolveFinalGarageVehiclePaint,
	validateAndActivateDecoded: validateAndActivateDecodedVehicleInGarage,
};

function toStringValue(f) {
	if (f == null) return "";
	if (typeof f === "string" || typeof f === "number") return String(f).trim();
	if (typeof f.value === "string" || typeof f.value === "number")
		return String(f.value).trim();
	return "";
}

function isPartifyTouchupFinderOpen() {
	return Boolean(document.querySelector("[data-tuf-overlay].tuf-open"));
}

function updateEasysearchLocalStorageOrNavigate(vehicleData) {
	const {
		year,
		make,
		model,
		submodel,
		engine,
		bodyType,
		decodedBodyTypes,
		vin,
		paintCode,
		decodedByBumper,
		lastCollectionImage,
		fitmentYmm,
		troublesome_format,
		troublesome_description,
	} = vehicleData;

	let anchor = null;
	const holder = document.querySelector(".easysearch-holder");
	const actionsHolder = holder?.querySelector(".easysearch-actions-holder");
	if (actionsHolder) {
		const btnHolder = actionsHolder.querySelector(".easysearch-btn-holder");
		if (btnHolder) {
			anchor = btnHolder.querySelector("a");
		}
	}

	// Validate that the EasySearch anchor is actually usable
	if (anchor && (!anchor.href || anchor.href.includes("javascript:void"))) {
		anchor = null;
	}

	const yearVal = toStringValue(year);
	const makeVal = toStringValue(make);
	const modelVal = toStringValue(model);
	const submodelVal = toStringValue(submodel);
	const engineVal = toStringValue(engine);
	const vinVal = toStringValue(vin);
	const paintCodeVal = toStringValue(paintCode);
	const fitmentYmmVal = toStringValue(fitmentYmm);
	const fields = [yearVal, makeVal, modelVal, submodelVal, engineVal];

	const structuredYmm = [yearVal, makeVal, modelVal, submodelVal, engineVal]
		.filter(Boolean)
		.join(" ");
	const ymm = fitmentYmmVal || structuredYmm;

	const isProductPage = window.location.pathname.includes("/products/");
	updateSearchTerms({
		ymm,
		year: yearVal,
		make: makeVal,
		model: modelVal,
		submodel: submodelVal,
		engine: engineVal,
		bodyType,
		decodedBodyTypes,
		vin: vinVal,
		paintCode: paintCodeVal,
		decodedByBumper,
		lastCollectionImage,
		fitmentYmm: fitmentYmmVal || null,
		troublesome_format,
		troublesome_description,
	});

	if (isPartifyTouchupFinderOpen()) {
		const terms = getSearchTerms();
		const activeVehicle = Array.isArray(terms) && terms.length ? terms[0] : null;
		if (
			typeof window.GarageUI !== "undefined" &&
			window.GarageUI.updateVehicleSelectionElement &&
			ymm
		) {
			window.GarageUI.updateVehicleSelectionElement(ymm);
		}
		if (
			typeof window.GarageLogic !== "undefined" &&
			window.GarageLogic.updateGaragePopup
		) {
			window.GarageLogic.updateGaragePopup(terms);
		}
		window.dispatchEvent(
			new CustomEvent("partify:vehicle:changed", {
				detail: activeVehicle,
			}),
		);
		if (typeof toggleGaragePopup === "function") {
			const garagePopup = document.querySelector(".garage-easyYMM-popup");
			if (garagePopup && !garagePopup.classList.contains("hidden")) {
				toggleGaragePopup();
			}
		}
		return;
	}

	const isHomepage =
		document.body.classList.contains("template-index") ||
		window.location.pathname === "/";

	if (isProductPage) {
		updateProductPageFitment(ymm, fields, anchor);
		if (typeof window.fbtRefresh === "function") {
			window.fbtRefresh();
		}
		// window.location.reload();
	} else if (isHomepage) {
		updateProductPageFitment(ymm, fields, anchor);
		if (typeof window.syncGarageBarUI === "function") {
			window.syncGarageBarUI(ymm);
		}
		// Update top left header selector
		if (
			typeof window.GarageUI !== "undefined" &&
			window.GarageUI.updateVehicleSelectionElement &&
			ymm
		) {
			window.GarageUI.updateVehicleSelectionElement(ymm);
		}
		// Update garage popup with fresh data
		if (
			typeof window.GarageLogic !== "undefined" &&
			window.GarageLogic.updateGaragePopup
		) {
			const freshTerms = getSearchTerms();
			window.GarageLogic.updateGaragePopup(freshTerms);
		}
		if (anchor) {
			const url = new URL(anchor.href);
			window.location.href = url.pathname + url.search + url.hash;
		} else if (ymm) {
			window.location.href = "/collections/" + generateCollectionHandle(ymm);
		}
	} else {
		if (ymm) {
			window.location.href = "/collections/" + generateCollectionHandle(ymm);
		}
	}
}

function updateProductPageFitment(ymm, fields, anchor) {
	if (window.__PARTIFY_NATIVE_FITMENT === "primary") return;
}

/**
 *
 * @param {Object} vehicleData - Vehicle data object matching VEHICLE_SCHEMA
 * Allows for editing a vehicle in place
 * SHOULD NOT add any new vehicle
 * Moves vehicle to front
 */
function updateSearchTerms(vehicleData) {
	const hasExplicitSubmodel = Object.prototype.hasOwnProperty.call(
		vehicleData,
		"submodel",
	);
	const hasExplicitFitmentYmm = Object.prototype.hasOwnProperty.call(
		vehicleData,
		"fitmentYmm",
	);
	vehicleData = normalizeSearchTerm(vehicleData);
	// Destructure needed fields
	const {
		ymm,
		year: yearVal,
		make: makeVal,
		model: modelVal,
		submodel: submodelVal,
		engine: engineVal,
		vin: vinVal,
		paintCode: paintCodeVal,
		bodyType: bodyTypeVal,
		decodedBodyTypes,
		decodedByBumper,
		lastCollectionImage,
		fitmentYmm,
		troublesome_format,
		troublesome_description,
	} = vehicleData;
	const fitmentYmmVal = toStringValue(fitmentYmm);

	// Custom deduplication and update logic
	let searchTerms = getSearchTerms();

	let foundIdx = searchTerms.findIndex((term) =>
		// only check if can find a car that match year/make/model
		// if so, we only update, dont add a new car
		//term.year === yearVal && term.make === makeVal && term.model === modelVal
		{
			return term.ymm.trim() === ymm.trim();
		},
	);

	// Found a match car on ymm
	if (ymm && foundIdx > -1) {
		let updated = false;
		let formattedYmm = "";
		// debugger;

		// reference of existing car
		let car = searchTerms[foundIdx];

		// Update year if incoming is more specific or different
		if (yearVal && (!car.year || car.year !== yearVal)) {
			car.year = yearVal;
			car.submodel = null;
			car.engine = null;
			updated = true;
		}

		// Update make if incoming is more specific or different
		if (makeVal && (!car.make || car.make !== makeVal)) {
			car.make = makeVal;
			car.submodel = null;
			car.engine = null;
			updated = true;
		}

		// Update model if incoming is more specific or different
		if (modelVal && (!car.model || car.model !== modelVal)) {
			car.model = modelVal;
			car.submodel = null;
			car.engine = null;
			updated = true;
		}

		// Update submodel if incoming is more specific or different
		if (submodelVal && (!car.submodel || car.submodel !== submodelVal)) {
			car.submodel = submodelVal;
			car.engine = null;
			updated = true;
		} else if (hasExplicitSubmodel && !submodelVal && car.submodel) {
			car.submodel = null;
			car.engine = null;
			updated = true;
		}
		// Update engine if incoming is more specific
		if (engineVal && (!car.engine || car.engine !== engineVal)) {
			car.engine = engineVal;
			updated = true;
		}
		// Update VIN if it doesn't already exist
		if (vinVal && !car.vin) {
			car.vin = vinVal;
			updated = true;
		}
		const beforeBodyTypes = JSON.stringify(car.decodedBodyTypes || []);
		applyDecodedBodyTypesToVehicle(car, {
			bodyType: bodyTypeVal,
			decodedBodyTypes,
		});
		if (JSON.stringify(car.decodedBodyTypes || []) !== beforeBodyTypes) {
			updated = true;
		}
		// Update paintcode if it doesn't already exist
		if (paintCodeVal && !car.paintCode) {
			car.paintCode = paintCodeVal;
			// Update the decodedByBumper here too
			searchTerms[foundIdx].decodedByBumper = decodedByBumper;
			updated = true;
		}
		// Update troublesome_format if it doesn't already exist
		if (troublesome_format && !car.troublesome_format) {
			car.troublesome_format = troublesome_format;
			updated = true;
		}
		// Update troublesome_description if it doesn't already exist
		if (troublesome_description && !car.troublesome_description) {
			car.troublesome_description = troublesome_description;
			updated = true;
		}
		if (fitmentYmmVal) {
			if (car.fitmentYmm !== fitmentYmmVal) {
				car.fitmentYmm = fitmentYmmVal;
				updated = true;
			}
		} else if (!hasExplicitFitmentYmm || car.fitmentYmm) {
			if (car.fitmentYmm) {
				updated = true;
			}
			car.fitmentYmm = null;
		}

		if (submodelVal && engineVal) {
			formattedYmm = `${yearVal} ${makeVal} ${modelVal} ${submodelVal || ""} ${
				engineVal || ""
			}`;
		} else if (submodelVal && !engineVal) {
			formattedYmm = `${yearVal} ${makeVal} ${modelVal} ${submodelVal || ""}`;
		} else {
			formattedYmm = `${yearVal} ${makeVal} ${modelVal}`;
		}
		car.ymm = fitmentYmmVal || formattedYmm;

		// Always move this car to the front, even if nothing was updated
		if (updated || true) {
			const [movedCar] = searchTerms.splice(foundIdx, 1);
			searchTerms.unshift(car);
		}
		// GarageUI.buildVehicleListHTML(searchTerms);

		setSearchTerms(searchTerms, 3);
		// This is a new entry (did not find a match in garage)
	} else if (ymm && foundIdx === -1) {
		// debugger;
		// No match on year, make, model, so add new
		const newTerm = normalizeSearchTerm({
			ymm,
			vin: vinVal,
			paintCode: paintCodeVal,
			year: yearVal,
			make: makeVal,
			model: modelVal,
			submodel: submodelVal,
			engine: engineVal,
			bodyType: bodyTypeVal,
			decodedBodyTypes,
			decodedByBumper: decodedByBumper,
			lastCollectionImage: lastCollectionImage,
			fitmentYmm: fitmentYmmVal || null,
			troublesome_format: troublesome_format,
			troublesome_description: troublesome_description,
		});
		searchTerms.unshift(newTerm);
		// use splice to keep the limit
		if (searchTerms.length > 5) searchTerms.splice(5);
		setSearchTerms(searchTerms, 4);
	}
}

function refactorModels(make, model) {
	let refactoredModel = model;
	let capMake = make.toUpperCase();
	let capModel = model.toUpperCase();
	// Special cases:

	// Chromedata returns the word mazda along with the model type.
	// e.g. Mazda3 instead of 3
	if (refactoredModel.includes("Mazda")) {
		// For the case where it is a hatchback, will look like Mazda3 Hatchback, pull the first word
		refactoredModel = refactoredModel.split(" ")[0];
		refactoredModel = refactoredModel.replace("Mazda", "");
	}

	// Chromedata can return the submodel along with subaru submodels.
	// e.g. WRX STI instead of WRX
	if (capMake === "SUBARU" && capModel.includes("WRX")) {
		// Just take the first word in this case
		refactoredModel = refactoredModel.split(" ")[0];
	}

	if (capMake === "LEXUS") {
		refactoredModel = refactoredModel.replace(" ", "");
	}

	return refactoredModel;
}

async function updateGarageAndNavigate(vehicleData) {
	const {
		year,
		make,
		model,
		submodel,
		engine,
		bodyType,
		decodedBodyTypes,
		vin,
		paintCode,
		state,
		plate,
		decodedByBumper,
		lastCollectionImage,
		fitmentYmm,
		wmiCountry,
		troublesome_format,
		troublesome_description,
	} = vehicleData;

	const refactoredModel = refactorModels(make, model);

	let yearVal, makeVal, modelVal, submodelVal, engineVal;

	if (window.__PARTIFY_NATIVE_FITMENT === "primary") {
		// Native path: use validated values from native YMM JSON,
		// not raw ChromeData values (which may include trim prefixes
		// like "2.0T SE" instead of just "SE").
		const vr = window.EASYSEARCH_VALIDATION_RESULT || {};
		yearVal = (year || "").toString().trim();
		makeVal = (make || "").trim();
		modelVal = (vr.model || model || "").trim();
		submodelVal = vr.canResolveYMM ? vr.submodel || "" : submodel || "";
		engineVal = vr.canResolveYMM ? vr.engine || "" : engine || "";
	} else {
		yearVal = await setSelectValueWhenReady(easysearchInputYear, year || "");
		makeVal = await setSelectValueWhenReady(easysearchInputMake, make || "");
		modelVal = await setSelectValueWhenReady(easysearchInputModel, model || "");
		submodelVal = await setSelectValueWhenReady(
			easysearchInputSubmodel,
			submodel || "",
		);
		engineVal = await setSelectValueWhenReady(
			easysearchInputEngine,
			engine || "",
		);
	}

	const persistedPaint = getGaragePersistedPaintState(
		paintCode,
		decodedByBumper,
		isCurrentVehicleTroublesome,
	);

	if (yearVal === "" || makeVal === "" || modelVal === "") {
		const msg = `We couldn't find ${[year, make, model].filter(Boolean).join(" ")} in our catalog. Please select your vehicle manually using the dropdowns.`;
		const gv2ErrorEl = document.querySelector("[data-gv2-vin-error]");
		const legacyErrorEl = document.querySelector(".errorMessageVIN");
		if (gv2ErrorEl) {
			gv2ErrorEl.textContent = msg;
		}
		if (legacyErrorEl) {
			legacyErrorEl.innerHTML = msg;
			legacyErrorEl.style.visibility = "visible";
		}
		return false;
	}

	let formattedYmm = "";
	if (submodelVal && engineVal) {
		formattedYmm = `${yearVal} ${makeVal} ${modelVal} ${submodelVal || ""} ${
			engineVal || ""
		}`;
	} else if (submodelVal && !engineVal) {
		formattedYmm = `${yearVal} ${makeVal} ${modelVal} ${submodelVal || ""}`;
	} else {
		formattedYmm = `${yearVal} ${makeVal} ${modelVal}`;
	}
	const fitmentYmmVal = toStringValue(fitmentYmm);
	const displayYmm = fitmentYmmVal || formattedYmm;

	// updateSearchTerms(
	// 	formattedYmm,
	// 	yearVal,
	// 	makeVal,
	// 	modelVal,
	// 	submodelVal,
	// 	engineVal,
	// 	vin,
	// 	localPaintCode,
	// 	decodedByBumper,
	// 	lastCollectionImage
	// );

	const updatedSearchTerms = moveVehicleToFront({
		ymm: displayYmm,
		fitmentYmm: fitmentYmmVal || null,
		vin: vin || null,
		paintCode: persistedPaint.paintCode,
		year: yearVal || null,
		make: makeVal || null,
		model: modelVal || null,
		submodel: submodelVal || null,
		engine: engineVal || null,
		bodyType: bodyType || null,
		decodedBodyTypes,
		state: state || null,
		plate: plate || null,
		decodedByBumper: persistedPaint.decodedByBumper,
		...(wmiCountry ? { wmiCountry } : {}),
		troublesome_format: troublesome_format || null,
		troublesome_description: troublesome_description || null,
	});

	if (isPartifyTouchupFinderOpen()) {
		const activeVehicle =
			Array.isArray(updatedSearchTerms) && updatedSearchTerms.length
				? updatedSearchTerms[0]
				: getSearchTerms()[0] || null;
		if (
			typeof window.GarageUI !== "undefined" &&
			window.GarageUI.updateVehicleSelectionElement &&
			displayYmm
		) {
			window.GarageUI.updateVehicleSelectionElement(displayYmm);
		}
		if (
			typeof window.GarageLogic !== "undefined" &&
			window.GarageLogic.updateGaragePopup
		) {
			window.GarageLogic.updateGaragePopup(getSearchTerms());
		}
		window.dispatchEvent(
			new CustomEvent("partify:vehicle:changed", {
				detail: activeVehicle,
			}),
		);
		if (typeof toggleGaragePopup === "function") {
			const garagePopup = document.querySelector(".garage-easyYMM-popup");
			if (garagePopup && !garagePopup.classList.contains("hidden")) {
				toggleGaragePopup();
			}
		}
		return true;
	}

	if (!window.location.pathname.includes("/products/")) {
		// Check if on homepage
		const isHomepage =
			document.body.classList.contains("template-index") ||
			window.location.pathname === "/" ||
			window.location.pathname === "/index";
		if (isHomepage) {
			let anchor = null;
			const holder = document.querySelector(".easysearch-holder");
			const actionsHolder = holder?.querySelector(".easysearch-actions-holder");
			if (actionsHolder) {
				const btnHolder = actionsHolder.querySelector(".easysearch-btn-holder");
				if (btnHolder) {
					anchor = btnHolder.querySelector("a");
				}
			}
			updateProductPageFitment(
				displayYmm,
				[
					toStringValue(yearVal),
					toStringValue(makeVal),
					toStringValue(modelVal),
					toStringValue(submodelVal),
					toStringValue(engineVal),
				],
				anchor,
			);
			// Update middle garage bar
			if (typeof window.syncGarageBarUI === "function") {
				window.syncGarageBarUI(displayYmm);
			}
			// Update top left header selector (one-way, no circular calls)
			if (
				typeof window.GarageUI !== "undefined" &&
				window.GarageUI.updateVehicleSelectionElement &&
				displayYmm
			) {
				window.GarageUI.updateVehicleSelectionElement(displayYmm);
			}

			// CRITICAL: Force garage popup UI refresh with fresh data
			if (
				typeof window.GarageLogic !== "undefined" &&
				window.GarageLogic.updateGaragePopup
			) {
				const freshTerms = getSearchTerms();
				window.GarageLogic.updateGaragePopup(freshTerms);
			}

			// Close the garage popup after adding vehicle
			if (typeof toggleGaragePopup === "function") {
				const garagePopup = document.querySelector(".garage-easyYMM-popup");
				if (garagePopup && !garagePopup.classList.contains("hidden")) {
					toggleGaragePopup();
				}
			}
			("");

			// Homepage-only redirect after successful add/select save.
			// Do not redirect while editing a vehicle.
			const isEditingVehicle = Boolean(
				window.editCarState && window.editCarState.open,
			);
			if (!isEditingVehicle) {
				const activeVehicle =
					Array.isArray(updatedSearchTerms) && updatedSearchTerms.length
						? updatedSearchTerms[0]
						: getSearchTerms()[0] || null;
				if (toStringValue(activeVehicle?.fitmentYmm)) {
					await navigateToGarageVehicleCollection(activeVehicle);
					return true;
				}
				let targetUrl = "";

				if (
					anchor &&
					anchor.href &&
					!anchor.href.includes("javascript:void(0)")
				) {
					try {
						const parsed = new URL(anchor.href, window.location.origin);
						if (
							parsed.origin === window.location.origin &&
							parsed.pathname.startsWith("/")
						) {
							targetUrl = parsed.pathname + parsed.search + parsed.hash;
						}
					} catch (e) {
						// no-op
					}
				}

				if (!targetUrl && formattedYmm) {
					const collectionHandle = generateCollectionHandle(formattedYmm);
					if (collectionHandle) {
						targetUrl = `/collections/${collectionHandle}`;
					}
				}

				if (targetUrl && targetUrl.startsWith("/")) {
					recordVehicleCollectionRecoveryContext(targetUrl, {
						year: yearVal,
						make: makeVal,
						model: modelVal,
						vin,
					});
					window.location.assign(targetUrl);
				}
			}
		} else if (window.__GARAGE_CONTROLLER__ !== "v2") {
			if (formattedYmm) {
				window.location.href =
					"/collections/" + generateCollectionHandle(formattedYmm);
			}
		}
	} else {
		// Product page: redirect to the vehicle's collection
		// if (formattedYmm) {
		// 	const collectionHandle = generateCollectionHandle(formattedYmm);
		// 	if (collectionHandle) {
		// 		window.location.assign("/collections/" + collectionHandle);
		// 		return true;
		// 	}
		// }
		updateEasysearchLocalStorageOrNavigate({
			year: yearVal,
			make: makeVal,
			model: modelVal,
			submodel: submodelVal,
			engine: engineVal,
			vin,
			paintCode: persistedPaint.paintCode,
			decodedByBumper: persistedPaint.decodedByBumper,
			lastCollectionImage: null,
			fitmentYmm: fitmentYmmVal || null,
			troublesome_format,
			troublesome_description,
		});
		firstVehicleColor = handleInsertLocalStoragePaintOption();
	}
	return true;
}

// Used for generating the correct filtered URL for non compatible vehicles
function updateGarageFirstVehicleData() {
	const vehicleDataContainer = document.querySelector(
		".vehicle-item-container.first-vehicle-item-container .vehicle-browse-catalog-container strong[data-vehicle]",
	);
	const notCompatibleBtnContainer = document.querySelector(
		".not-compatible-button",
	);
	if (vehicleDataContainer) {
		garageFirstVehicleData = vehicleDataContainer.getAttribute("data-vehicle");
	}
	if (!garageFirstVehicleData) {
		if (notCompatibleBtnContainer)
			notCompatibleBtnContainer.style.display = "none";
	} else {
		if (notCompatibleBtnContainer)
			notCompatibleBtnContainer.style.display = "block";
	}
}

/*******************************************************************************************************
 **                                                                                                    **
 **                                             UTILITIES                                              **
 **                                                                                                    **
 *******************************************************************************************************/

async function getIpAddress() {
	try {
		const response = await fetch("https://api.ipify.org?format=json");
		const data = await response.json();
		return data.ip; // Returns the client's public IP
	} catch (error) {
		console.error("Error fetching IP address:", error);
		return null;
	}
}

function setVinDecoderInputValue(storedVinMsg, vinInputBox, vinSubmitBtn) {
	const garageSearchTerms = getSearchTerms();
	if (
		garageSearchTerms &&
		garageSearchTerms[0] &&
		garageSearchTerms[0].vin &&
		!isMismatch
	) {
		const vin = garageSearchTerms[0].vin;
		const ymm = garageSearchTerms[0].ymm || "";
		if (storedVinMsg) {
			storedVinMsg.style.display = "block";
			if (ymm === "") {
				storedVinMsg.textContent = `Using VIN: ${vin}`;
			} else {
				storedVinMsg.textContent = `Using ${vin} for your ${ymm}`;
			}
			vinInputBox.value = vin;
			vinSubmitBtn.disabled = false;
		}
	}
}

function removeVinDecoderInputValue(storedVinMsg, vinInputBox, vinSubmitBtn) {
	const garageSearchTerms = getSearchTerms();
	if (
		garageSearchTerms &&
		garageSearchTerms[0] &&
		garageSearchTerms[0].vin &&
		!isMismatch
	) {
		if (storedVinMsg) {
			storedVinMsg.style.display = "none";
			storedVinMsg.textContent = "";
			vinInputBox.value = "";
			vinSubmitBtn.disabled = true;
		}
	}
}

/*
    functionLocation - 1 = Garage
    functionLocation - 2 = Vin Verification
*/
// Function to handle VIN change
function handleVinChange(event, functionLocation, errorMsg) {
	let activeWrapper;
	const vinElements = getVisibleVinDecoderElements();
	if (vinElements) {
		activeWrapper = vinElements.wrapper;
	}
	let vinInput = event.target.value.toUpperCase();
	const filteredValue = sanitizeVehicleLookupVin(vinInput);
	const truncatedValue = filteredValue.slice(0, 17);

	event.target.value = truncatedValue;

	if (functionLocation === 1) {
		if (attemptedVinsInGarage.length) {
			for (let i = 0; i < attemptedVinsInGarage.length; i++) {
				if (
					attemptedVinsInGarage[i].toUpperCase() ===
					truncatedValue.toUpperCase()
				) {
					document.querySelector(".errorMessageVIN").style.visibility =
						"visible";
					document.querySelector(".errorMessageVIN").innerHTML =
						"VIN already searched. Please try a different VIN";
					document.getElementById("vin-to-collection-btn").disabled = true;
					return;
				} else {
					document.querySelector(".errorMessageVIN").style.visibility =
						"hidden";
				}
			}
		}

		if (truncatedValue.length === 17) {
			document.getElementById("vin-to-collection-btn").disabled = false;
		} else {
			document.getElementById("vin-to-collection-btn").disabled = true;
		}

		if (vinInput !== filteredValue) {
			alert(errorMsg);
		}
	} else if (functionLocation === 2) {
		if (attemptedVinsVinVerification.length) {
			for (let i = 0; i < attemptedVinsVinVerification.length; i++) {
				if (
					attemptedVinsVinVerification[i].toUpperCase() ===
					truncatedValue.toUpperCase()
				) {
					let activeWrapper;
					if (vinElements) {
						activeWrapper = vinElements.wrapper;
					}
					const vinErrorMsgContainer = activeWrapper.querySelector(
						".vin-error-message-container",
					);
					vinErrorMsgContainer.style.display = "block";

					activeWrapper.querySelector(
						".vin-verification-submission-failed-message",
					).style.display = "block";
					activeWrapper.querySelector(
						".vin-verification-submission-failed-message",
					).innerHTML = "VIN already searched. Please try a different VIN";
					//document.getElementById('vin-to-collection-btn').disabled = true;

					document.querySelector(
						'.vin-decoder-submit-btn:not([style*="display: none"])',
					).disabled = true;
					return true;
				} else {
					activeWrapper.querySelector(
						".vin-verification-submission-failed-message",
					).style.display = "none";
				}
			}
		}

		if (truncatedValue.length === 17) {
			document.getElementById("vin-to-collection-btn").disabled = false;
		} else {
			document.getElementById("vin-to-collection-btn").disabled = true;
		}

		if (vinInput !== filteredValue) {
			alert(errorMsg);
		}
	} else if (functionLocation === 3) {
		if (attemptedDecodedVins.length) {
			for (let i = 0; i < attemptedDecodedVins.length; i++) {
				if (
					attemptedDecodedVins[i].toUpperCase() === truncatedValue.toUpperCase()
				) {
					let activeWrapper;
					if (vinElements) {
						activeWrapper = vinElements.wrapper;
					}
					const vinErrorMsgContainer = activeWrapper.querySelector(
						".vin-error-message-container",
					);
					vinErrorMsgContainer.style.display = "block";

					activeWrapper.querySelector(
						".vin-decoder-submission-failed-message",
					).style.display = "block";
					activeWrapper.querySelector(
						".vin-decoder-submission-failed-message",
					).innerHTML = "VIN already searched. Please try a different VIN";
					const addToCartBtn = document.querySelector(".add-to-cart");
					if (addToCartBtn) addToCartBtn.disabled = true;
					// document.getElementById('vin-to-collection-btn').disabled = true;
					return true;
				} else {
					activeWrapper.querySelector(
						".vin-decoder-submission-failed-message",
					).style.display = "none";
				}
			}
		}

		if (vinInput !== filteredValue) {
			alert(errorMsg);
		}
	}
}

function handleStateSelectChange(
	event,
	errorAlreadyAttempted,
	errorMessageSelector,
	buttonId,
	licenseInput,
	oRestrictedStates,
) {
	const stateInput = event.target.value.toUpperCase();
	const licenseBtn = document.getElementById(buttonId);
	const errorMessageLicense = document.querySelector(errorMessageSelector);
	let licenseValue = document.getElementById(licenseInput).value;

	// If a user typed in their license before selected a state, and the state restricts "O"s, then we need to clean the license plate now
	// This is to avoid a situation where a user types in a license plate with "O"s, then selects a state that restricts "O"s, but the license plate still has "O"s in it
	if (licenseValue.length) {
		licenseValue = cleanLicensePlate(
			stateInput,
			licenseValue,
			oRestrictedStates,
		);

		licenseGarageLatest = licenseValue;
		document.getElementById(licenseInput).value = licenseValue;
	}
	event.target.value = stateInput;
	if (
		attemptedDecodedLicensePlates.includes(
			stateInput + "-" + licenseGarageLatest,
		)
	) {
		licenseBtn.disabled = true;
		errorMessageLicense.style.visibility = "visible";
		errorMessageLicense.innerHTML = errorAlreadyAttempted;
		return;
	}
	if (
		errorMessageLicense.style.visibility &&
		errorMessageLicense.style.visibility === "visible"
	)
		errorMessageLicense.style.visibility = "hidden";
	if (
		typeof licenseGarageLatest === "string" &&
		licenseGarageLatest.length > 0
	) {
		licenseBtn.disabled = false;
	} else {
		licenseBtn.disabled = true;
	}
}

function handleLicenseChange(
	event,
	functionLocation,
	errorMsg,
	errorAlreadyAttempted,
	stateDropdownClass,
	oRestrictedStates,
	id,
) {
	let licenseInput = event.target.value.toUpperCase();
	const stateSelect = document.querySelector(stateDropdownClass);
	const state = stateSelect ? stateSelect.value : "";
	let sanitizedPlateInput = normalizeVehicleLookupPlate(
		state,
		licenseInput,
		oRestrictedStates,
	);

	event.target.value = sanitizedPlateInput;
	const licenseBtn = document.getElementById(id);
	const errorMessageLicense = document.querySelector(".errorMessageLicense");

	licenseGarageLatest = sanitizedPlateInput;
	if (
		attemptedDecodedLicensePlates.includes(state + "-" + sanitizedPlateInput)
	) {
		licenseBtn.disabled = true;
		errorMessageLicense.style.visibility = "visible";
		errorMessageLicense.innerHTML = errorAlreadyAttempted;
		return;
	}
	if (errorMessageLicense.style.visibility === "visible")
		errorMessageLicense.style.visibility = "hidden";
	if (state.length && sanitizedPlateInput.length > 0) {
		licenseBtn.disabled = false;
	} else {
		licenseBtn.disabled = true;
	}
}

/**
 * @param {*} vinInput
 * @param {*} boolMatchByVIN
 * Appends on to the end of the variant selector the option to match by VIN
 * if boolMatchByVIN is true and the option exists in the DOM as a hidden div
 */

function handleAppendMatchByVin(vinInput, displayMsg) {
	// Reference: resortToGetPaintCodeByVin
	const combinedVariantSelect = document.getElementById("variant-selector");
	if (!combinedVariantSelect) {
		console.warn("Variant selector dropdown not found.");
		return "";
	}
	const vin = vinInput;
	let hiddenDiv;
	let priceDifference;
	storedDecodedVin = vin;

	// We must determine if the option in variant-selector for "match paint by vin" already exists before continuing
	const existingOption = Array.from(combinedVariantSelect.options).find(
		(option) => option.label.startsWith("Match Paint by VIN"),
	);

	// If there is no current existing option, then we can proceed to add it
	// We look for the hidden div that contains the option information
	// If it exists, we create a new option in the variant-selector select element
	// We then append the option to the select element and remove the hidden div from the DOM
	// Finally we auto select the option to match by VIN and formulate the message to display to the user
	if (!existingOption || existingOption === undefined) {
		if (document.querySelector(".hidden-vin-option")) {
			hiddenDiv = document.querySelector(".hidden-vin-option");
		} else if (document.querySelector(".hidden-vin-option-single-quality")) {
			hiddenDiv = document.querySelector(".hidden-vin-option-single-quality");
		}

		// Only display the option to select Match Paint by VIN in certain circumstances
		if (hiddenDiv) {
			if (!combinedVariantSelect) {
				console.error("Error: Select element not found.");
				return;
			}

			let value;
			if (hiddenDiv.getAttribute("data-value")) {
				value = hiddenDiv.getAttribute("data-value");
			} else if (hiddenDiv.getAttribute("value")) {
				value = hiddenDiv.getAttribute("value");
			}

			const text = hiddenDiv.textContent;
			const price = hiddenDiv.getAttribute("data-price") || "";
			priceDifference = hiddenDiv.getAttribute("data-price-difference") || "";
			const color = hiddenDiv.getAttribute("data-color") || "";
			const variantTitle = hiddenDiv.getAttribute("data-variant-title") || "";
			const productTitle = hiddenDiv.getAttribute("data-product-title") || "";
			const variantSKU = hiddenDiv.getAttribute("data-variant-sku") || "";
			const option = document.createElement("option");

			option.value = value;
			option.textContent = text;
			option.setAttribute("data-price", price);
			option.setAttribute("data-price-difference", priceDifference);
			option.setAttribute("data-color", color);
			option.setAttribute("data-variant-title", variantTitle);
			option.setAttribute("data-product-title", productTitle);
			option.setAttribute("data-variant-sku", variantSKU);
			option.setAttribute(
				"data-supplier",
				hiddenDiv.getAttribute("data-supplier") || "",
			);

			option.classList.add("hidden-vin-option");

			// Append and remove
			combinedVariantSelect.appendChild(option);
			hiddenDiv.remove();
		} else {
			window.NO_MATCH_BY_VIN_OPTION_EXISTS = true;
			return "";
		}
	}

	// Unless there is no hiddenDiv (meaning that the option to match by VIN does not exist on
	// that product in Shopify), when this function is called we run the following functions
	// If the option to match by VIN does exist, it will be auto selected and the
	// message will be formulated
	// handleAutoSelectMatchByVin();
	handleFormulateVinMessage(vin, displayMsg);
	if (typeof amountOfVINPostMessages !== "undefined") {
		amountOfVINPostMessages++;
	}
	return formatPriceToUSDGlobal(priceDifference);
}

/**
 * We must always call this function directly after handleAppendMatchByVin.
 * This is because the option for Match Paint by VIN does not exist as an option prior to calling that function.
 * This function will auto select the option to match by VIN if it exists in the DOM
 */
function handleAutoSelectMatchByVin() {
	const combinedVariantSelect = document.getElementById("variant-selector");
	if (!combinedVariantSelect) return;
	const options = Array.from(combinedVariantSelect.options);
	options.find((option) => {
		const codeInFrontOfPrice = option.label.split("+")[0];
		if (codeInFrontOfPrice.trim() === "Match Paint by VIN") {
			combinedVariantSelect.value = option.value;
			autoSelectedColor = option.value;
			const event = new Event("change", { bubbles: true });
			event._fromAutoSelect = true;
			combinedVariantSelect.dispatchEvent(event);
		}
	});
}

/**
 *
 * @param {*} vin
 * @param {*} displayMsg bool to display the message or not--needed if match paint by vin is determined at the garage level vs decoder level
 */
function handleFormulateVinMessage(vin, displayMsg = false) {
	// Reference: formulateMessage
	const variantSelector = document.getElementById("variant-selector");
	const paintOptionsCheckboxGroup = document.querySelector(
		".paint-options-checkbox-group",
	);
	const selectCodeFailSafe = document.querySelector(".select-code-fail-safe");
	const failSafeIsVisible =
		selectCodeFailSafe && selectCodeFailSafe.style.display !== "none";

	if (variantSelector) {
		const options = Array.from(variantSelector.options);
		options.find((option) => {
			if (
				(option.label.startsWith("Match Paint by VIN") ||
					option.label.startsWith("Pintura de combinación por VIN")) &&
				document.querySelector(".vin-selected-message") === null
			) {
				const vinMessage = document.createElement("div");
				vinMessage.className = "vin-decoder-message vin-selected-message";
				vinMessage.style.display = displayMsg ? "block" : "none";
				vinMessage.innerHTML = `
                            <strong style=\"color: #444;\">${vinAttachedToOrderTranslationPayload.autoAttachMsgStrong}</strong> 
                            <span style=\"color: #000; font-weight: 800;\">${vin}</span> 
                            <span style=\"color: #444;\">${vinAttachedToOrderTranslationPayload.willBeAttached}</span> 
                            <span style=\"color: #444; font-weight: bold;\">${vinAttachedToOrderTranslationPayload.bold}</span> 
                            <span style=\"color: #444;\">${vinAttachedToOrderTranslationPayload.forThisItem}</span>
                        `;
				if (failSafeIsVisible) {
					selectCodeFailSafe.insertAdjacentElement("afterend", vinMessage);
				} else if (paintOptionsCheckboxGroup) {
					paintOptionsCheckboxGroup.insertAdjacentElement(
						"afterend",
						vinMessage,
					);
				}
				// }
				return true;
			}
		});
	} else {
		console.warn("Variant selector dropdown not found.");
	}
}

async function createCompanyProfile(
	companyName,
	firstName,
	lastName,
	email,
	phone,
	title,
	address,
	city,
	zipCode,
	stateAbbr,
	locationName,
	pdf,
	taxRegistrationId,
	typeText,
	reasonText,
) {
	try {
		const response = await fetch(
			"https://tax-exemption-signup-505215902673.us-east5.run.app/createCustomerProfile",
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					companyName,
					firstName,
					lastName,
					email,
					phone,
					title,
					address,
					city,
					zipCode,
					stateAbbr,
					locationName,
					pdf,
					taxRegistrationId,
					typeText,
					reasonText,
				}),
			},
		);

		if (!response.ok) {
			throw new Error(
				`Error fetching order details. Status: ${response.status}. Message: ${response.statusText}.`,
			);
		}
		const data = await response.json();
	} catch (error) {
		console.error("Error fetching vehicle data:", error);
	}
}

async function fetchFromBumper(vin) {
	const url = `https://bumperdotcom-api-345230973812.us-east5.run.app/bumperdotcom-api?vin=${vin}`;
	const res = await fetch(url, {
		method: "GET",
		headers: { "Content-Type": "application/json" },
	});
	const data = await res.json();
	if (!res.ok) {
		const err = new Error("Bumper API error");
		err.status = res.status;
		err.raw = data;
		throw err;
	}
	return data;
}

async function fetchFromChromeData(vin, state, plate, location) {
	const url =
		"https://garage-vin-service-node-740168228309.us-east5.run.app/fetchVehicleData";
	const res = await fetch(url, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ vin, state, plate, location }),
	});
	const data = await res.json();
	if (!res.ok) {
		const err = new Error("ChromeData API error");
		err.status = res.status;
		err.raw = data;
		throw err;
	}
	return data;
}

async function fetchCustomerAccountData(id) {
	if (!id) return;
	const url = "https://customer-account-data-725897343962.us-east5.run.app";

	const res = await fetch(url, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ customerId: id }),
	});

	const data = await res.json();

	if (!res.ok) {
		const err = new Error("Customer Account Data API error");
		err.status = res.status;
		err.raw = data;
		throw err;
	}

	return data;
}

function formatSearchTermsForJSON(terms) {
	if (!Array.isArray(terms)) {
		throw new Error("formatSearchTermsForJSON expects an array");
	}

	const vehicles = terms
		.filter((v) => v && typeof v === "object")
		.map((v) => {
			const year =
				typeof v.year === "string" && /^\d+$/.test(v.year)
					? Number(v.year)
					: (v.year ?? null);

			return {
				year,
				make: v.make ?? null,
				model: v.model ?? null,
				submodel: v.submodel ?? null,
				engine: v.engine ?? null,
				bodyType: v.bodyType ?? null,
				decodedBodyTypes: normalizeDecodedBodyTypeList(v.decodedBodyTypes),
				vin: v.vin ?? null,
				plate: v.plate ?? null,
				state: v.state ?? null,
				paintCode: v.paintCode ?? null,
				decodedByBumper: v.decodedByBumper ?? null,
				lastCollectionImage: v.lastCollectionImage ?? null,
				ymm: v.ymm ?? null,
				fitmentYmm: v.fitmentYmm ?? null,
				troublesome_format: v.troublesome_format ?? null,
				troublesome_description: v.troublesome_description ?? null,
				...(v.wmiCountry ? { wmiCountry: v.wmiCountry } : {}),
			};
		});

	return {
		vehicles,
	};
}

async function updateCustomerAccountGarage(garageData) {
	const id = getPartifyCustomerId();
	if (!id) {
		throw new Error("Missing customer id");
	}

	if (
		!garageData ||
		typeof garageData !== "object" ||
		Array.isArray(garageData)
	) {
		throw new Error("garageData must be a plain object");
	}

	const url =
		"https://update-customer-account-info-49754682551.us-east5.run.app";

	const res = await fetch(url, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify({
			customerId: id,
			garageData,
		}),
	});

	let data;

	try {
		data = await res.json();
	} catch (e) {
		throw new Error("Failed to parse updateCustomerAccountGarage response");
	}

	if (!res.ok) {
		const err = new Error("Customer garage update API error");
		err.status = res.status;
		err.raw = data;
		throw err;
	}

	return data;
}

// Normalize bumper and chromedata payloads into a common shape.
// We could probably reduce this function further as we are only getting the paintcode info
// from those paintcode makes, but for now we will keep all the vehicle info in case we want to
// use it later.
function normalizeBumperData(raw) {
	let errorMessage = "";
	let colorCode = "";
	let validVehicleFound = false;
	let genericDesc = "";
	let fullDescription = "";
	let hex = "";

	const vehicle = raw.entities?.vehicles?.automobiles?.[0] || {};
	const vehicleError = raw.entities?.vehicles?.automobiles.length === 0;
	// We can set vin to valid here because if it was invalid, it would have thrown and err prior to this function call
	const validVin = true;
	const design = vehicle.design || {};

	const year = vehicle.year || "";
	const make = vehicle.make || "";
	const model = vehicle.model || "";
	const submodel = vehicle.design?.trim || "";
	const engineSpecs = vehicle.specs?.engine || {};
	const cylinders = engineSpecs.cylinders;
	const size = engineSpecs.size;
	const engine =
		cylinders && size ? `${cylinders} Cyl ${Number(size).toFixed(1)}L` : "";
	const ymm = `${year} ${make} ${model} ${submodel} ${engine}`.trim();

	const colors = design.colors || [];
	const exteriorColors = colors.filter((c) => c.category === "Exterior");
	if (exteriorColors.length === 1) {
		colorCode = exteriorColors[0].code || "";
		colorCode = normalizeToyotaColorCode(make, colorCode);
		genericDesc = exteriorColors[0].generic_name || "";
		fullDescription = exteriorColors[0].name || "";
		// Sadly bumper.com does not return hex values
		hex = "";
	} else if (exteriorColors.length > 1) {
		errorMessage = `Too many paint codes to determine`;
	} else {
		errorMessage = `No exterior colors found for the provided VIN`;
	}
	if (year !== "" && make !== "" && model !== "") {
		validVehicleFound = true;
	}

	return {
		validVin,
		validVehicleFound,
		vehicleError,
		year,
		make,
		model,
		submodel,
		engine,
		ymm,
		colorCode: colorCode,
		genericDesc: genericDesc || "",
		fullDescription: fullDescription || "",
		hex: hex || "",
		site: "Bumper.com",
		decodedByBumper: true,
		errorMessage,
	};
}

// Extract a clean body-type word from a raw Chrome bodyType or styleDescription string.
// bodyType already returns clean values like "Sedan", "Coupe", "SUV".
// styleDescription returns values like "4dr Sedan (3.5L 6cyl 7A)", "Sport Utility 4D", etc.
// Returns "" when the input is empty.
const CLEAN_BODY_TYPES = [
	"Sedan",
	"Coupe",
	"Convertible",
	"Hatchback",
	"Wagon",
	"SUV",
	"Truck",
	"Van",
	"Minivan",
];
function normalizeBodyTypeFromStyleDescription(str) {
	if (!str) return "";
	// If input is already a clean body type word, return it directly
	if (CLEAN_BODY_TYPES.includes(str.trim())) return str.trim();
	const s = str.toLowerCase();
	if (s.includes("sedan")) return "Sedan";
	if (s.includes("coupe")) return "Coupe";
	if (s.includes("convertible")) return "Convertible";
	if (s.includes("hatchback")) return "Hatchback";
	if (s.includes("wagon")) return "Wagon";
	if (s.includes("suv") || s.includes("sport utility")) return "SUV";
	if (s.includes("cab")) return str.trim(); // preserve full cab descriptor
	return str.trim();
}

function normalizeChromeDataSubmodel(dataResult) {
	const sources = [dataResult];
	if (Array.isArray(dataResult?.vehicles)) {
		dataResult.vehicles.forEach(function (vehicle) {
			sources.push(vehicle);
		});
	}

	const distinctValues = new Map();
	sources.forEach(function (source) {
		if (!source) return;
		[
			source.submodel,
			source.subModel,
			source.trim,
			source.trimName,
			source.series,
		].forEach(function (value) {
			const cleaned = value == null ? "" : String(value).trim();
			if (!cleaned) return;
			const compareKey = cleaned.toLowerCase();
			if (!distinctValues.has(compareKey)) {
				distinctValues.set(compareKey, cleaned);
			}
		});
	});

	return distinctValues.size === 1 ? distinctValues.values().next().value : "";
}

function normalizeBodyTypeForCompare(value) {
	return String(value || "")
		.trim()
		.toLowerCase()
		.replace(/&/g, "and")
		.replace(/[^a-z0-9]+/g, "");
}

function normalizeDecodedBodyTypeList(values) {
	const source = Array.isArray(values)
		? values
		: String(values || "")
				.split(",")
				.map(function (value) {
					return value.trim();
				});
	const seen = new Set();
	const decodedBodyTypes = [];

	source.forEach(function (value) {
		const cleaned = normalizeBodyTypeFromStyleDescription(value);
		const compareKey = normalizeBodyTypeForCompare(cleaned);
		if (!cleaned || !compareKey) return;
		if (seen.has(compareKey)) return;
		seen.add(compareKey);
		decodedBodyTypes.push(cleaned);
	});

	return decodedBodyTypes;
}

function getBodyTypeAcqMappingTarget(decodedBodyType) {
	const decodedKey = normalizeBodyTypeForCompare(decodedBodyType);
	const mapping = window.__PARTIFY_BODY_STYLE_MAP || null;
	if (!decodedKey || !mapping) return "";

	if (typeof mapping === "object") {
		for (const source in mapping) {
			if (!Object.prototype.hasOwnProperty.call(mapping, source)) continue;
			if (normalizeBodyTypeForCompare(source) === decodedKey) {
				return mapping[source];
			}
		}
	}

	return "";
}

function resolveBodyTypeAcqMatch(decodedValues, selectedValue, optionValues) {
	function buildResult(mismatch, matched, ambiguous) {
		return {
			mismatch,
			matched,
			ambiguous,
			decodedBodyTypes,
			resolvedBodyTypes,
		};
	}

	const decodedBodyTypes = normalizeDecodedBodyTypeList(decodedValues);
	const selectedKey = normalizeBodyTypeForCompare(selectedValue);
	const optionKeys = new Set(
		(Array.isArray(optionValues) ? optionValues : String(optionValues || "").split(","))
			.map(normalizeBodyTypeForCompare)
			.filter(Boolean),
	);
	const resolvedBodyTypes = [];
	let hasUnresolvedDecodedValue = false;

	if (!decodedBodyTypes.length || !selectedKey || !optionKeys.size) {
		return buildResult(false, false, true);
	}

	decodedBodyTypes.forEach(function (decodedBodyType) {
		const directKey = normalizeBodyTypeForCompare(decodedBodyType);
		let resolvedValue = "";
		let resolvedKey = "";

		if (optionKeys.has(directKey)) {
			resolvedValue = decodedBodyType;
			resolvedKey = directKey;
		} else {
			resolvedValue = getBodyTypeAcqMappingTarget(decodedBodyType);
			resolvedKey = normalizeBodyTypeForCompare(resolvedValue);
		}

		if (resolvedKey && optionKeys.has(resolvedKey)) {
			resolvedBodyTypes.push(resolvedValue);
		} else {
			hasUnresolvedDecodedValue = true;
		}
	});

	if (
		resolvedBodyTypes.some(function (bodyType) {
			return normalizeBodyTypeForCompare(bodyType) === selectedKey;
		})
	) {
		return buildResult(false, true, false);
	}

	if (hasUnresolvedDecodedValue || !resolvedBodyTypes.length) {
		return buildResult(false, false, true);
	}

	return buildResult(true, false, false);
}

function collectChromeDataDecodedBodyTypes(dataResult) {
	const rawValues = [];
	if (Array.isArray(dataResult?.vehicles)) {
		dataResult.vehicles.forEach(function (vehicle) {
			if (vehicle && vehicle.bodyType) rawValues.push(vehicle.bodyType);
		});
	}
	rawValues.push(dataResult?.bodyType || "");
	return normalizeDecodedBodyTypeList(rawValues);
}

function applyDecodedBodyTypesToVehicle(vehicle, decodedBodyTypeData) {
	if (!vehicle || typeof vehicle !== "object") return vehicle;
	const incomingBodyTypes = normalizeDecodedBodyTypeList(
		decodedBodyTypeData?.decodedBodyTypes ||
			decodedBodyTypeData?.bodyTypes ||
			"",
	);
	if (incomingBodyTypes.length) {
		vehicle.decodedBodyTypes = incomingBodyTypes;
		vehicle.bodyType =
			normalizeBodyTypeFromStyleDescription(decodedBodyTypeData?.bodyType) ||
			incomingBodyTypes[0];
	} else {
		const existingBodyTypes = normalizeDecodedBodyTypeList(
			vehicle.decodedBodyTypes || "",
		);
		vehicle.decodedBodyTypes = existingBodyTypes;
	}
	return vehicle;
}

window.PartifyBodyTypeValidation = window.PartifyBodyTypeValidation || {};
window.PartifyBodyTypeValidation.normalizeBodyTypeForCompare =
	normalizeBodyTypeForCompare;
window.PartifyBodyTypeValidation.normalizeDecodedBodyTypeList =
	normalizeDecodedBodyTypeList;
window.PartifyBodyTypeValidation.collectChromeDataDecodedBodyTypes =
	collectChromeDataDecodedBodyTypes;
window.PartifyBodyTypeValidation.resolveBodyTypeAcqMatch =
	resolveBodyTypeAcqMatch;

function normalizeChromeData(raw) {
	let errorMessage = "";
	let colorCode = "";
	let validVehicleFound = false;
	let styleId = "";
	let genericDesc = "";
	let fullDescription = "";
	let hex = "";
	const dataResult = raw.vehicleData?.result || {};
	const hasValidatedVin =
		dataResult.validVin?.toString().toLowerCase() === "true";
	const wmiCountry =
		hasValidatedVin && typeof dataResult.wmiCountry === "string"
			? dataResult.wmiCountry.trim()
			: "";
	const year = dataResult.year || "";
	const make = dataResult.make || "";
	const model = dataResult.model || "";
	const submodel = normalizeChromeDataSubmodel(dataResult);
	const decodedBodyTypes = collectChromeDataDecodedBodyTypes(dataResult);
	const bodyType = normalizeBodyTypeFromStyleDescription(
		decodedBodyTypes[0] ||
			dataResult.vehicles?.[0]?.bodyType ||
			dataResult.bodyType ||
			dataResult.styleDescription ||
			"",
	);
	const engine = raw?.engine || "";
	const ymm = `${year} ${make} ${model} ${submodel} ${engine}`.trim();
	const exteriorColors = dataResult.exteriorColors || [];
	if (exteriorColors.length === 1) {
		colorCode = exteriorColors[0].colorCode || "";
		colorCode = normalizeToyotaColorCode(make, colorCode);
		styleId =
			exteriorColors[0].styles.length === 1 ? exteriorColors[0].styles[0] : "";
		genericDesc = exteriorColors[0].genericDesc || "";
		fullDescription = exteriorColors[0].description || "";
		hex = exteriorColors[0].rgbHexValue || "";
	} else if (exteriorColors.length > 1) {
		errorMessage = `Too many paint codes to determine`;
	} else {
		errorMessage = `No exterior colors found for the provided VIN`;
	}
	const vehicleError = raw.vehicleData?.error || false;

	// ChromeData signals a hard error only when both error===true AND validVin===false.
	// error===true alone can be informational (e.g. "No Match Found in request Locale,
	// returning alternate Locale result") where the VIN itself is still valid — those
	// must be allowed through.  Only reject when the VIN is explicitly invalid.
	if (vehicleError && dataResult.validVin?.toString() === "false") {
		return {
			validVin: false,
			validVehicleFound: false,
			year: "",
			make: "",
			model: "",
			submodel: "",
			engine: "",
			bodyType: "",
			decodedBodyTypes: [],
			ymm: "",
			colorCode: "",
			styleId: "",
			genericDesc: "",
			fullDescription: "",
			hex: "",
			site: "ChromeData",
			decodedByBumper: false,
			errorMessage:
				raw.vehicleData?.message || "ChromeData returned an error for this VIN",
			vehicleError: true,
		};
	}

	let validVin = dataResult.validVin?.toString() !== "false";
	if (year !== "" && make !== "" && model !== "") {
		validVehicleFound = true;
	}
	return {
		validVin: validVin || false,
		validVehicleFound,
		year: year || "",
		make: make || "",
		model: model || "",
		submodel: submodel || "",
		engine: engine || "",
		bodyType: bodyType || "",
		decodedBodyTypes,
		...(validVehicleFound && wmiCountry ? { wmiCountry } : {}),
		ymm: ymm,
		colorCode: colorCode,
		styleId: styleId,
		genericDesc: genericDesc || "",
		fullDescription: fullDescription || "",
		hex: hex || "",
		site: "ChromeData",
		decodedByBumper: false,
		errorMessage,
		vehicleError,
	};
}

// Toyota, Lexus, and Scion paint codes are always 3 digits but sometimes Bumper or Chromedata returns them as 4.
// e.g. Bumper === 0040, in reality it is 040.  So we take the last 3
function normalizeToyotaColorCode(make, code) {
	if (!code) return "";
	const m = make?.toLowerCase?.() || "";
	if (["toyota", "lexus", "scion"].includes(m)) {
		return code.slice(-3);
	}
	return code;
}

function normalizePaintCodeForMatch(value) {
	const raw = String(value || "")
		.split("+")[0]
		.trim();
	return raw.includes(" / ") ? raw.split(" / ").pop().trim() : raw;
}

function getPaintCodeMatchAliases(value) {
	const normalized = normalizePaintCodeForMatch(value).toLowerCase();
	if (!normalized) return [];
	const aliases = [normalized];
	if (normalized.indexOf("wa") === 0 && normalized.length > 2) {
		aliases.push(normalized.slice(2));
	} else {
		aliases.push("wa" + normalized);
	}
	return aliases.filter((alias, index) => aliases.indexOf(alias) === index);
}

function paintCodeMatchesValue(paintCode, value) {
	const targets = getPaintCodeMatchAliases(paintCode);
	const candidate = normalizePaintCodeForMatch(value).toLowerCase();
	const candidateAliases = getPaintCodeMatchAliases(candidate);
	return targets.some((target) =>
		candidateAliases.some(
			(alias) =>
				alias === target ||
				alias.startsWith(target + " ") ||
				alias.startsWith(target + "("),
		),
	);
}

function getVariantOptionPaintCode(option) {
	return normalizePaintCodeForMatch(
		option?.getAttribute("data-variant-title") ||
			option?.label ||
			option?.textContent ||
			"",
	);
}

function isSelectablePaintVariantOption(option) {
	return !!(
		option &&
		option.value &&
		!option.disabled &&
		option.dataset.unavailable !== "true"
	);
}

function selectorHasPaintVariantOptions(select) {
	return !!(
		select &&
		Array.from(select.options).some(
			(option) => option.value && getVariantOptionPaintCode(option),
		)
	);
}

function variantDataHasPaintOptions(groups) {
	return !!(
		groups &&
		typeof groups === "object" &&
		Object.keys(groups).some(
			(key) => Array.isArray(groups[key]) && groups[key].length,
		)
	);
}

function isSelectablePaintVariantData(variant) {
	return !!(
		variant &&
		variant.id &&
		variant.variantAvailable !== true &&
		variant.variantAvailable !== "true"
	);
}

function productHasSelectablePaintCode(paintCode) {
	if (!paintCode) return false;

	const variantSelector = document.getElementById("variant-selector");
	if (selectorHasPaintVariantOptions(variantSelector)) {
		return Array.from(variantSelector.options).some(
			(option) =>
				isSelectablePaintVariantOption(option) &&
				paintCodeMatchesValue(paintCode, getVariantOptionPaintCode(option)),
		);
	}

	const groups = window.productVariants || {};
	if (variantDataHasPaintOptions(groups)) {
		return Object.keys(groups).some((key) =>
			(groups[key] || []).some(
				(variant) =>
					isSelectablePaintVariantData(variant) &&
					paintCodeMatchesValue(
						paintCode,
						variant.variantTitle || variant.title || "",
					),
			),
		);
	}

	return true;
}

function referenceMatchesTroublesomeValue(reference, value) {
	const target = String(value || "")
		.trim()
		.toLowerCase();
	if (!target) return false;
	return String(reference || "")
		.split(",")
		.map((color) => color.trim().toLowerCase())
		.some((color) => color === target);
}

function findTroublesomePaintReference(
	keys,
	value,
	vehicle,
	includeRelatedReferences = false,
) {
	const matchingKeys = keys.filter((key) =>
		referenceMatchesTroublesomeValue(key, value),
	);
	if (!includeRelatedReferences || !getAuthoritativePaintCodesForVehicle(vehicle)) {
		return matchingKeys.find((key) =>
			productHasSelectablePaintCode(productPageTroublesomeReferences[key]),
		);
	}

	const relatedValues = matchingKeys.flatMap((key) =>
		String(key || "").split(","),
	);
	const candidateKeys = includeRelatedReferences
		? keys.filter((key) =>
				relatedValues.some((relatedValue) =>
					referenceMatchesTroublesomeValue(key, relatedValue),
				),
			)
		: matchingKeys;
	const validKeys = candidateKeys.filter((key) => {
		const mappedPaintCode = productPageTroublesomeReferences[key];
		return (
			productHasSelectablePaintCode(mappedPaintCode) &&
			isPaintCodeValidForVehicle(vehicle, mappedPaintCode) === true
		);
	});
	const validTargets = new Set(
		validKeys.map(
			(key) =>
				getVehiclePaintCodeAliases(
					productPageTroublesomeReferences[key],
				).sort()[0],
		),
	);
	return validTargets.size === 1 ? validKeys[0] : null;
}

async function normalizeModelAndCompare(decodedVehicleDataPayload) {
	let errorMessage = "";

	// Clear stale result before each new decode attempt
	window.EASYSEARCH_VALIDATION_RESULT = null;

	// Validate and normalize the entire vehicle data
	const validationResult = await validateAndNormalizeVehicleData(
		decodedVehicleDataPayload,
	);

	// Keep the decoded model as a broad fallback, but never restore unresolved trim
	// or engine data after native/EasySearch validation has rejected it.
	const normalizedModel =
		validationResult.model || decodedVehicleDataPayload.model;
	const normalizedSubmodel = validationResult.submodel || null;
	const normalizedEngine = validationResult.engine || null;

	// Store validation results globally for use in showMismatchModal
	window.EASYSEARCH_VALIDATION_RESULT = validationResult;

	decodedVehicleData = {
		year: decodedVehicleDataPayload.year,
		make: decodedVehicleDataPayload.make,
		model: normalizedModel,
		submodel: normalizedSubmodel,
		engine: normalizedEngine,
		bodyType: decodedVehicleDataPayload.bodyType || "",
		decodedBodyTypes: normalizeDecodedBodyTypeList(
			decodedVehicleDataPayload.decodedBodyTypes,
		),
		paintCode: decodedVehicleDataPayload.paintCode,
		site: decodedVehicleDataPayload.site,
		decodedByBumper: decodedVehicleDataPayload.decodedByBumper,
		description: decodedVehicleDataPayload.fullDescription,
		canResolveYMM: validationResult.canResolveYMM,
		canResolveComplete: validationResult.canResolveComplete,
	};

	// if (!window.OVERRIDE_FITMENT_CHECKER) {
	// 	// Compare with user input
	// 	mismatchDifferences = await compareWithDecoded(decodedVehicleData);
	// 	isMismatch = Object.keys(mismatchDifferences).length > 0;

	// 	if (isMismatch) {
	// 		errorMessage = `Vehicle data differences detected: ${formatDifferences(
	// 			mismatchDifferences
	// 		)}`;
	// 	}
	// }

	return {
		model: normalizedModel,
		submodel: normalizedSubmodel,
		engine: normalizedEngine,
		bodyType: decodedVehicleDataPayload.bodyType || "",
		decodedBodyTypes: normalizeDecodedBodyTypeList(
			decodedVehicleDataPayload.decodedBodyTypes,
		),
		errorMessage,
		validationResult,
	};
}

function handleTroublesomeMake(
	make,
	previousColorCode,
	description,
	previousDecodedByBumper,
	previousErrorMessage,
	vehicle,
) {
	let colorCode = previousColorCode;
	let isCurrentVehicleTroublesome = false;
	let decodedByBumper = previousDecodedByBumper;
	let errorMessage = previousErrorMessage;

	if (
		make?.toLowerCase().length &&
		troublesomeMakesGlobal.includes(make.toLowerCase())
	) {
		isCurrentVehicleTroublesome = true;

		// If colorCode was decoded successfully and the current make is troublesome, ensure that decodedByBumper is false because we do not record
		// the paint code in this case
		// if (colorCode?.length > 0) {
		// 	decodedByBumper = false;
		// }

		// Don't attempt to match by description if not on product page
		// Return early
		if (!window.location.pathname.includes("/products/")) {
			return {
				colorCode,
				isCurrentVehicleTroublesome,
				decodedByBumper,
				errorMessage,
				originalColorCode: previousColorCode,
			};
		}
		const keys = Object.keys(productPageTroublesomeReferences);

		// Prefer the exact decoded paint-code reference before the broader color
		// description. GM descriptions like "Summit White" can span multiple
		// product paint codes, while the product metafield can scope "GAZ" to the
		// correct selectable variant for this PDP/YMM.
		let matchedKey = findTroublesomePaintReference(keys, colorCode, vehicle);

		// Second attempt: to match by description if color code did not match
		if (!matchedKey && make.toLowerCase() !== "volkswagen") {
			matchedKey = findTroublesomePaintReference(
				keys,
				description,
				vehicle,
				true,
			);
		}

		// If still no match, then we have a problem and log the error
		if (!matchedKey) {
			errorMessage = `Troublesome paintcode found: ${colorCode}. ${description} does not match any description in the metadata fields.`;
		}

		colorCode = matchedKey
			? productPageTroublesomeReferences[matchedKey]
			: colorCode;
	}
	successfulVinDecoding = true;
	return {
		colorCode,
		isCurrentVehicleTroublesome,
		decodedByBumper,
		errorMessage,
		originalColorCode: previousColorCode,
	};
}

function handleInvalidVehicleAttempt(
	usedLicenseLookup,
	noResults,
	failed3times,
	buttons = {},
) {
	const { vinButton = null, licenseButton = null } = buttons;
	const errorMessageVIN = document.querySelector(".errorMessageVIN");
	const errorMessageLicense = document.querySelector(".errorMessageLicense");

	if (attemptedVinsInGarage.length < decodeMaxAttempts) {
		if (usedLicenseLookup) {
			errorMessageLicense.innerHTML = noResults;
			errorMessageLicense.style.visibility = "visible";
		} else {
			errorMessageVIN.innerHTML = noResults;
			errorMessageVIN.style.visibility = "visible";
		}
		garageVinSubmissionBool = false;
		return { success: true };
	} else {
		if (usedLicenseLookup) {
			errorMessageLicense.innerHTML = failed3times;
			errorMessageLicense.style.visibility = "visible";
			document.getElementById("license").disabled = true;
			if (licenseButton) licenseButton.disabled = true;
		} else {
			errorMessageVIN.innerHTML = failed3times;
			errorMessageVIN.style.visibility = "visible";
			document.getElementById("vin").disabled = true;
			if (vinButton) vinButton.disabled = true;
		}
		return { success: false };
	}
}

function handleDecoderFailure(payload) {
	// since we have two instance now, need to check which one is present, and then select the element
	const vinElements = getVisibleVinDecoderElements();
	let activeWrapper;
	if (vinElements) {
		activeWrapper = vinElements.wrapper;
	}
	const vin = payload.vin;
	const vinErrorMsgContainer = activeWrapper.querySelector(
		".vin-error-message-container",
	);
	if (vinErrorMsgContainer) vinErrorMsgContainer.style.display = "block";
	const submissionFailedMsgClass = activeWrapper.querySelector(
		payload.submissionFailedMsgClass,
	);
	const failedAttemptMsgClass = activeWrapper.querySelector(
		payload.failedAttemptMsgClass,
	);
	const remainingAttemptsMsgClass = activeWrapper.querySelector(
		payload.remainingAttemptsMsgClass,
	);
	const submissionFailedMsg = payload.submissionFailedMsg;
	const failedAttemptMsg = payload.failedAttemptMsg;
	const remainingAttempts = payload.remainingAttempts;
	const calculatedRemainingAttempts = payload.calculatedRemainingAttempts;

	submissionFailedMsgClass.style.display = "block";
	submissionFailedMsgClass.innerHTML = submissionFailedMsg;

	failedAttemptMsgClass.style.display = "block";
	failedAttemptMsgClass.innerHTML = failedAttemptMsg + vin;

	// Is the following if statement only for license decoder? or can it be used for both?
	if (
		failedAttemptMsgClass.style.visibility &&
		failedAttemptMsgClass.style.visibility === "hidden"
	) {
		failedAttemptMsgClass.style.visibility = "visible";
	}

	remainingAttemptsMsgClass.style.display = "block";
	remainingAttemptsMsgClass.innerHTML =
		remainingAttempts + (calculatedRemainingAttempts - 1);
}

// Only called at location 1 (Garage)
async function logGarageVehicleEvent(vin, state, plate, data = {}, error) {
	// if (!alreadyLogged) {
	// 	await logGarageEventToSheets("type", {
	// 		vin,
	// 		plate,
	// 		state,
	// 		year: data?.year || "",
	// 		make: data?.make || "",
	// 		model: data?.model || "",
	// 		submodel: data?.submodel || "",
	// 		engine: data?.engine || "",
	// 		error: error || "",
	// 		functionCall: "Garage VIN Submission",
	// 	});
	// } else {
	// 	alreadyLogged = false;
	// }
}

/*
    functionLocation - 1 = Garage
    functionLocation - 3 = Vin Decoder - Vin Lookup
    functionLocation - 4 = Vin Decoder - License Plate Lookup
*/
async function fetchVehicleDataByVin(
	state,
	plate,
	vin,
	functionLocation,
	noResults,
	failed3times,
	searchBtn,
	tailoredSuccessMessage,
	failedAttemptMsg,
	remainingAttempts,
	bumperdotcommake,
	requestToken,
) {
	// const remainingAttemptsMsgClass = document.querySelector(
	// 	'[class^="vin-decoder-remaining-attempts"]:not([style*="display: none"])'
	// );
	const vinSearchBtn = document.getElementById("vin-to-collection-btn");
	const licenseToVinbtn = document.getElementById("license-to-vin-btn");

	let calculatedRemainingAttemptsVinDecoder =
		decodeMaxAttempts - attemptedDecodedVins.length;
	let calculatedRemainingAttemptsLicenseDecoder =
		decodeMaxAttempts - attemptedDecodedLicensePlates.length;

	// COMMENTED OUT: Saves ~100ms per call
	// let partifyLocation = await getPartifyLocation();
	let partifyLocation = ""; // Skipping location lookup for performance
	let parentUrl = window.location.href;
	let data = "";
	let vehicleError = false;
	let validVin = false;
	let validVehicleFound = false;
	let chromedatamake = false;
	let decodedByBumper = false;
	let colorCode = "";
	let year = "";
	let make = "";
	let model = "";
	let submodel = "";
	let engine = "";
	let bodyType = "";
	let decodedBodyTypes = [];
	let ymm = "";
	let site = "";
	let styleId = "";
	let genericDesc = "";
	let fullDescription = "";
	let hex = "";
	let errorMessage = "";
	let lastCollectionImage = "";
	let fitmentYmm = "";
	let wmiCountry = "";

	try {
		// Check if vin is empty.. if so, then license lookup failed
		if (vin.length === 17) {
			// No matter what, always fetch from chromedata first to establish consistent vehicle data
			data = await fetchFromChromeData(vin, state, plate, functionLocation);
			if (!isCurrentPaintDecoderRequest(requestToken)) return { validVin: false };
			({
				validVin,
				validVehicleFound,
				year,
				make,
				model,
				submodel,
				engine,
				bodyType,
				decodedBodyTypes,
				wmiCountry,
				ymm,
				colorCode,
				styleId,
				genericDesc,
				fullDescription,
				hex,
				site,
				decodedByBumper,
				errorMessage,
				vehicleError,
			} = normalizeChromeData(data));
			if (window.shopCurrency === "CAD") {
				colorCode = "";
			}
			//debugger;
			// Must update the model so that it matches the easysearch model for mismatch comparison
			const decodedVehicleDataPayload = {
				year,
				make,
				model,
				submodel,
				engine,
				bodyType,
				decodedBodyTypes,
				paintCode: window.shopCurrency === "USD" ? colorCode : "",
				site,
				decodedByBumper,
				fullDescription,
			};
			const fullVinMapping =
				await resolveFullVinLookupMapping(decodedVehicleDataPayload);
			if (fullVinMapping && fullVinMapping.fitmentYmm) {
				fitmentYmm = fullVinMapping.fitmentYmm;
			}
			({
				model,
				submodel,
				engine,
				bodyType,
				decodedBodyTypes,
				errorMessage,
				validationResult,
			} = await normalizeModelAndCompare(decodedVehicleDataPayload));
			if (!isCurrentPaintDecoderRequest(requestToken)) return { validVin: false };
			// debugger;
			// if vehicle added from garage using vin, the bumperdotcommake would be false, need to flip to true here inorder to get the paintcode
			const isProductPage =
				window.location.pathname.includes("/products/") ||
				window.location.pathname.includes("/product/") ||
				window.location.pathname.includes("/shop/");
			if (isBumperDotcomMake(make)) {
				bumperdotcommake = true;
			}
			// If bumperdotcommake is true, and chromedata did not successfully decode the paintcode,
			// then we set the paintcode with bumper.com paintcode
			// if (
			// 	bumperdotcommake &&
			// 	bumperdotcommake === true &&
			// 	!colorCode.length &&
			// 	parseInt(year) >= 2012
			// ) {
			// 	try {
			// 		data = await fetchFromBumper(vin);
			// 		const returnedNormalizedBumperRes = normalizeBumperData(data);

			// 		// If colorCode was not established by chromedata, then we take the bumper colorCode
			// 		colorCode = returnedNormalizedBumperRes.colorCode;
			// 		if (!fullDescription) {
			// 			fullDescription = returnedNormalizedBumperRes.fullDescription;
			// 		}
			// 		// Update the decoded site to bumper.
			// 		site = returnedNormalizedBumperRes.site;
			// 		decodedByBumper = returnedNormalizedBumperRes.decodedByBumper;
			// 	} catch (error) {
			// 		// Bumper API failed (likely out of credits), continue without bumper data
			// 		console.warn("Bumper API call failed:", error.message);
			// 	}
			// }

			// If at this point we still do not have a colorCode, we set it to .VIN to indicate
			// that the paint code could not be decoded by either chromedata or bumper.com
			// and we will therefore always resort to match paint by vin
			if (!isVinDecodeExcludedMake(make) && window.shopCurrency === "USD") {
				if (bumperdotcommake && colorCode.length === 0) {
					colorCode = ".VIN";
					decodedByBumper = true;
				} else if (colorCode.length === 0) {
					colorCode = ".VIN";
				}
			}
			// Update the variables with normalized values
			if (validationResult && validationResult.model) {
				model = validationResult.model;
			}
			if (validationResult && validationResult.submodel) {
				submodel = validationResult.submodel;
			}
			if (validationResult && validationResult.engine) {
				engine = validationResult.engine;
			}
		} else {
			vehicleError = true;
			validVin = false;
		}
		const decodedVehicleForTroublesomePaint = {
			year,
			make,
			model,
			submodel,
			engine,
			fitmentYmm,
			ymm: fitmentYmm || [year, make, model].filter(Boolean).join(" "),
		};

		// Garage
		if (functionLocation === 1) {
			const usedLicenseLookup = Boolean(state && plate);
			// If current vehicle is troublesome, we don't want to set the paint code at the garage level
			let originalColorCode = colorCode;
			({
				colorCode,
				isCurrentVehicleTroublesome,
				decodedByBumper,
				errorMessage,
				originalColorCode,
			} = handleTroublesomeMake(
				make,
				colorCode,
				fullDescription,
				decodedByBumper,
				errorMessage,
				decodedVehicleForTroublesomePaint,
			));
			const decodedVehicleForPaintValidation = {
				...decodedVehicleForTroublesomePaint,
				bodyType,
				vin,
				state,
				plate,
				paintCode: colorCode,
				troublesome_format: isCurrentVehicleTroublesome
					? originalColorCode
					: null,
				troublesome_description: isCurrentVehicleTroublesome
					? fullDescription
					: null,
			};
			({ paintCode: colorCode, decodedByBumper } =
				await resolveFinalGarageVehiclePaint(
					decodedVehicleForPaintValidation,
					colorCode,
					decodedByBumper,
				));

			if (validVehicleFound) {
				// Don't add garageVinSubmissionBool here because there will be a time when the button
				// is clickable between submission and page navigation.  Don't want anyone spamming
				// the button.
				await logGarageVehicleEvent(vin, state, plate, decodedVehicleData, "");

				if (chromeDataMakesGlobal.includes(make.toLowerCase())) {
					chromedatamake = true;
				}

				const troublesomeFormatValue = isCurrentVehicleTroublesome
					? originalColorCode
					: null;
				const troublesomeDescriptionValue = isCurrentVehicleTroublesome
					? fullDescription
					: null;
				validVehicleFound = await updateGarageAndNavigate({
					year: year.toString(),
					make,
					model,
					submodel,
					engine,
					bodyType,
					decodedBodyTypes,
					vin,
					paintCode: colorCode,
					state,
					plate,
					decodedByBumper,
					lastCollectionImage,
					fitmentYmm,
					...(wmiCountry ? { wmiCountry } : {}),
					troublesome_format: troublesomeFormatValue,
					troublesome_description: troublesomeDescriptionValue,
				});
			}

			// validVehicleFound can change from true to false if updateGarageAndNavigate does not find a valid YMM in the easysearch dropdowns
			// Consequently, it must be treated as an invalid vehicle
			if (!validVehicleFound) {
				await logGarageVehicleEvent(
					vin,
					state,
					plate,
					decodedVehicleData,
					"Could not find matching YMM in EasySearch dropdowns.",
				);
				handleInvalidVehicleAttempt(usedLicenseLookup, noResults, failed3times);

				return {
					validVin: validVehicleFound,
					ymm: ymm,
					paintCode: colorCode || "",
					decodedByBumper: decodedByBumper,
				};
			}
			return {
				validVin: validVehicleFound,
				ymm: ymm,
				paintCode: colorCode || "",
				decodedByBumper: decodedByBumper,
			};
			// Vin Decoder
		} else if (functionLocation === 3) {
			if (attemptedDecodedVins.length <= decodeMaxAttempts) {
				let originalColorCode = colorCode;
				({
					colorCode,
					isCurrentVehicleTroublesome,
					decodedByBumper,
					errorMessage,
					originalColorCode,
				} = handleTroublesomeMake(
					make,
					colorCode,
					fullDescription,
					decodedByBumper,
					errorMessage,
					decodedVehicleForTroublesomePaint,
				));

				mismatchDifferences = await compareWithDecoded(decodedVehicleData);
				if (!isCurrentPaintDecoderRequest(requestToken)) return { validVin: false };

				isMismatch = Object.keys(mismatchDifferences).length > 0;
				if (isMismatch) {
					errorMessage = `Vehicle data differences detected: ${formatDifferences(
						mismatchDifferences,
					)}`;
				}

				if (vehicleError === false || validVin === true) {
					return {
						validVin: true,
						ymm: ymm,
						year: year || "",
						make: make || "",
						model: model || "",
						submodel: submodel || "",
						engine: engine || "",
						fitmentYmm,
						bodyType: bodyType || "",
						decodedBodyTypes,
						...(wmiCountry ? { wmiCountry } : {}),
						paintCode: colorCode || "",
						decodedByBumper: decodedByBumper,
						isCurrentVehicleTroublesome: isCurrentVehicleTroublesome,
						troublesome_format: isCurrentVehicleTroublesome
							? originalColorCode
							: null,
						troublesome_description: isCurrentVehicleTroublesome
							? fullDescription
							: null,
					};
				} else {
					if (errorMessage === "") {
						errorMessage = `VIN Decoder: Error fetching vehicle data.`;
					}
					const decoderFailurePayload = {
						vin,
						submissionFailedMsgClass: ".vin-decoder-submission-failed-message",
						failedAttemptMsgClass: ".vin-decoder-failed-attempt",
						remainingAttemptsMsgClass: ".vin-decoder-remaining-attempts",
						// remainingAttemptsMsgClass: remainingAttemptsMsgClass,
						submissionFailedMsg: noResults,
						failedAttemptMsg: failedAttemptMsg,
						remainingAttempts,
						calculatedRemainingAttempts: calculatedRemainingAttemptsVinDecoder,
					};
					//here
					handleDecoderFailure(decoderFailurePayload);

					return {
						validVin: false,
						ymm: "",
						paintCode: colorCode || "",
						decodedByBumper: null,
						isCurrentVehicleTroublesome: isCurrentVehicleTroublesome,
					};
				}
			} else {
				// Vin Decoder failed 3 times
				if (errorMessage === "") {
					errorMessage = `Failed 3 times. Error fetching vehicle data.`;
				}
				const decoderFailurePayload = {
					vin,
					submissionFailedMsgClass: ".vin-decoder-submission-failed-message",
					failedAttemptMsgClass: ".vin-decoder-failed-attempt",
					remainingAttemptsMsgClass: ".vin-decoder-remaining-attempts",
					// remainingAttemptsMsgClass: remainingAttemptsMsgClass,

					submissionFailedMsg: failed3times,
					failedAttemptMsg: failedAttemptMsg,
					remainingAttempts,
					calculatedRemainingAttempts: calculatedRemainingAttemptsVinDecoder,
				};
				handleDecoderFailure(decoderFailurePayload);

				successfulVinDecoding = false;
				return {
					validVin: false,
					ymm: "",
					paintCode: colorCode || "",
					decodedByBumper: null,
					isCurrentVehicleTroublesome: isCurrentVehicleTroublesome,
				};
			}
			// License Decoder
		} else if (functionLocation === 4) {
			if (attemptedDecodedLicensePlates.length <= decodeMaxAttempts) {
				let originalColorCode = colorCode;
				({
					colorCode,
					isCurrentVehicleTroublesome,
					decodedByBumper,
					errorMessage,
					originalColorCode,
				} = handleTroublesomeMake(
					make,
					colorCode,
					fullDescription,
					decodedByBumper,
					errorMessage,
					decodedVehicleForTroublesomePaint,
				));

				mismatchDifferences = await compareWithDecoded(decodedVehicleData);
				if (!isCurrentPaintDecoderRequest(requestToken)) return { validVin: false };
				isMismatch = Object.keys(mismatchDifferences).length > 0;
				if (isMismatch) {
					errorMessage = `Vehicle data differences detected: ${formatDifferences(
						mismatchDifferences,
					)}`;
				}

				if (vehicleError === false || validVin === true) {
					return {
						validVin: true,
						ymm: ymm,
						year: year || "",
						make: make || "",
						model: model || "",
						submodel: submodel || "",
						engine: engine || "",
						fitmentYmm,
						bodyType: bodyType || "",
						decodedBodyTypes,
						...(wmiCountry ? { wmiCountry } : {}),
						paintCode: colorCode || "",
						decodedByBumper: decodedByBumper,
						isCurrentVehicleTroublesome: isCurrentVehicleTroublesome,
						troublesome_format: isCurrentVehicleTroublesome
							? originalColorCode
							: null,
						troublesome_description: isCurrentVehicleTroublesome
							? fullDescription
							: null,
					};
				} else {
					if (errorMessage === "") {
						errorMessage = `License Decoder: Error fetching vehicle data.`;
					}
					const decoderFailurePayload = {
						vin,
						submissionFailedMsgClass:
							".license-decoder-submission-failed-message",
						failedAttemptMsgClass: ".license-decoder-failed-attempt",
						remainingAttemptsMsgClass: ".license-decoder-remaining-attempts",
						submissionFailedMsg: noResults,
						failedAttemptMsg: failedAttemptMsg,
						remainingAttempts,
						calculatedRemainingAttempts:
							calculatedRemainingAttemptsLicenseDecoder,
					};
					handleDecoderFailure(decoderFailurePayload);

					return {
						validVin: false,
						ymm: "",
						paintCode: colorCode || "",
						decodedByBumper: null,
					};
				}
			} else {
				if (errorMessage === "") {
					errorMessage = `Failed 3 times. Error fetching vehicle data.`;
				}

				const decoderFailurePayload = {
					vin,
					submissionFailedMsgClass:
						".license-decoder-submission-failed-message",
					failedAttemptMsgClass: ".license-decoder-failed-attempt",
					remainingAttemptsMsgClass: ".license-decoder-remaining-attempts",
					submissionFailedMsg: failed3times,
					failedAttemptMsg: failedAttemptMsg,
					remainingAttempts,
					calculatedRemainingAttempts:
						calculatedRemainingAttemptsLicenseDecoder,
				};
				handleDecoderFailure(decoderFailurePayload);

				successfulVinDecoding = false;
				return {
					validVin: false,
					ymm: "",
					paintCode: colorCode || "",
					decodedByBumper: null,
				};
			}
		}
	} catch (error) {
		if (!isCurrentPaintDecoderRequest(requestToken)) return { validVin: false };
		if (functionLocation === 1) {
			const usedLicenseLookup = Boolean(state && plate);
			handleInvalidVehicleAttempt(usedLicenseLookup, noResults, failed3times);
			if (usedLicenseLookup) {
				await logGarageVehicleEvent(vin, state, plate, {}, error.message);
			} else {
				await logGarageVehicleEvent(vin, "", "", {}, error.message);
			}
		} else if (functionLocation === 3) {
			// attemptedDecodedVins.push(vin);
			if (attemptedDecodedVins.length < decodeMaxAttempts) {
				if (errorMessage === "") {
					errorMessage = `[CATCH] Garage: Error fetching vehicle data: ${error.message}`;
				}
				const decoderFailurePayload = {
					vin,
					submissionFailedMsgClass: ".vin-decoder-submission-failed-message",
					failedAttemptMsgClass: ".vin-decoder-failed-attempt",
					remainingAttemptsMsgClass: ".vin-decoder-remaining-attempts",
					// remainingAttemptsMsgClass: remainingAttemptsMsgClass,

					submissionFailedMsg: noResults,
					failedAttemptMsg: failedAttemptMsg,
					remainingAttempts,
					calculatedRemainingAttempts: calculatedRemainingAttemptsVinDecoder,
				};
				handleDecoderFailure(decoderFailurePayload);
			} else {
				if (errorMessage === "") {
					errorMessage = `Failed 3 times. Error fetching vehicle data: ${error.message}`;
				}
				const decoderFailurePayload = {
					vin,
					submissionFailedMsgClass: ".vin-decoder-submission-failed-message",
					failedAttemptMsgClass: ".vin-decoder-failed-attempt",
					remainingAttemptsMsgClass: ".vin-decoder-remaining-attempts",
					// remainingAttemptsMsgClass: remainingAttemptsMsgClass,

					submissionFailedMsg: failed3times,
					failedAttemptMsg: failedAttemptMsg,
					remainingAttempts,
					calculatedRemainingAttempts: calculatedRemainingAttemptsVinDecoder,
				};
				handleDecoderFailure(decoderFailurePayload);
			}
			return {
				validVin: false,
				ymm: "",
				paintCode: colorCode || "",
				decodedByBumper: null,
			};
		} else if (functionLocation === 4) {
			const licenseDecoderFailedAttemptClass = document.querySelector(
				".license-decoder-failed-attempt",
			);
			const licenseDecoderSubmissionFailedMessageClass = document.querySelector(
				".license-decoder-submission-failed-message",
			);
			const licenseDecoderRemainingAttemptsClass = document.querySelector(
				".license-decoder-remaining-attempts",
			);
			if (attemptedDecodedLicensePlates.length <= decodeMaxAttempts) {
				if (errorMessage === "") {
					errorMessage = `[CATCH] License Decoder: Error fetching vehicle data: ${error.message}`;
				}
				const decoderFailurePayload = {
					vin,
					submissionFailedMsgClass:
						".license-decoder-submission-failed-message",
					failedAttemptMsgClass: ".license-decoder-failed-attempt",
					remainingAttemptsMsgClass: ".license-decoder-remaining-attempts",
					submissionFailedMsg: noResults,
					failedAttemptMsg: failedAttemptMsg,
					remainingAttempts,
					calculatedRemainingAttempts:
						calculatedRemainingAttemptsLicenseDecoder,
				};
				handleDecoderFailure(decoderFailurePayload);
			} else {
				if (errorMessage === "") {
					errorMessage = `Failed 3 times. Error fetching vehicle data: ${error.message}`;
				}
				const decoderFailurePayload = {
					vin,
					submissionFailedMsgClass:
						".license-decoder-submission-failed-message",
					failedAttemptMsgClass: ".license-decoder-failed-attempt",
					remainingAttemptsMsgClass: ".license-decoder-remaining-attempts",
					submissionFailedMsg: noResults,
					failedAttemptMsg: failedAttemptMsg,
					remainingAttempts,
					calculatedRemainingAttempts:
						calculatedRemainingAttemptsLicenseDecoder,
				};
				handleDecoderFailure(decoderFailurePayload);
			}
			return {
				validVin: false,
				ymm: "",
				paintCode: colorCode || "",
				decodedByBumper: null,
			};
		}
		console.error("Error fetching vehicle data:", error);
	} finally {
		if (functionLocation === 1) {
			vinSearchBtn.classList.remove("vin-to-collection-btn--loading");
			vinSearchBtn.innerHTML = searchBtn;
		}

		if (functionLocation === 3 || functionLocation === 4) {
			decoderLoggerWebhook(
				state,
				plate,
				vin,
				site,
				year,
				make,
				model,
				submodel,
				engine,
				styleId,
				colorCode,
				errorMessage,
				partifyLocation,
				parentUrl,
				genericDesc,
				fullDescription,
				hex,
			);
		}
	}
}

function cleanLicensePlate(state, dirtyPlate, oRestrictedStates) {
	// Remove spaces and trim the plate input
	let plate = dirtyPlate ? dirtyPlate.replace(/[\s-]+/g, "").trim() : "";
	// Only modify the plate if the state is in the list of states that restrict the letter O
	if (oRestrictedStates.includes(state)) {
		plate = plate.replace(/O/g, "0");
	}
	return plate;
}

// function to compare stored data with decodedData
async function compareWithDecoded(decodedData) {
	const userInput = getSearchTerms()[0] || {};
	const easyModel = decodedData?.model;
	if (easyModel) decodedData.model = easyModel;
	const differences = {};

	const toComparable = (key, value) => {
		if (value == null) return "";
		const s = String(value).toLowerCase().trim();
		if (key === "year") return s.replace(/\D/g, "");
		return s.replace(/[^a-z0-9]/g, "");
	};

	// REMOVE THE SUBMODEL | ENGINE FROM THE KEYSTOCOMPARE< WE DONT NEED THEM HERE
	// TODO: NEED TO FILP THEM BACK ONCE FINISHING THE REFERENCING
	// const keysToCompare = ["year", "make", "model", "submodel", "engine"];
	const keysToCompare = ["year", "make", "model"];
	for (const key of keysToCompare) {
		const userComparable = toComparable(key, userInput[key]);
		const decodedComparable = toComparable(key, decodedData[key]);
		if (
			userComparable &&
			decodedComparable &&
			userComparable !== decodedComparable
		) {
			differences[key] = {
				user: userInput[key],
				decoded: decodedData[key],
			};
		}
	}

	// keep for debug
	// if (Object.keys(differences).length > 0) {
	// 	console.log("Vehicle data differences detected:", differences); // Keep for debug
	// }

	return differences;
}

// New helper function (add this anywhere in the utilities section)

function formatDifferences(differences) {
	return Object.entries(differences)

		.map(
			([key, { user, decoded }]) =>
				`${
					key.charAt(0).toUpperCase() + key.slice(1)
				} (User: ${user}, Decoded: ${decoded})`,
		)

		.join(", ");
}

function formatPriceToUSDGlobal(price) {
	if (!price) return;

	if (price.trim().startsWith("$")) {
		return price;
	}
	const formattedPrice = (parseFloat(price) / 100).toFixed(2);
	return "$" + formattedPrice;
}

// Uncomment the following function whenever we get more bumper credits
async function fetchBumpersLicenseToVin(state, plate) {
	// Detect product page paint decoder context
	const isProductPaintDecoder = Boolean(
		document.querySelector(
			".vin-decoder-license-to-vin, .license-to-vin-decoder-wrapper",
		),
	);
	let vin = "";
	let year = "";
	let make = "";
	let model = "";
	let submodel = "";
	let engine = "";

	try {
		alreadyLogged = false;
		const params = new URLSearchParams({
			state: String(state || "").trim(),
			plate: String(plate || "").trim(),
		});
		const response = await fetch(
			`https://license-to-vin-273472976974.us-east5.run.app/license-to-vin?${params.toString()}`,
			{
				method: "GET",
				headers: { "Content-Type": "application/json" },
			},
		);

		if (response.ok === false) {
			if (response.status === 401 && isProductPaintDecoder) {
				throw new Error("AUTHORIZATION_ERROR");
			}
			throw new Error(`HTTP_${response.status}`);
		}
		const data = await response.json();
		vin = data.entities?.vehicles?.automobiles[0]?.vin || "";
		if (!vin) {
			throw new Error("No VIN found for the provided license plate.");
		}
		year = data.entities?.vehicles?.automobiles[0]?.year || "";
		make = data.entities?.vehicles?.automobiles[0]?.make || "";
		model = data.entities?.vehicles?.automobiles[0]?.model || "";
		submodel = data.entities.vehicles.automobiles[0]?.design?.trim || "";
		const engineSpecs = data.entities.vehicles.automobiles[0]?.specs?.engine;
		const cylinders = engineSpecs?.cylinders;
		const size = engineSpecs?.size?.toFixed(1);
		if (cylinders && size) {
			engine = `${cylinders} Cyl ${size}L`;
		} else {
			engine = "";
		}
		if (data.entities?.vehicles?.automobiles.length > 0) {
			// Fire-and-forget: log in background
			logGarageEventToSheets("type", {
				vin: vin,
				plate: plate,
				state: state,
				year: year,
				make: make,
				model: model,
				submodel: submodel,
				engine: engine,
				error: "",
				functionCall: "License To VIN",
			});

			alreadyLogged = true;
		}

		return { vin, year, make, model };
	} catch (error) {
		// Handle product page paint decoder errors separately
		if (isProductPaintDecoder) {
			const msgEl = document.querySelector(
				".license-decoder-submission-failed-message",
			);
			const errText = String((error && error.message) || "");
			const isAuth = errText.includes("AUTHORIZATION_ERROR");

			const msg = isAuth
				? window.__ppLicenseServiceMsg || window.__ppLicenseNoResultsMsg || ""
				: window.__ppLicenseNoResultsMsg || "";

			if (msgEl) {
				msgEl.innerHTML = msg;
				msgEl.style.display = "block";
			}
			return;
		}

		// Fire-and-forget: log in background
		logGarageEventToSheets("type", {
			vin: vin,
			plate: plate,
			state: state,
			year: "",
			make: "",
			model: "",
			submodel: "",
			engine: "",
			error: error.message,
			functionCall: "License To VIN",
		});
		console.error("Error fetching vehicle data:", error);
		throw new Error(
			"We couldn't match that plate. Try VIN instead, or double-check the state and plate.",
			{ cause: error },
		);
	}
}

async function handleVinDecode(
	vinInput,
	state,
	licensePlate,
	location,
	bumperdotcommake,
	failed3timesmsg,
	noResults,
	searchBtn,
	tailoredSuccessMessage,
	failedAttemptMsg,
	remainingAttempts,
	vinDecoderFirstMsg,
	vinDecoderEndMsg,
	forceSelectMsgStart,
	forceSelectMsgEnd,
	requestToken,
) {
	if (location === 3 || location === 4) {
		directPdpVehicleSelection = null;
		pendingMismatchDecodedVehicle = null;
	}
	if (location === 1) {
		window.igEvents = window.igEvents || [];
		window.igEvents.push({ event: "vinEntryAtGarage" });
	} else if (location === 3 || location === 4) {
		window.igEvents = window.igEvents || [];
		window.igEvents.push({ event: "vinEntryAtVinDecoder" });
	}

	let exteriorColor = "";

	// Returns isVinValid and Paint code (if no paint code, returns empty string)
	let {
		validVin,
		ymm,
		year: decodedYear,
		make: decodedMake,
		model: decodedModel,
		submodel: decodedSubmodel,
		engine: decodedEngine,
		fitmentYmm,
		bodyType,
		decodedBodyTypes,
		wmiCountry: decodedWmiCountry,
		paintCode,
		decodedByBumper,
		isCurrentVehicleTroublesome,
		troublesome_format: returnedTroublesomeFormat,
		troublesome_description: returnedTroublesomeDescription,
	} = await fetchVehicleDataByVin(
		state,
		licensePlate,
		vinInput,
		location,
		noResults,
		failed3timesmsg,
		searchBtn,
		tailoredSuccessMessage,
		failedAttemptMsg,
		remainingAttempts,
		bumperdotcommake,
		requestToken,
	);
	if (!isCurrentPaintDecoderRequest(requestToken)) return false;
	if (validVin) {
		let directPdpVehicleActivated = false;
		const decodedVehicleForPaintValidation = {
			year: decodedYear,
			make: decodedMake,
			model: decodedModel,
			submodel: decodedSubmodel,
			engine: decodedEngine,
			fitmentYmm,
			bodyType,
			decodedBodyTypes,
			ymm: fitmentYmm || ymm,
			vin: vinInput,
			plate: licensePlate,
			state,
			paintCode,
			decodedByBumper,
			wmiCountry: decodedWmiCountry,
			isCurrentVehicleTroublesome,
			troublesome_format: returnedTroublesomeFormat || null,
			troublesome_description: returnedTroublesomeDescription || null,
		};
		({ paintCode, decodedByBumper } = await resolveFinalGarageVehiclePaint(
			decodedVehicleForPaintValidation,
			paintCode,
			decodedByBumper,
		));
		if (!isCurrentPaintDecoderRequest(requestToken)) return false;
		decodedVehicleForPaintValidation.paintCode = paintCode;
		decodedVehicleForPaintValidation.decodedByBumper = decodedByBumper;
		if (
			isMismatch &&
			window.shopCurrency === "USD" &&
			hasPdpMatchPaintByVinCapability() &&
			(location === 3 || location === 4)
		) {
			pendingMismatchDecodedVehicle = {
				vehicle: { ...decodedVehicleForPaintValidation },
				options: {
					vin: vinInput,
					state,
					plate: licensePlate,
					requestToken,
				},
			};
		}

		if (
			!isMismatch &&
			window.shopCurrency === "USD" &&
			(location === 3 || location === 4)
		) {
			const previousTerms = readStoredGarageTerms();
			const activeVehicle = await validateAndActivateDecodedVehicleInGarage(
				decodedVehicleForPaintValidation,
				{
					vin: vinInput,
					state,
					plate: licensePlate,
					requestToken,
				},
			);
			if (!isCurrentPaintDecoderRequest(requestToken)) return false;
			if (activeVehicle) {
				const currentTerms = readStoredGarageTerms();
				recordRemovedGarageVehicleIdentities(previousTerms, currentTerms);
				directPdpVehicleSelection = {
					key: getCanonicalGarageVehicleKey(activeVehicle),
				};
				directPdpVehicleActivated = true;
				window.PartifyPdpVinState?.complete?.();
			}
		}
		// On CA store, skip all paint selection logic — just store the VIN
		if (window.shopCurrency !== "USD" || twoToneSelected === true) {
			if (!isMismatch) {
				const searchTerms = saveVinToFirstSearchTerm(
					vinInput,
					state,
					licensePlate,
					5,
					decodedWmiCountry,
					{ bodyType, decodedBodyTypes },
				);
				window.GarageLogic?.updateGaragePopup(searchTerms);
			}
			window.__CA_VIN_SUBMIT = true;
			return validVin;
		}
		// Excluded makes (e.g. Tesla, Suzuki): VIN decoding is unsupported — store the VIN
		// but skip handleAutoSelectColor entirely so the user's selected paint code is preserved.
		const decodedMakeForExclude = String(decodedMake || "").trim();
		if (
			isVinDecodeExcludedMake(decodedMakeForExclude) ||
			(!decodedMakeForExclude && isCurrentProductVinDecodeExcludedMake())
		) {
			if (!isMismatch) {
				const searchTerms = saveVinToFirstSearchTerm(
					vinInput,
					state,
					licensePlate,
					5,
					decodedWmiCountry,
					{ bodyType, decodedBodyTypes },
				);
				window.GarageLogic?.updateGaragePopup(searchTerms);
			}
			return validVin;
		}
		// Vin is valid and paint code is found
		const { matchingOption, noPaintVariants } = handleAutoSelectColor(
			paintCode,
			vinInput,
			licensePlate,
			location,
			vinDecoderFirstMsg,
			vinDecoderEndMsg,
			forceSelectMsgStart,
			forceSelectMsgEnd,
		);
		// Break early if no paint variants -- meaning we do not know all of the paint variants to select
		// from in the event of a troublesome make--this is determined after the quality is selected
		if (noPaintVariants) {
			// VIN must be saved before returning so setupVinSync can attach it at ATC time.
			// Without this, submitting a license plate before selecting quality leaves searchTerms[0].vin
			// empty, causing the VIN to be missing from cart properties (Control/Hybrid variants).
			if (!isMismatch) {
				saveVinToFirstSearchTerm(
					vinInput,
					state,
					licensePlate,
					5,
					decodedWmiCountry,
					{ bodyType, decodedBodyTypes },
				);
			}
			return validVin;
		}
		let searchTerms = getSearchTerms();
		if (!isMismatch) {
			if (!directPdpVehicleActivated) {
				if (!Array.isArray(searchTerms)) searchTerms = [];
				if (!searchTerms[0]) searchTerms[0] = { ...VEHICLE_SCHEMA, ymm: "" };
				setVehicleVinCountry(searchTerms[0], vinInput, decodedWmiCountry);
				if (state) searchTerms[0].state = state;
				if (licensePlate) searchTerms[0].plate = licensePlate;
				applyDecodedBodyTypesToVehicle(searchTerms[0], {
					bodyType,
					decodedBodyTypes,
				});
			// to avoid attaching paintcode for troublesome makes
			if (
				matchingOption ||
				!searchTerms[0].paintCode ||
				isCurrentVehicleTroublesome
			) {
				searchTerms[0].paintCode = paintCode;
			}

			// Need this because may decode the paint code but did not find matching option
			// In here, will set the decodedByBumper......
			if (paintCode !== "") {
				if (Array.isArray(searchTerms) && searchTerms.length > 0) {
					const firstYMM = searchTerms[0].ymm || "";
					if (firstYMM === ymm) {
						searchTerms[0].paintCode = paintCode;
					}
				}
			}

			// If the paint code for the first vehicle (searchTerm) is the same as the paintCode returned from decoding,
			// we can safely set the decodedByBumper value
			if (searchTerms[0].paintCode === paintCode) {
				searchTerms[0].decodedByBumper = decodedByBumper;
			}

			// Store troublesome format/description from the decode
			if (returnedTroublesomeFormat && !searchTerms[0].troublesome_format) {
				searchTerms[0].troublesome_format = returnedTroublesomeFormat;
			}
			if (
				returnedTroublesomeDescription &&
				!searchTerms[0].troublesome_description
			) {
				searchTerms[0].troublesome_description = returnedTroublesomeDescription;
			}

				setSearchTerms(searchTerms, 5);
				window.GarageLogic.updateGaragePopup(getSearchTerms());
			}

			if (matchingOption) {
				// Find the price for the decoded paint code from the variant selector
				const variantSelectorForPrice =
					document.getElementById("variant-selector");
				if (variantSelectorForPrice) {
					const matchedVariantOpt = Array.from(
						variantSelectorForPrice.options,
					).find((opt) => opt.label.split("+")[0].trim() === paintCode);
					if (matchedVariantOpt) {
						const priceDiff =
							parseFloat(matchedVariantOpt.dataset.priceDifference || 0) / 100;
						decodedPaintedOptionPrice = priceDiff.toLocaleString("en-US", {
							style: "currency",
							currency: "USD",
						});
					}
				}
				// Show the stored paint option label and populate its text
				if (storedOptionLabel) storedOptionLabel.style.display = "block";
				if (failSafeSelectCodeOptionLabel)
					failSafeSelectCodeOptionLabel.style.display = "block";
				if (selectCodeOptionLabel) selectCodeOptionLabel.style.display = "none";
				if (checkboxStoredCodeText) {
					checkboxStoredCodeText.innerHTML =
						decodedPaintedOptionPainted + " " + paintCode;
				}
				if (storedOptionLabel) storedOptionLabel.classList.remove("match-paint-by-vin-option");
				// Show the decoded VIN/license message
				if (state && licensePlate) {
					displayStoredPaintCodeMsg(
						false,
						state + "-" + licensePlate,
						paintCode,
					);
				} else if (vinInput) {
					displayStoredPaintCodeMsg(true, vinInput, paintCode);
				}
				// Hide the "give us your VIN" prompt and the VIN/license radio group
				if (paintOptionGroup) paintOptionGroup.style.display = "none";
				hideGiveUsYourVinMsg();
				// Mark the stored code radio as selected (no change event — variant already set)
				const checkboxUseStoredCode = document.getElementById(
					"checkbox-paint-code-local-storage",
				);
				if (checkboxUseStoredCode) checkboxUseStoredCode.checked = true;
			}
			if (directPdpVehicleActivated) {
				window.PartifyPdpVinState?.complete?.({
					requestToken,
					restore: true,
				});
			}
		}
	} else if (
		!validVin &&
		paintCode === ".VIN" &&
		(location === 3 || location === 4)
	) {
		// Handle case when VIN decode failed but we still have .VIN as paintCode
		// Call handleAutoSelectColor to trigger the Match Paint by VIN flow
		handleAutoSelectColor(
			paintCode,
			vinInput,
			licensePlate,
			location,
			vinDecoderFirstMsg,
			vinDecoderEndMsg,
			forceSelectMsgStart,
			forceSelectMsgEnd,
		);

		let searchTerms = getSearchTerms();
		if (!isMismatch) {
			searchTerms = saveVinToFirstSearchTerm(
				vinInput,
				state,
				licensePlate,
				5,
				{ bodyType, decodedBodyTypes },
			);
			searchTerms[0].paintCode = paintCode;
			setSearchTerms(searchTerms, 5);
			window.GarageLogic.updateGaragePopup(getSearchTerms());
		}
	}

	return validVin;
}

/**
 * Handles the scenario when vehicles fail to decode paint codes.
 * If the global toggle for matching paint by VIN is enabled, it attempts to append
 * the match by VIN price and updates the UI accordingly.
 *
 * If no match by VIN price is found, it resorts to forcing the user to select a paint code
 */
function handleVehiclesFailedToDecodePaint(
	vinInput,
	licensePlate,
	forceSelectMsgStart,
	forceSelectMsgEnd,
	location,
	autoSelectColor,
) {
	if (window.TOGGLE_MATCH_PAINT_BY_VIN) {
		const matchPaintByVinPrice = handleAppendMatchByVin(
			vinInput,
			autoSelectColor,
		);
		const matchPaintByVinOption = getMatchByVinVariantOption();

		// In some cases (let's say there is no hidden div because the part does not have the
		// match by vin option), the matchPaintByVinPrice will be null or empty string
		// Therefore we must force them to select a code
		if (!matchPaintByVinPrice && !matchPaintByVinOption) {
			// If empty, jump to else logic
			resortToForceSelectCode();
			formulateForcePaintCodeSelectMsg(
				vinInput,
				licensePlate,
				forceSelectMsgStart,
				forceSelectMsgEnd,
				location,
			);
		} else {
			matchingPaintByVinVisible = true;
			if (storedOptionLabel) storedOptionLabel.style.display = "block";
			if (failSafeSelectCodeOptionLabel)
				failSafeSelectCodeOptionLabel.style.display = "block";
			if (selectCodeOptionLabel) selectCodeOptionLabel.style.display = "none";
			if (checkboxStoredCodeText)
				checkboxStoredCodeText.innerHTML = matchPaintByVinTranslationKey;
			if (storedOptionLabel) storedOptionLabel.classList.add("match-paint-by-vin-option");

			if (autoSelectColor) {
				const checkboxUseStoredCode = document.getElementById(
					"checkbox-paint-code-local-storage",
				);
				if (checkboxUseStoredCode) checkboxUseStoredCode.checked = true;
				const paintCodeWrapperGlobal = document.querySelector(
					".paint-code-wrapper",
				);
				if (paintCodeWrapperGlobal?.classList.contains("show")) {
					paintCodeWrapperGlobal.classList.remove("show");
				}
			}
		}
	} else {
		resortToForceSelectCode();
		formulateForcePaintCodeSelectMsg(
			vinInput,
			licensePlate,
			forceSelectMsgStart,
			forceSelectMsgEnd,
			location,
		);
	}
}

function persistVehiclePaintCodeAsMatchByVin(vehicle) {
	if (!vehicle) return;
	const searchTerms = getSearchTerms();
	if (!Array.isArray(searchTerms) || !searchTerms.length) return;
	searchTerms[0].paintCode = ".VIN";
	if (vehicle.vin && !searchTerms[0].vin) searchTerms[0].vin = vehicle.vin;
	if (vehicle.plate && !searchTerms[0].plate) searchTerms[0].plate = vehicle.plate;
	if (vehicle.state && !searchTerms[0].state) searchTerms[0].state = vehicle.state;
	setSearchTerms(searchTerms, 5);
	if (window.GarageLogic && window.GarageLogic.updateGaragePopup) {
		window.GarageLogic.updateGaragePopup(getSearchTerms());
	}
}

function selectStoredMatchByVinFallback(
	vehicle,
	persistFallback,
	attempt = 0,
	requestToken = null,
	requestSnapshot = null,
	selectionKeys = null,
) {
	const request = window.PartifyPaintDecoderRequest;
	const capturedSnapshot = requestSnapshot || request.snapshot();
	const capturedSelectionKeys =
		selectionKeys ||
		[
			getStoredPaintSelectionKey(vehicle, vehicle?.paintCode),
			getStoredPaintSelectionKey(vehicle, ".VIN"),
		];
	const isStillCurrent = function () {
		if (!canCurrentPaintOwnerWriteStoredPaint(requestToken)) return false;
		if (requestToken == null && !request.matches(capturedSnapshot)) return false;
		const currentVehicle = getSearchTerms()?.[0];
		return capturedSelectionKeys.includes(
			getStoredPaintSelectionKey(currentVehicle, currentVehicle?.paintCode),
		);
	};
	if (!isStillCurrent()) return false;
	const vin = String(vehicle?.vin || "").trim();
	const plate = String(vehicle?.plate || vehicle?.licensePlate || "").trim();
	handleVehiclesFailedToDecodePaint(vin, plate, "", "", plate ? 4 : 3, true);
	showPdpVinChangeControl(document.querySelector(".vin-selected-message"));
	handleAutoSelectMatchByVin();

	const matchPaintByVinOption = getMatchByVinVariantOption();
	if (!matchPaintByVinOption) {
		if (attempt < 5) {
			setTimeout(function () {
				selectStoredMatchByVinFallback(
					vehicle,
					persistFallback,
					attempt + 1,
					requestToken,
					capturedSnapshot,
					capturedSelectionKeys,
				);
			}, 100);
			return false;
		}
		resetStoredPaintToNoVinState();
		if (paintOptionGroup) paintOptionGroup.style.display = "block";
		return false;
	}

	storedDecodedVin = vin;
	matchingPaintByVinVisible = true;
	if (storedOptionLabel) storedOptionLabel.style.display = "block";
	if (failSafeSelectCodeOptionLabel)
		failSafeSelectCodeOptionLabel.style.display = "block";
	if (selectCodeOptionLabel) selectCodeOptionLabel.style.display = "none";
	if (checkboxStoredCodeText)
		checkboxStoredCodeText.innerHTML = matchPaintByVinTranslationKey;
	if (storedOptionLabel) storedOptionLabel.classList.add("match-paint-by-vin-option");
	if (paintOptionGroup) paintOptionGroup.style.display = "none";
	hideGiveUsYourVinMsg();

	const storedRadio = document.getElementById(
		"checkbox-paint-code-local-storage",
	);
	if (storedRadio) {
		const syncStoredMatchByVinSelection = function () {
			if (!isStillCurrent()) return;
			storedRadio.checked = true;
			handleAutoSelectMatchByVin();
			selectStoredPaintVariantOption();
			storedRadio.dispatchEvent(new Event("change", { bubbles: true }));
		};
		syncStoredMatchByVinSelection();
		setTimeout(syncStoredMatchByVinSelection, 0);
		setTimeout(syncStoredMatchByVinSelection, 250);
	}

	if (persistFallback && isStillCurrent()) persistVehiclePaintCodeAsMatchByVin(vehicle);
	return true;
}

function handleAutoSelectColor(
	paintCode,
	vinInput,
	licensePlate,
	location,
	decoderFirstMsg,
	decoderEndMsg,
	forceSelectMsgStart,
	forceSelectMsgEnd,
) {
	const paintCodeWrapper = document.querySelector(".paint-code-wrapper");
	const paintcodeAppContainer = document.getElementById(
		"paintcode-app-container",
	);
	const variantSelector = document.getElementById("variant-selector");
	const paintedStockKeyLevel = document.querySelector(
		".painted-stock-key-paint-level",
	);
	const paintOptionsVinLicense = document.querySelector(
		".paint-options-vin-license",
	);
	let matchingOption = false;

	// Trigger modal for any mismatch on product page
	const isProductPage =
		window.location.pathname.includes("/products/") ||
		window.location.pathname.includes("/product/") ||
		window.location.pathname.includes("/shop/");

	if (isMismatch && isProductPage) {
		showMismatchModal(mismatchDifferences, vinInput, licensePlate, paintCode);
		return false; // stop to show modal
	}

	if (variantSelector) {
		const options = Array.from(variantSelector.options);

		// If on product page and a quality hasn't been selected yet, then there will only be one option -- 'Select Paint Code'
		// If the paintCode is not .VIN, then we can assume it was either successfully decoded or has yet to be decoded
		if (options.length === 1) return { matchingOption, noPaintVariants: true };

		// --- Banned paint code interception ---
		const bannedEntry = BANNED_PAINT_CODES.find(
			(b) => b.code.toLowerCase() === paintCode.trim().toLowerCase(),
		);
		if (bannedEntry) {
			console.log(
				`[BannedPaint] Blocked code: ${bannedEntry.code}, showing banner`,
			);

			// Click "Select Paint Code" radio to reveal the dropdown
			const paintRadio = document.getElementById(
				"checkbox-select-paint-option",
			);
			if (paintRadio && !paintRadio.checked) {
				paintRadio.checked = true;
				paintRadio.dispatchEvent(new Event("change", { bubbles: true }));
			}

			const wrapper = document.querySelector(".paint-code-wrapper");
			if (wrapper) {
				const existing = wrapper.parentElement.querySelector(
					".partify-banned-paint-banner",
				);
				if (existing) existing.remove();

				// Determine banner message before options populate
				let bannerMsg = "";
				if (bannedEntry.alternativeCode) {
					bannerMsg = `We're unable to paint ${bannedEntry.label} (${bannedEntry.code}). We've automatically selected ${bannedEntry.alternativeLabel} (${bannedEntry.alternativeCode}) — visible in the dropdown below.`;
				} else {
					bannerMsg = `We're unable to paint ${bannedEntry.label} (${bannedEntry.code}). Please select a different paint code from the dropdown below.`;
				}

				const banner = document.createElement("div");
				banner.className = "partify-banned-paint-banner";
				banner.setAttribute("role", "alert");
				banner.style.cssText =
					"background:#fff3cd;border:1px solid #ffc107;color:#664d03;padding:10px 14px;border-radius:6px;margin-bottom:10px;font-size:14px;line-height:1.4;";
				banner.textContent = bannerMsg;
				wrapper.parentElement.insertBefore(banner, wrapper);

				// Wait for dropdown to populate after radio change, then select SG2
				setTimeout(() => {
					if (bannedEntry.alternativeCode) {
						const freshOptions = Array.from(variantSelector.options);
						let altOption = null;
						freshOptions.forEach((opt) => {
							console.log("[BannedPaint] option text:", opt.text);
							if (
								opt.text
									.toUpperCase()
									.includes(bannedEntry.alternativeCode.toUpperCase())
							) {
								altOption = opt;
							}
						});

						if (altOption) {
							altOption.selected = true;
							variantSelector.value = altOption.value;
							const altEvent = new Event("change", { bubbles: true });
							altEvent._fromAutoSelect = true;
							variantSelector.dispatchEvent(altEvent);
							console.log(
								"[BannedPaint] Set dropdown value to:",
								variantSelector.value,
							);
							console.log("[BannedPaint] SG2 found and auto-selected");
						} else {
							// SG2 not in dropdown — update banner to manual-select message
							banner.textContent = `We're unable to paint ${bannedEntry.label} (${bannedEntry.code}). Please select a different paint code from the dropdown below.`;
							console.log(
								"[BannedPaint] SG2 not found in dropdown, customer must pick manually",
							);
						}
					}
				}, 300);

				// Dismiss banner only on manual customer selection of a valid (non-banned) code
				const dismissHandler = (e) => {
					if (e._fromAutoSelect) return;
					const selectedLabel =
						variantSelector.options[variantSelector.selectedIndex]?.label || "";
					const selectedCode = selectedLabel.split("+")[0].trim().toLowerCase();
					const stillBanned = BANNED_PAINT_CODES.some(
						(b) => b.code.toLowerCase() === selectedCode,
					);
					if (!stillBanned && selectedCode) {
						const bannerEl = wrapper.parentElement.querySelector(
							".partify-banned-paint-banner",
						);
						if (bannerEl) bannerEl.remove();
						variantSelector.removeEventListener("change", dismissHandler);
					}
				};
				variantSelector.addEventListener("change", dismissHandler);
			}
			return { matchingOption: false, noPaintVariants: false };
		}
		// --- End banned paint code interception ---

		options.find((option) => {
			const codeInFrontOfPrice = option.label.split("+")[0];

			// In some instances, we have the same paint code but the color itself varies between years:
			// e.g. PBM (2006-2013) +$153.00 and PBM (2018-2023) +$153.00
			// if (codeInFrontOfPrice.includes("(")) {
			if (
				codeInFrontOfPrice.trim() === paintCode &&
				isSelectablePaintVariantOption(option)
			) {
				matchingOption = true;
				if (option.textContent.includes("🚚")) {
					decodedVINHasAvailableStock = true;
				}
				autoSelectedColor = option.value;
				autoSelectedOption = option;
				return { matchingOption, noPaintVariants: false };
			}
		});

		if (matchingOption) {
			storedDecodedVin = vinInput;

			// Set the selectedVariantUnavailable to false to enable the addToCart button
			// We can do this because we know we already have the VIN
			selectedVariantUnavailable = false;
			if (autoSelectedOption.dataset.unavailable === "true") {
				autoSelectedBanned = true;
			}

			variantSelector.value = autoSelectedColor;
			document.body.classList.remove("no-scroll-paint-swatch");
			if (window.enablePrepaintedMessaging) {
				if (paintedStockKeyLevel && decodedVINHasAvailableStock) {
					paintedStockKeyLevel.style.display = "block";
				}
			}
			// Trigger the change event programmatically
			const autoselectEvent = new Event("change", { bubbles: true });
			autoselectEvent._fromAutoSelect = true;
			variantSelector.dispatchEvent(autoselectEvent);
			if (typeof vinInputLibrary !== "undefined" && vinInputLibrary) {
				vinInputLibrary.value = vinInput;
			}
		} else {
			// Either match paint by vin or force select code
			handleVehiclesFailedToDecodePaint(
				vinInput,
				licensePlate,
				forceSelectMsgStart,
				forceSelectMsgEnd,
				location,
				true,
			);
			handleAutoSelectMatchByVin();
			// Trigger stored radio change handler so the full UI setup (price, form action, add to cart) fires
			const storedRadio = document.getElementById(
				"checkbox-paint-code-local-storage",
			);
			if (storedRadio && storedRadio.checked) {
				storedRadio.dispatchEvent(new Event("change", { bubbles: true }));
			}
			hideGiveUsYourVinMsg();
			paintOptionsVinLicense.classList.add("hidden");
			console.warn(
				`No matching option found for paint code: ${paintCode} using the vin: ${vinInput}`,
			);
		}
	} else {
		console.warn("Variant selector dropdown not found.");
		return { matchingOption, noPaintVariants: true };
	}

	if (location === 3) {
		const paintCodeAppContainer = document.getElementById(
			"paintcode-app-container",
		);
		if (paintCodeAppContainer) paintCodeAppContainer.classList.remove("show");
	}

	if (location === 4) {
		const paintCodeAppContainerLicense = document.getElementById(
			"paintcode-app-container-license",
		);
		if (paintCodeAppContainerLicense)
			paintCodeAppContainerLicense.classList.remove("show");
	}

	if (!window.TOGGLE_MATCH_PAINT_BY_VIN) {
		if (paintCodeWrapper) paintCodeWrapper.classList.add("show");
	} else {
		if (paintCodeWrapper) paintCodeWrapper.classList.remove("show");
	}

	// If searching from the garage and not on the product page, there won't be a
	// variantSelector, therefore, we must return noPaintVariants as true
	if (location === 1 && !window.location.pathname.includes("/products/")) {
		return { matchingOption, noPaintVariants: true };
	}

	return { matchingOption, noPaintVariants: false };
}

function resetStoredPaintToNoVinState() {
	storedDecodedVin = "";
	matchingPaintByVinVisible = false;
	document.querySelector(".vin-selected-message")?.remove();
	if (storedDecodedPaintCodeMsgGlobal)
		storedDecodedPaintCodeMsgGlobal.style.display = "none";
	if (storedOptionLabel) {
		storedOptionLabel.style.display = "none";
		storedOptionLabel.classList.remove("match-paint-by-vin-option");
		const r = storedOptionLabel.querySelector('input[type="radio"]');
		if (r) r.checked = false;
	}
	if (failSafeSelectCodeOptionLabel)
		failSafeSelectCodeOptionLabel.style.display = "none";
	if (selectCodeOptionLabel) selectCodeOptionLabel.style.display = "block";
	if (paintOptionGroup) paintOptionGroup.style.display = "none";
}

function hasPdpMatchPaintByVinCapability() {
	const isProductPage = ["/products/", "/product/", "/shop/"].some((path) =>
		window.location.pathname.includes(path),
	);
	return Boolean(
		isProductPage &&
			window.shopCurrency === "USD" &&
			window.TOGGLE_MATCH_PAINT_BY_VIN === true &&
			document.getElementById("checkbox-get-paint-code-with-vin") &&
			(document.querySelector(".hidden-vin-option") || getMatchByVinVariantOption()),
	);
}

function syncPdpVinInputsFromGarage(options = {}) {
	if (window.__partifyPdpVinEditing && options.force !== true) return "";
	const activeVehicle = readStoredGarageTerms()[0] || null;
	const vin = normalizeVehicleLookupVin(activeVehicle?.vin);
	const canonicalVin = vin.length === 17 ? vin : "";
	window.__partifySyncingPdpVinInputs = true;
	try {
		document.querySelectorAll("[data-vin-input]").forEach((input) => {
			if (input.value === canonicalVin) return;
			input.value = canonicalVin;
			input.dispatchEvent(new Event("input", { bubbles: true }));
		});
	} finally {
		window.__partifySyncingPdpVinInputs = false;
	}
	document.body.classList.toggle("has-vin-on-file", Boolean(canonicalVin));
	document.querySelectorAll("[data-pdp-vin-change]").forEach((button) => {
		button.hidden = !canonicalVin;
		button.style.display = canonicalVin ? "inline-flex" : "none";
	});
	return canonicalVin;
}

function showPdpVinChangeControl(resolvedMessage) {
	if (!hasPdpMatchPaintByVinCapability()) return;
	const changeControl = document.querySelector("[data-pdp-vin-change]");
	if (!changeControl) return;
	if (resolvedMessage && changeControl.parentElement !== resolvedMessage) {
		resolvedMessage.appendChild(changeControl);
	}
	changeControl.hidden = false;
	changeControl.style.display = "inline-flex";
}

function restoreResolvedPdpVinStateFromGarage(requestToken) {
	if (window.__partifyPdpVinEditing || !hasPdpMatchPaintByVinCapability()) {
		return false;
	}
	const activeVehicle = readStoredGarageTerms()[0] || null;
	const vin = normalizeVehicleLookupVin(activeVehicle?.vin);
	if (
		vin.length !== 17 ||
		(!String(activeVehicle?.paintCode || "").trim() &&
			!String(activeVehicle?.troublesome_format || "").trim())
	) {
		return false;
	}

	syncPdpVinInputsFromGarage({ force: true });
	return Boolean(handleInsertLocalStoragePaintOption(requestToken));
}

function resetPdpVinTransientState(options = {}) {
	const openEditor = options.openEditor === true;
	pdpVinEditingFromVehicleKey = openEditor
		? getCanonicalGarageVehicleKey(readStoredGarageTerms()[0])
		: "";
	window.__partifyPdpVinEditing = openEditor;
	directPdpVehicleSelection = null;
	pendingMismatchDecodedVehicle = null;
	window.PartifyPaintDecoderRequest.invalidate(openEditor ? "vin" : "vehicle");
	autoSelectedColor = "";
	autoSelectedOption = null;
	firstVehicleColor = "";
	resetStoredPaintToNoVinState();
	document.body.classList.remove("has-vin-on-file");

	document.querySelectorAll("[data-vin-input]").forEach((input) => {
		input.value = "";
	});
	document
		.querySelectorAll(
			".vin-decoder-submission-failed-message, .license-decoder-submission-failed-message, .vin-decoder-submission-success-message, .vin-selected-message",
		)
		.forEach((element) => {
			element.style.display = "none";
		});
	document.querySelectorAll(".vin-decoder-submit-btn").forEach((button) => {
		button.disabled = true;
		button.classList.add("vin-submit--initial");
	});

	const variantSelector = document.getElementById("variant-selector");
	if (variantSelector) variantSelector.value = "";
	document.querySelectorAll('form [name="id"]').forEach((input) => {
		input.value = "";
	});
	document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
		try {
			const action = new URL(form.getAttribute("action") || "/cart/add", window.location.origin);
			action.searchParams.delete("id");
			form.setAttribute("action", `${action.pathname}${action.search}`);
		} catch (error) {
			// Keep the reset local even if a third-party form has an invalid action.
		}
	});
	document.querySelectorAll('input[name="properties[VIN]"]').forEach((input) => {
		input.value = "";
		input.disabled = true;
	});
	document.querySelectorAll(".add-to-cart, [name='add']").forEach((button) => {
		button.disabled = true;
	});
	document.querySelectorAll("[data-pdp-vin-change]").forEach((button) => {
		button.hidden = true;
		button.style.display = "none";
	});

	if (hasPdpMatchPaintByVinCapability()) {
		const vinRadio = document.getElementById("checkbox-get-paint-code-with-vin");
		vinRadio.disabled = false;
		if (paintOptionGroup) {
			paintOptionGroup.classList.remove("hidden");
			paintOptionGroup.style.display = "block";
		}
		if (openEditor) {
			vinRadio.checked = true;
			vinRadio.dispatchEvent(new Event("change", { bubbles: true }));
			document.querySelector("[data-vin-input]")?.focus();
		}
	}
}

function hasPendingMismatchDecodedVehicle(requestToken) {
	return Boolean(
		pendingMismatchDecodedVehicle &&
			pendingMismatchDecodedVehicle.options.requestToken === requestToken &&
			isCurrentPaintDecoderRequest(requestToken),
	);
}

async function activatePendingMismatchDecodedVehicle(vin) {
	const pending = pendingMismatchDecodedVehicle;
	if (
		!pending ||
		normalizeVehicleLookupVin(pending.vehicle.vin) !==
			normalizeVehicleLookupVin(vin) ||
		!isCurrentPaintDecoderRequest(pending.options.requestToken)
	) {
		return null;
	}

	const previousTerms = readStoredGarageTerms();
	const activeVehicle = await validateAndActivateDecodedVehicleInGarage(
		pending.vehicle,
		pending.options,
	);
	if (
		!activeVehicle ||
		!isCurrentPaintDecoderRequest(pending.options.requestToken)
	) {
		return null;
	}

	recordRemovedGarageVehicleIdentities(previousTerms, readStoredGarageTerms());
	directPdpVehicleSelection = {
		key: getCanonicalGarageVehicleKey(activeVehicle),
	};
	pendingMismatchDecodedVehicle = null;
	window.PartifyPaintDecoderRequest.finish(pending.options.requestToken, true);
	window.PartifyPdpVinState?.complete?.({
		requestToken: pending.options.requestToken,
		restore: true,
	});
	return activeVehicle;
}

function discardPendingMismatchDecodedVehicle() {
	const pending = pendingMismatchDecodedVehicle;
	pendingMismatchDecodedVehicle = null;
	if (
		pending &&
		isCurrentPaintDecoderRequest(pending.options.requestToken)
	) {
		window.PartifyPaintDecoderRequest.invalidate("vin");
	}
}

window.PartifyPdpVinState = {
	...(window.PartifyPdpVinState || {}),
	syncFromGarage: syncPdpVinInputsFromGarage,
	restoreResolvedFromGarage: restoreResolvedPdpVinStateFromGarage,
	hasPendingMismatch: hasPendingMismatchDecodedVehicle,
	beginEdit() {
		resetPdpVinTransientState({ openEditor: true });
	},
	complete(options = {}) {
		window.__partifyPdpVinEditing = false;
		pdpVinEditingFromVehicleKey = "";
		syncPdpVinInputsFromGarage({ force: true });
		return options.restore === true
			? restoreResolvedPdpVinStateFromGarage(options.requestToken)
			: false;
	},
	isEditing() {
		return window.__partifyPdpVinEditing === true;
	},
};

document.addEventListener("click", (event) => {
	if (!event.target?.closest?.("[data-pdp-vin-change]")) return;
	event.preventDefault();
	window.PartifyPdpVinState.beginEdit();
});

window.addEventListener("searchTermsUpdated", () => {
	window.PartifyPdpVinState.syncFromGarage();
});

function findVariantSelectOptionByPaintCode(variantSelector, paintCode) {
	if (!variantSelector || !paintCode) return null;
	const targetCode = String(paintCode).trim();
	return Array.from(variantSelector.options).find((option) => {
		if (
			!option.value ||
			option.disabled ||
			option.getAttribute("data-unavailable") === "true"
		)
			return false;
		const variantTitle = (
			option.getAttribute("data-variant-title") ||
			(option.label || option.textContent || "").split("+")[0] ||
			""
		).trim();
		const cleanTitle = variantTitle.includes(" / ")
			? variantTitle.split(" / ").pop().trim()
			: variantTitle;
		return (
			variantTitle === targetCode ||
			cleanTitle === targetCode ||
			(targetCode.length >= 2 && cleanTitle.startsWith(targetCode + " ")) ||
			(targetCode.length >= 2 && cleanTitle.startsWith(targetCode + "("))
		);
	});
}

function resolveSelectableExactTroublesomePaintReference(vehicle, variantSelector) {
	const rawPaintCode = getRawDecodedVehiclePaintCode(vehicle);
	const make = String(vehicle?.make || "").trim().toLowerCase();
	if (
		window.shopCurrency !== "USD" ||
		twoToneSelected === true ||
		isMismatch === true ||
		pendingMismatchDecodedVehicle ||
		!variantSelector ||
		!hasVinOrPlateContext(vehicle) ||
		shouldForceMatchByVinForVehicle(vehicle) ||
		!make ||
		!troublesomeMakesGlobal.includes(make) ||
		!rawPaintCode ||
		isMatchPaintByVinCode(rawPaintCode) ||
		findVariantSelectOptionByPaintCode(variantSelector, rawPaintCode) ||
		!window.location.pathname.includes("/products/")
	)
		return null;

	const exactReference = findTroublesomePaintReference(
		Object.keys(productPageTroublesomeReferences),
		rawPaintCode,
		vehicle,
	);
	if (!exactReference) return null;

	const paintCode = String(
		productPageTroublesomeReferences[exactReference] || "",
	).trim();
	const matchingOption = findVariantSelectOptionByPaintCode(
		variantSelector,
		paintCode,
	);
	return matchingOption ? { paintCode, matchingOption } : null;
}

function reconcileStoredExactReferenceVehiclePaint(vehicle) {
	if (!isMatchPaintByVinCode(vehicle?.paintCode)) return vehicle;
	const vehicleKey = getCanonicalGarageVehicleKey(vehicle);
	const searchTerms = getSearchTerms();
	const activeVehicle = searchTerms?.[0];
	if (
		!vehicleKey ||
		getCanonicalGarageVehicleKey(activeVehicle) !== vehicleKey ||
		!isMatchPaintByVinCode(activeVehicle?.paintCode)
	)
		return activeVehicle || null;

	const persistedPaint = getGaragePersistedPaintState(
		getRawDecodedVehiclePaintCode(activeVehicle),
		activeVehicle.decodedByBumper,
		true,
	);
	searchTerms[0] = { ...activeVehicle, ...persistedPaint };
	setSearchTerms(searchTerms, 5);
	if (window.GarageLogic?.updateGaragePopup) {
		window.GarageLogic.updateGaragePopup(getSearchTerms());
	}
	return getSearchTerms()?.[0] || null;
}

function getVehiclePaintCodeForCurrentProduct(vehicle) {
	let paintCode = String(vehicle?.paintCode || "").trim();
	const make = String(vehicle?.make || "").trim().toLowerCase();
	const rawPaintCode = String(vehicle?.troublesome_format || "").trim();
	const decodedPaintCode = make && troublesomeMakesGlobal.includes(make)
		? getRawDecodedVehiclePaintCode(vehicle)
		: paintCode;
	if (
		window.shopCurrency === "USD" &&
		!shouldForceMatchByVinForVehicle(vehicle) &&
		!isMatchPaintByVinCode(decodedPaintCode) &&
		findVariantSelectOptionByPaintCode(
			document.getElementById("variant-selector"),
			decodedPaintCode,
		)
	)
		return decodedPaintCode;
	if (
		make &&
		troublesomeMakesGlobal.includes(make) &&
		rawPaintCode &&
		window.location.pathname.includes("/products/")
	) {
		const referencedPaint = handleTroublesomeMake(
			vehicle.make,
			rawPaintCode,
			vehicle.troublesome_description || "",
			vehicle.decodedByBumper,
			"",
			vehicle,
		);
		paintCode = String(referencedPaint.colorCode || rawPaintCode).trim();
	}
	return paintCode;
}

function resolveStoredPaintVariantChoice(
	variantSelector,
	vehicle,
	fallbackPaintCode,
	matchByVinAvailable,
) {
	const candidateCodes = [
		getVehiclePaintCodeForCurrentProduct(vehicle),
		vehicle?.paintCode,
		fallbackPaintCode,
	]
		.map((value) => String(value || "").trim())
		.filter((value, index, values) => value && values.indexOf(value) === index);

	for (const paintCode of candidateCodes) {
		if (isMatchPaintByVinCode(paintCode)) continue;
		const normalizedPaintCode = paintCode.includes(" / ")
			? paintCode.split(" / ").pop().trim()
			: paintCode;
		const matchingOption = findVariantSelectOptionByPaintCode(
			variantSelector,
			normalizedPaintCode,
		);
		if (matchingOption) {
			return {
				paintCode: normalizedPaintCode,
				matchingOption,
				useMatchByVin: false,
			};
		}
	}

	const realPaintCode = candidateCodes.find(
		(paintCode) => !isMatchPaintByVinCode(paintCode),
	);
	return {
		paintCode: realPaintCode || candidateCodes[0] || "",
		matchingOption: null,
		useMatchByVin:
			candidateCodes.some(isMatchPaintByVinCode) || Boolean(matchByVinAvailable),
	};
}

function enableResolvedStoredPaintControls(variantSelector, matchingOption) {
	if (
		!variantSelector ||
		!matchingOption ||
		matchingOption.disabled ||
		matchingOption.dataset.unavailable === "true"
	)
		return false;

	variantSelector.disabled = false;
	[
		"checkbox-select-unpainted",
		"checkbox-select-paint-option",
		"checkbox-get-paint-code-with-vin",
		"checkbox-get-paint-code-with-license",
		"fake-checkbox-get-paint-code-with-license-or-vin",
		"checkbox-paint-code-local-storage",
	].forEach((id) => {
		const control = document.getElementById(id);
		if (control) control.disabled = false;
	});

	const failSafe = document.querySelector(".select-code-fail-safe");
	if (failSafe) failSafe.disabled = false;
	return true;
}

// When the stored/decoded paint-code radio is (re)selected, sync the variant-selector
// (which is what actually posts to the cart via name="id") back to the decoded code.
// Without this, manually picking a different code in the dropdown and then re-selecting
// the stored radio would leave the dropdown's variant in the cart instead of the decoded one.
function selectStoredPaintVariantOption() {
	const variantSelector = document.getElementById("variant-selector");
	if (!variantSelector) return;
	const terms = getSearchTerms();
	const storedVehicle = terms && terms.length ? terms[0] : null;
	const choice = resolveStoredPaintVariantChoice(
		variantSelector,
		storedVehicle,
		"",
		Boolean(getMatchByVinVariantOption()),
	);
	const match = choice.useMatchByVin
		? getMatchByVinVariantOption()
		: choice.matchingOption;
	if (!choice.useMatchByVin && match) {
		matchingPaintByVinVisible = false;
		enableResolvedStoredPaintControls(variantSelector, match);
		autoSelectedColor = match.value;
		autoSelectedOption = match;
		selectedVariantUnavailable = false;
	}
	if (match && variantSelector.value !== match.value) {
		variantSelector.value = match.value;
		variantSelector.dispatchEvent(new Event("change", { bubbles: true }));
	}
	return Boolean(match);
}

function bindStoredPaintRadioSync() {
	const storedRadio = document.getElementById(
		"checkbox-paint-code-local-storage",
	);
	if (!storedRadio || storedRadio.dataset.syncBound === "true") return;
	storedRadio.dataset.syncBound = "true";
	storedRadio.addEventListener("change", function () {
		if (storedRadio.checked) selectStoredPaintVariantOption();
	});
}

function canCurrentPaintOwnerWriteStoredPaint(requestToken) {
	const request = window.PartifyPaintDecoderRequest;
	if (requestToken != null) return request.current(requestToken);
	if (request.status === "pending") return false;
	if (["manual", "unpainted"].includes(request.owner)) return false;
	if (
		request.status === "idle" &&
		["vin", "plate", "manual", "lookup", "paint-option"].includes(
			request.owner,
		)
	)
		return false;
	return true;
}

function getStoredPaintSelectionKey(vehicle, paintCode) {
	return [
		vehicle?.ymm,
		vehicle?.vin,
		vehicle?.state,
		vehicle?.plate || vehicle?.licensePlate,
		paintCode,
	]
		.map((value) =>
			String(value || "")
				.trim()
				.toUpperCase(),
		)
		.join("|");
}

function syncResolvedStoredPaintSelection(vehicle, paintCode, requestToken) {
	if (window.shopCurrency !== "USD" || !hasVinOrPlateContext(vehicle)) return;
	const storedRadio = document.getElementById("checkbox-paint-code-local-storage");
	if (!storedRadio) return;

	const canonicalPaintCode =
		vehicle?.paintCode || vehicle?.troublesome_format || paintCode;
	const selectionKey = getStoredPaintSelectionKey(vehicle, canonicalPaintCode);
	storedRadio.checked = true;
	setTimeout(function () {
		const currentVehicle = getSearchTerms()?.[0];
		if (
			!canCurrentPaintOwnerWriteStoredPaint(requestToken) ||
			getStoredPaintSelectionKey(
				currentVehicle,
				currentVehicle?.paintCode || currentVehicle?.troublesome_format,
			) !==
				selectionKey
		)
			return;

		const currentStoredRadio = document.getElementById(
			"checkbox-paint-code-local-storage",
		);
		if (!currentStoredRadio) return;
		selectStoredPaintVariantOption();
		if (currentStoredRadio.disabled) return;
		currentStoredRadio.checked = true;
		currentStoredRadio.dispatchEvent(new Event("change", { bubbles: true }));
	}, 0);
}

function handleInsertLocalStoragePaintOption(requestToken) {
	if (!canCurrentPaintOwnerWriteStoredPaint(requestToken)) return "";
	const garageSearchTerms = getSearchTerms();
	const combinedVariantSelector = document.getElementById("variant-selector");
	bindStoredPaintRadioSync();
	let firstItemColor = "";
	let firstItemState = "";
	let firstItemPlate = "";
	let firstItemVin = "";
	let foundMatchingColor = false;
	if (garageSearchTerms && garageSearchTerms.length) {
		const firstVehicle = garageSearchTerms[0];
		const rawVehiclePaintCode = getRawDecodedVehiclePaintCode(firstVehicle);
		const directPaintOption =
			window.shopCurrency === "USD" &&
			!shouldForceMatchByVinForVehicle(firstVehicle) &&
			!isMatchPaintByVinCode(rawVehiclePaintCode) &&
			findVariantSelectOptionByPaintCode(
				combinedVariantSelector,
				rawVehiclePaintCode,
			);
		firstItemColor = getVehiclePaintCodeForCurrentProduct(firstVehicle);
		const exactReferenceChoice =
			resolveSelectableExactTroublesomePaintReference(
				firstVehicle,
				combinedVariantSelector,
			);
		if (exactReferenceChoice) firstItemColor = exactReferenceChoice.paintCode;
		firstItemState = firstVehicle?.state;
		firstItemPlate = firstVehicle?.plate;
		firstItemVin = firstVehicle?.vin;
		if (
			shouldForceMatchByVinForVehicle(firstVehicle)
		) {
			firstItemColor = "Match Paint by VIN";
		} else if (isMatchPaintByVinCode(firstItemColor)) {
			firstItemColor = "Match Paint by VIN";
		}

		let invalidVehiclePaintCode = false;
		if (
			rawVehiclePaintCode &&
			!isMatchPaintByVinCode(rawVehiclePaintCode) &&
			hasVinOrPlateContext(firstVehicle)
		) {
			invalidVehiclePaintCode =
				isPaintCodeValidForVehicle(firstVehicle, rawVehiclePaintCode) === false;
			if (invalidVehiclePaintCode && !directPaintOption && !exactReferenceChoice) {
				firstItemColor = "Match Paint by VIN";
			}
		}

		// If the color exists in local storage
		if (firstItemColor) {
			if (isMatchPaintByVinCode(firstItemColor)) {
				selectStoredMatchByVinFallback(
					firstVehicle,
					invalidVehiclePaintCode,
					0,
					requestToken,
				);
				return firstItemColor;
			}

			const allVariantOptions = window.productVariants || {};

			// Pick the first non-empty array ([] is truthy in JS, so use .length)
			const sampleArrToCheck =
				(allVariantOptions.aftermarket?.length &&
					allVariantOptions.aftermarket) ||
				(allVariantOptions.capa?.length && allVariantOptions.capa) ||
				(allVariantOptions.oem?.length && allVariantOptions.oem) ||
				(allVariantOptions.current?.length && allVariantOptions.current) ||
				(allVariantOptions.single?.length && allVariantOptions.single) ||
				[];

			const unpaintedOption = sampleArrToCheck[0];
			for (let i = 0; i < sampleArrToCheck.length; i++) {
				const option = sampleArrToCheck[i];

				const vt = (option.variantTitle || "").trim();
				const tt = (option.title || "").trim();
				const fc = firstItemColor.trim();
				// Strip quality prefix (e.g. "Aftermarket / PGU (2011-2019)" → "PGU (2011-2019)")
				const vtClean = vt.includes(" / ") ? vt.split(" / ").pop().trim() : vt;
				if (
					fc === vt ||
					fc === tt ||
					fc === vtClean ||
					(fc.length >= 2 && vt.startsWith(fc + " ")) ||
					(fc.length >= 2 && vt.startsWith(fc + "(")) ||
					(fc.length >= 2 && vtClean.startsWith(fc + " ")) ||
					(fc.length >= 2 && vtClean.startsWith(fc + "("))
				) {
					let priceDifference = option.priceDifference || null;
					if (priceDifference === null) {
						// we need to search through all the options of combinedVariantSelector to find the priceDifference, which is an attribute data-price-difference
						const allOptions = combinedVariantSelector
							? Array.from(combinedVariantSelector.options)
							: [];
						const matchedOption = allOptions.find((opt) => {
							const codeInFrontOfPrice = opt.label.split("+")[0];
							return codeInFrontOfPrice.trim() === firstItemColor.trim();
						});
						if (matchedOption) {
							priceDifference = matchedOption.dataset.priceDifference;
						} else {
							priceDifference = 0;
						}
					}

					decodedPaintedOptionPrice = (priceDifference / 100).toLocaleString(
						"en-US",
						{
							style: "currency",
							currency: "USD",
						},
					);
					foundMatchingColor = true;
				}
			}

			if (!foundMatchingColor) {
				const matchedSelectOption = findVariantSelectOptionByPaintCode(
					combinedVariantSelector,
					firstItemColor,
				);
				if (matchedSelectOption) {
					const priceDifference = Number(
						matchedSelectOption.dataset.priceDifference || 0,
					);
					decodedPaintedOptionPrice = (
						Number.isFinite(priceDifference) ? priceDifference / 100 : 0
					).toLocaleString("en-US", {
						style: "currency",
						currency: "USD",
					});
					foundMatchingColor = true;
				}
			}

			// If the color stored in local storage is found to match a variant option, display matching option and hide VIN and license option
			if (foundMatchingColor) {
				const resolvedStoredVehicle = exactReferenceChoice
					? reconcileStoredExactReferenceVehiclePaint(firstVehicle)
					: firstVehicle;
				matchingPaintByVinVisible = false;
				if (storedOptionLabel) storedOptionLabel.style.display = "block";
				if (failSafeSelectCodeOptionLabel)
					failSafeSelectCodeOptionLabel.style.display = "block";
				if (selectCodeOptionLabel) selectCodeOptionLabel.style.display = "none";
				// Formats the checkbox to look like: "Painted [color]"
				if (checkboxStoredCodeText) {
					checkboxStoredCodeText.innerHTML =
						decodedPaintedOptionPainted + " " + firstItemColor;
				}
				if (storedOptionLabel) storedOptionLabel.classList.remove("match-paint-by-vin-option");

				// If both state and plate exist in local storage, render the paint option message accordingly
				if (firstItemState && firstItemPlate) {
					const localStatePlate = firstItemState + "-" + firstItemPlate;
					displayStoredPaintCodeMsg(false, localStatePlate, firstItemColor);
					hideGiveUsYourVinMsg();

					// If no state or plate exist but a vin does, render the paint option accordingly
				} else if (firstItemVin) {
					const localVin = firstItemVin;
					displayStoredPaintCodeMsg(true, localVin, firstItemColor);
					hideGiveUsYourVinMsg();
				}
				if (paintOptionGroup) {
					paintOptionGroup.style.display = "none";
				}

				// Reuse the stored-radio change path so the current quality's variant and
				// product form are synchronized after the surrounding render completes.
				syncResolvedStoredPaintSelection(
					resolvedStoredVehicle || getSearchTerms()?.[0] || firstVehicle,
					firstItemColor,
					requestToken,
				);

				// If the stored VIN paint code is valid for the vehicle but not sold on
				// this PDP, keep the garage code and use this product's VIN fallback.
			} else {
				const shouldUseProductFallback =
					hasVinOrPlateContext(firstVehicle) &&
					isPaintCodeValidForVehicle(firstVehicle, rawVehiclePaintCode) !== false;
				if (shouldUseProductFallback) {
					selectStoredMatchByVinFallback(
						firstVehicle,
						false,
						0,
						requestToken,
					);
				} else {
					if (paintOptionGroup) {
						paintOptionGroup.style.display = "block";
					}
					resetStoredPaintToNoVinState();
				}
			}

			// If vehicle exists in garage but does not have a color associated with it yet,
			// ensure the VIN and license options are displayed
		} else {
			if (paintOptionGroup) {
				paintOptionGroup.style.display = "block";
			}
		}
	}

	return firstItemColor;
}

function refreshStoredPaintOptionFromGarage(event) {
	if (
		!window.location.pathname.includes("/products/") ||
		!document.getElementById("variant-selector")
	)
		return;
	const activeVehicle = readStoredGarageTerms()[0] || null;
	if (window.__partifyPdpVinEditing) {
		const activeKey = getCanonicalGarageVehicleKey(activeVehicle);
		const eventKey = getCanonicalGarageVehicleKey(event?.detail);
		if (
			(eventKey && eventKey !== activeKey) ||
			!activeKey ||
			activeKey === pdpVinEditingFromVehicleKey
		) {
			return;
		}
		window.__partifyPdpVinEditing = false;
		pdpVinEditingFromVehicleKey = "";
	}
	if (!activeVehicle) {
		if (hasPdpMatchPaintByVinCapability()) {
			resetPdpVinTransientState();
		} else {
			window.PartifyPaintDecoderRequest.invalidate("vehicle");
			resetStoredPaintToNoVinState();
			if (paintOptionGroup) paintOptionGroup.style.display = "block";
		}
		return;
	}

	window.PartifyPaintDecoderRequest.invalidate("vehicle");
	resetStoredPaintToNoVinState();
	window.PartifyPdpVinState.syncFromGarage({ force: true });
	firstVehicleColor = handleInsertLocalStoragePaintOption();
	if (firstVehicleColor) window.PartifyPaintDecoderRequest.claim("stored");
	if (!firstVehicleColor && paintOptionGroup) paintOptionGroup.style.display = "block";
}

window.addEventListener("partify:vehicle:changed", refreshStoredPaintOptionFromGarage);

function scheduleStoredPaintOptionInitializationSync() {
	if (!window.location.pathname.includes("/products/")) return;
	[0, 250, 1000, 3000].forEach((delay) => {
		setTimeout(function () {
			if (!document.getElementById("variant-selector")) return;
			observeStoredPaintVariantOptions();
			if (!canCurrentPaintOwnerWriteStoredPaint()) return;
			const currentVehicle = getSearchTerms()?.[0];
			if (
				!currentVehicle?.paintCode &&
				!currentVehicle?.troublesome_format
			)
				return;
			window.PartifyPdpVinState.syncFromGarage({ force: true });
			firstVehicleColor = handleInsertLocalStoragePaintOption();
			if (firstVehicleColor) {
				window.PartifyPaintDecoderRequest.claim("stored");
				setTimeout(selectStoredPaintVariantOption, 0);
			}
		}, delay);
	});
}

let storedPaintVariantObserver = null;
let storedPaintVariantObserverTarget = null;
let storedPaintVariantSyncTimer = null;

function observeStoredPaintVariantOptions() {
	const variantSelector = document.getElementById("variant-selector");
	if (!variantSelector || storedPaintVariantObserverTarget === variantSelector)
		return;

	if (storedPaintVariantObserver) storedPaintVariantObserver.disconnect();
	storedPaintVariantObserverTarget = variantSelector;
	storedPaintVariantObserver = new MutationObserver(function () {
		clearTimeout(storedPaintVariantSyncTimer);
		storedPaintVariantSyncTimer = setTimeout(function () {
			const storedRadio = document.getElementById(
				"checkbox-paint-code-local-storage",
			);
			if (
				!storedRadio?.checked ||
				!canCurrentPaintOwnerWriteStoredPaint()
			)
				return;
			handleInsertLocalStoragePaintOption();
		}, 0);
	});
	storedPaintVariantObserver.observe(variantSelector, { childList: true });
}

if (document.readyState === "loading") {
	document.addEventListener(
		"DOMContentLoaded",
		scheduleStoredPaintOptionInitializationSync,
		{ once: true },
	);
} else {
	scheduleStoredPaintOptionInitializationSync();
}

function displayStoredPaintCodeMsg(isVin, vinOrLicense, color) {
	storedDecodedPaintCodeMsgGlobal.style.display = "block";
	showPdpVinChangeControl(storedDecodedPaintCodeMsgGlobal);
	const licenseOrVinSpan = document.querySelector(
		".stored-paint-code-license-or-vin",
	);
	const colorSpan = document.querySelector(".stored-paint-code-color");

	if (isVin) {
		licenseOrVinSpan.textContent = vinWord + " " + vinOrLicense + ",";
	} else {
		licenseOrVinSpan.textContent = licensePlateWord + " " + vinOrLicense + ",";
	}
	colorSpan.textContent = color + ".";
}

function hideGiveUsYourVinMsg() {
	const giveUsYourVinMsg = document.querySelector(".give-us-your-vin-msg");
	if (giveUsYourVinMsg) giveUsYourVinMsg.style.display = "none";
}

function showGiveUsYourVinMsg() {
	const giveUsYourVinMsg = document.querySelector(".give-us-your-vin-msg");
	if (giveUsYourVinMsg) giveUsYourVinMsg.style.display = "block";
}

function resortToForceSelectCode() {
	if (typeof window.closePaintCodeHelpModal === "function") {
		window.closePaintCodeHelpModal();
	}
	const paintCodeWrapper = document.querySelector(".paint-code-wrapper");
	const paintCodeCheckbox = document.getElementById(
		"checkbox-select-paint-option",
	);
	const fakeCheckboxCircle = document.getElementById(
		"fake-checkbox-get-paint-code-with-license-or-vin",
	);
	if (paintCodeWrapper) paintCodeWrapper.classList.add("show");
	if (paintCodeCheckbox) {
		paintCodeCheckbox.checked = true;
		paintCodeCheckbox.dispatchEvent(new Event("change", { bubbles: true }));
	}

	// Hide both vin and license checkboxes if they exist
	if (paintOptionGroup) {
		paintOptionGroup.style.display = "none";
	}

	// the fake check should be removed
	if (fakeCheckboxCircle) fakeCheckboxCircle.checked = false;
}

function formulateForcePaintCodeSelectMsg(
	vin,
	licensePlate,
	forceSelectMsgStart,
	forceSelectMsgEnd,
	location,
) {
	const isProductPage = window.location.pathname.includes("/products/");
	if (isMismatch && isProductPage) {
		// Check if modal is already visible to avoid duplicates
		const modalContainer = document.querySelector(".mismatch-modal-container");
		if (modalContainer && modalContainer.style.display !== "none") {
			return; // Already shown
		}

		showMismatchModal(mismatchDifferences, vin, licensePlate, paintCode);
	} else if (!isMismatch) {
		const vinDecoderMessage = document.querySelector(".vin-decoder-message");
		if (!vinDecoderMessage) {
			const paintCodeWrapper = document.querySelector(".paint-code-wrapper");
			// const existingMessage = vinTextboxContainer.querySelector('.vin-selected-message');
			// vinTextboxContainer.style.display = "block";

			// // Remove any existing message to avoid duplication
			// if (existingMessage) {
			//   existingMessage.remove();
			// }

			// Create a new message to indicate the selected VIN
			const vinMessage = document.createElement("div");
			vinMessage.className = "vin-decoder-message resort-to-force-select";
			vinMessage.style.display = "block";
			if (location === 3) {
				vinMessage.innerHTML = `
        <strong style="color: #444;">${forceSelectMsgStart}</strong> 
        <span style="color: #000; font-weight: 800;">${vin}.</span> 
        <span style="color: #444;">${forceSelectMsgEnd}</span> 
      `;
			}

			if (location === 4) {
				vinMessage.innerHTML = `
        <strong style="color: #444;">${forceSelectMsgStart}</strong> 
        <span style="color: #000; font-weight: 800;">${licensePlate}.</span> 
        <span style="color: #444;">${forceSelectMsgEnd}</span> 
      `;
			}
			vinMessage.style.backgroundColor = "#ffbc2c17";
			vinMessage.style.border = "1px solid #ffae00";

			failedVinDecode = true;

			// Append the message to the VIN textbox container
			paintCodeWrapper.appendChild(vinMessage);
		}
	}
}

async function decoderLoggerWebhook(
	state,
	plate,
	vin,
	site,
	year,
	make,
	model,
	submodel,
	engine,
	styleId,
	code,
	error,
	partifyLocation,
	parentUrl,
	genericDesc,
	fullDescription,
	hex,
) {
	try {
		const response = await fetch(
			"https://script.google.com/macros/s/AKfycbyZxJM6lMzTVo_kfJIKIZbbz1gjo_xqE0LMgNYs1qWwpDUgjh6pmziBltVbzvxq5fVndg/exec",
			{
				redirect: "follow",
				method: "POST",
				headers: {
					"Content-Type": "text/plain;charset=UTF-8",
				},
				body: JSON.stringify({
					state: state,
					plate: plate,
					vin: vin,
					site: site,
					year: year,
					make: make,
					model: model,
					submodel: submodel,
					engine: engine,
					styleId: styleId,
					code: code,
					error: error,
					themeName: themeName,
					partifyLocation: partifyLocation,
					parentUrl: parentUrl,
					genericDesc: genericDesc,
					fullDescription: fullDescription,
					hex: hex,
				}),
			},
		);

		// If the response is not JSON, log it as text to debug
		const text = await response.text();
		// Try parsing the JSON only if the response status is OK
		if (response.ok) {
			const result = JSON.parse(text);
			return result;
		} else {
			console.error("Server responded with an error:", text);
		}
	} catch (error) {
		console.error("Error submitting logs:", error);
	}
}

async function logGarageEventToSheets(logWay, payload, ...rest) {
	let endpoint = "";
	try {
		// Optionally add timestamp if not present
		if (logWay === "dropdown") {
			const raw = localStorage.getItem("searchTerms");

			if (raw) {
				const parseData = JSON.parse(raw);
				if (Array.isArray(parseData) && parseData.length > 0) {
					payload.year = parseData[0].year || "";
					payload.make = parseData[0].make || "";
					payload.model = parseData[0].model || "";
					payload.submodel = parseData[0].submodel || "";
					payload.engine = parseData[0].engine || "";
					payload.currentPage = parseData[0].currentPage || "";
				}
			}
			endpoint =
				"https://script.google.com/macros/s/AKfycbyufCNIVCej7CBjGLcv23qfPqD5lFTyh4XLhze8RWun7V4dp4py1obPsXy-U2RhoKmd/exec";
		}

		// Optionally add themeName and partifyLocation if available
		if (logWay === "type") {
			if (typeof themeName !== "undefined" && themeName && !payload.themeName) {
				payload.themeName = themeName;
			}
			// COMMENTED OUT: Saves ~100ms
			// if (
			// 	typeof getPartifyLocation === "function" &&
			// 	!payload.partifyLocation
			// ) {
			// 	const loc = await getPartifyLocation();
			// 	payload.partifyLocation = loc;
			// }
			endpoint =
				"https://script.google.com/macros/s/AKfycbwfmcyBq79Foiipq9GpPgS4EuPUvocnbd5GYrLZvrTF30ZFOTOkKFoL64f-T01CDW38Og/exec";
		}

		const response = await fetch(endpoint, {
			redirect: "follow",
			method: "POST",
			headers: {
				"Content-Type": "text/plain;charset=UTF-8",
			},
			body: JSON.stringify(payload),
		});
		const text = await response.text();
		if (response.ok) {
			try {
				return JSON.parse(text);
			} catch (e) {
				return text;
			}
		} else {
			console.error("Server responded with an error:", text);
		}
	} catch (error) {
		console.error("Error submitting logs:", error);
	}
}

// COMMENTED OUT: Saves ~100ms per call
// async function getPartifyLocation() {
// 	const ip = await getIpAddress();

// 	let partifyLocation = "";
// 	if (ip === "107.5.210.48") {
// 		partifyLocation = "Warren";
// 	} else if (
// 		ip === "98.209.126.177" ||
// 		ip === "68.34.24.103" ||
// 		ip === "68.34.24.104"
// 	) {
// 		partifyLocation = "Fraser";
// 	}

// 	return partifyLocation;
// }

function toggleProductDoesNotFitMsg(doesNotFit) {
	let vehicleList = document.querySelector(".vehicle-list");
	if (vehicleList) {
		let failToFitMsg = document.querySelector(
			".product-does-not-fit-garage-msg",
		);
		if (!failToFitMsg) {
			failToFitMsg = document.createElement("div");
			failToFitMsg.className = "product-does-not-fit-garage-msg";
			failToFitMsg.textContent = "";
			vehicleList.parentNode.insertBefore(failToFitMsg, vehicleList);
		}
		if (!doesNotFit) {
			failToFitMsg.style.display = "none";
		} else {
			failToFitMsg.style.display = "block";
		}
	}
}

function toggleGaragePopup() {
	// debugger;
	// Always select the popup regardless of hidden state
	let garageEasyPopup = document.querySelector(".garage-easyYMM-popup");
	if (!garageEasyPopup) {
		console.error("No .garage-easyYMM-popup element found!");
		return;
	}

	const garagePopupStyleId = "garage-popup-style";
	const scrollY = window.scrollY;

	garageEasyPopup.classList.toggle("hidden");

	// Opens garage
	if (!garageEasyPopup.classList.contains("hidden")) {
		document.body.dataset.scrollY = scrollY;

		if (!document.getElementById(garagePopupStyleId)) {
			const style = document.createElement("style");
			style.id = garagePopupStyleId;
			style.textContent = `
            body.no-scroll-garage-popup {
              position: fixed;
              top: -${scrollY}px;
              left: 0;
              width: 100%;
              height: 100%;
              overflow: hidden !important;
            }
          `;
			document.head.appendChild(style);
		}
		document.body.classList.add("no-scroll-garage-popup");
		hideShopifyChat();
	} else {
		// Closes garage
		const savedScrollY = parseInt(document.body.dataset.scrollY || "0", 10);
		delete document.body.dataset.scrollY;

		let terms = JSON.parse(localStorage.getItem("searchTerms")) || [];
		if (
			document.getElementById("add-vehicle-btn").style.display === "none" &&
			terms.length > 0
		) {
			const garagePopupContainer = document.querySelector(
				".garage-easyYMM-popup-container",
			);
			garagePopupContainer.querySelector(".easyYMM").style.display = "none";
			document.getElementById("vin-to-collection-placeholder").style.display =
				"none";
			document.getElementById("vin-to-collection-or").style.display = "none";
			if (garagePopupContainer) {
				garagePopupContainer.classList.remove("no-bottom-radius");
			}
			document.getElementById("add-vehicle-btn").style.display = "flex";
			document.querySelector(".garage-block").style.display = "block";
			document.querySelector(".select-your-vehicle-header").style.display =
				"none";
		}
		const style = document.getElementById(garagePopupStyleId);
		if (style) {
			style.remove();
		}

		window.scrollTo(0, savedScrollY);
		showShopifyChat();

		// Close edit car state if open
		if (window.editCarState) {
			window.editCarState.open = false;
		}
	}
}

function generateCollectionHandle(vehicleData) {
	let collectionHandle = vehicleData
		.toLowerCase()
		.replace(/[./]/g, "-")
		.replace(/\s+/g, "-")
		.replace(/[^\w\-]+/g, "")
		.replace(/--+/g, "-")
		.replace(/-+$/, "");

	return collectionHandle;
}

// Simplified showMismatchModal - now uses pre-validated data
function showMismatchModal(differences, vin, licensePlate, paintCode) {
	const modalContainer = document.querySelector(".mismatch-modal-container");
	const modalOverlay = document.querySelector(".mismatch-modal-overlay");
	const differencesContainer = document.getElementById("mismatch-details");

	if (!modalContainer || !modalOverlay || !differencesContainer) {
		console.error("Modal elements not found");
		return;
	}

	// Get validation results from global scope (set in normalizeModelAndCompare)
	const validationResult = window.EASYSEARCH_VALIDATION_RESULT || {
		canResolveYMM: false,
		canResolveComplete: false,
	};

	// Build comparison table
	const user = getSearchTerms()[0] || {};
	const decoded = decodedVehicleData || {};
	decoded.paintCode = paintCode;

	// If User selected from select Paint Code, update paintCode here
	const combinedVariantSelect = document.getElementById("variant-selector");
	const selectedOption =
		combinedVariantSelect.querySelector("option:checked") ||
		combinedVariantSelect.querySelector("option[selected]");
	let variantTitle = null;
	if (selectedOption) {
		variantTitle = selectedOption.getAttribute("data-variant-title");
		user.paintCode = variantTitle;
	}
	const FIELDS = [
		["year", "Year"],
		["make", "Make"],
		["model", "Model"],
		// ["submodel", "Submodel"],
		// ["engine", "Engine"],
		// ["paintCode", "PaintCode"], // could bring it back when needed
	];

	function fmt(v) {
		if (v == null) return "—";
		const s = String(v).trim();
		return s.length ? s : "—";
	}

	const toKey = (v) =>
		String(v ?? "")
			.toLowerCase()
			.replace(/[^a-z0-9]/g, "")
			.trim();

	const rows = FIELDS.map(([key, label]) => {
		const u = key === "year" ? (user.year ?? user.year?.toString()) : user[key];
		const d =
			key === "year"
				? (decoded.year ?? decoded.year?.toString())
				: decoded[key];
		const isDiff = toKey(u) !== toKey(d);

		return `
            <tr>
                <th class="mismatch-field" style="font-weight: bold;">${label}</th>
                <td><span class="${
									isDiff ? "mismatch-diff" : "mismatch-same"
								}">${fmt(u)}</span></td>
                <td><span class="${
									isDiff ? "mismatch-diff" : "mismatch-same"
								}">${fmt(d)}</span></td>
            </tr>
        `;
	}).join("");

	const diffHTML = `
        <div>
            <table class="mismatch-table">
                <thead>
                    <tr>
                        <th></th>
                        <th style="font-weight: bold;">Your selection</th>
                        <th style="font-weight: bold;">Decoded</th>
                    </tr>
                </thead>
                <tbody>${rows}</tbody>
            </table>
        </div>
    `;

	differencesContainer.innerHTML = diffHTML;

	// remember which radio was selected before the mismatch modal opens
	const wasVinChecked = document.getElementById(
		"checkbox-get-paint-code-with-vin",
	)?.checked;
	const wasLicenseChecked = document.getElementById(
		"checkbox-get-paint-code-with-license",
	)?.checked;

	openQuestionModal({
		scrollLockClass: "no-scroll",
		overlaySelector: ".mismatch-modal-overlay",
		modalSelector: ".mismatch-modal-container",
	});
	// restore paint-option radio selection right after the modal opens
	const vinRadio = document.getElementById("checkbox-get-paint-code-with-vin");
	const licRadio = document.getElementById(
		"checkbox-get-paint-code-with-license",
	);
	if (vinRadio && wasVinChecked) vinRadio.checked = true;
	if (licRadio && wasLicenseChecked) licRadio.checked = true;
	if (typeof syncFakeCircle === "function") syncFakeCircle();
	// setup the calling location
	const isCallFromVinInput = vinRadio.checked === true ? true : false;
	const isCallFromSelectPaintCode =
		document.getElementById("checkbox-select-paint-option") &&
		document.getElementById("checkbox-select-paint-option").checked === true
			? true
			: false;
	// Setup buttons
	const closeBtn = document.getElementById("mismatch-back-btn");
	const proceedBtn = document.getElementById(
		"mismatch-close-and-use-decoded-btn",
	);

	closeBtn.replaceWith(closeBtn.cloneNode(true));
	proceedBtn.replaceWith(proceedBtn.cloneNode(true));

	const newCloseBtn = document.getElementById("mismatch-back-btn");
	const newProceedBtn = document.getElementById(
		"mismatch-close-and-use-decoded-btn",
	);

	// Check if major fields differ
	const biMisMatch = ["make", "model"].every(
		(k) => differences && differences[k],
	);
	// check if all three fields differ
	const triMisMatch = ["year", "make", "model"].every(
		(k) => differences && differences[k],
	);

	window.MISMATCH_ALL_THREE = !!triMisMatch;

	// Update button states based on pre-validated data
	updateButtonStates(
		newProceedBtn,
		newCloseBtn,
		biMisMatch,
		validationResult.canResolveYMM,
	);

	// Setup event listeners
	setupModalEventListeners(
		newProceedBtn,
		newCloseBtn,
		vin,
		licensePlate,
		paintCode,
		biMisMatch,
		validationResult,
		isCallFromVinInput,
		isCallFromSelectPaintCode,
	);
}

function updateButtonStates(proceedBtn, closeBtn, biMisMatch, canResolveYMM) {
	// Before resorting to match by vin, we must ensure that it exists in the first place
	const hiddenVinOption = document.querySelector(".hidden-vin-option");
	if (biMisMatch && !canResolveYMM) {
		proceedBtn.style.display = "none";
		proceedBtn.disabled = true;
		closeBtn.textContent = getAttr(
			closeBtn,
			"data-use-selection",
			"Go back and use your selection",
		);
	} else if (biMisMatch && canResolveYMM) {
		proceedBtn.style.display = "block";
		proceedBtn.disabled = false;
		proceedBtn.textContent = getAttr(
			proceedBtn,
			"data-use-decoded",
			"Continue with decoded vehicle",
		);
	} else if (!biMisMatch && !canResolveYMM && hiddenVinOption) {
		proceedBtn.style.display = "block";
		proceedBtn.disabled = false;
		proceedBtn.textContent = getAttr(
			proceedBtn,
			"data-use-match_paint_by_vin",
			"Continue with Match Paint by VIN",
		);
	} else {
		proceedBtn.style.display = "block";
		proceedBtn.disabled = false;
		proceedBtn.textContent = getAttr(
			proceedBtn,
			"data-use-decoded",
			"Continue with decoded vehicle",
		);
	}
}

function setupModalEventListeners(
	proceedBtn,
	closeBtn,
	vin,
	licensePlate,
	paintCode,
	biMisMatch,
	validationResult,
	isCallFromVinInput,
	isCallFromSelectPaintCode,
) {
	proceedBtn.addEventListener("click", async (e) => {
		e.preventDefault();
		e.stopPropagation();

		if (biMisMatch && !validationResult.canResolveYMM) {
			console.warn("Cannot proceed: All fields differ and can't be resolved");
			return;
		}

		const activatedPendingVehicle =
			await activatePendingMismatchDecodedVehicle(vin);
		if (activatedPendingVehicle) {
			closeMismatchModal();
			return;
		}

		if (!validationResult.canResolveYMM) {
			// Use Match Paint by VIN flow
			handleMatchPaintByVinFlow(vin);
			closeMismatchModal();
			return;
		}

		// Proceed with decoded vehicle
		await handleDecodedVehicleFlow(vin, paintCode, validationResult);
	});

	closeBtn.addEventListener("click", (e) => {
		e.preventDefault();
		e.stopPropagation();
		discardPendingMismatchDecodedVehicle();
		closeMismatchModal();

		failedVinDecode = false;

		/// isCallFromVinInput is passed from main showMismatch function
		clearAndHidePaintOptionSelect(
			e,
			isCallFromVinInput,
			isCallFromSelectPaintCode,
		);

		// If call is from select paint code, re-evaluate add to cart button state -- could either enable or disable
		// If call is from vin input, then must ensure the add to cart button is disabled
		if (isCallFromSelectPaintCode) {
			evaluateAddToCartButtonState();
		} else {
			const addToCartBtn = document.querySelector(".add-to-cart");
			if (addToCartBtn) addToCartBtn.disabled = true;
			if (isCallFromVinInput) window.PartifyPdpVinState?.beginEdit?.();
		}
	});
}

async function handleDecodedVehicleFlow(vin, paintCode, validationResult) {
	isMismatch = true;
	const newSubmodel = validationResult.submodel
		? validationResult.submodel
		: "";
	const newEngine =
		newSubmodel && validationResult.engine ? validationResult.engine : "";

	const newYmm = [
		validationResult.year.toString(),
		validationResult.make,
		validationResult.model,
		newSubmodel,
		newEngine,
	]
		.filter(Boolean)
		.join(" ");

	const terms = getSearchTerms();
	const activeVehicle = terms[0] || {};
	// check if the make is troublesome make
	if (troublesomeMakesGlobal.includes(validationResult.make.toLowerCase())) {
		paintCode = "";
	}
	updateSearchTerms({
		ymm: newYmm,
		year: validationResult.year.toString(),
		make: validationResult.make,
		model: validationResult.model,
		submodel: newSubmodel,
		engine: newEngine,
		bodyType: activeVehicle.bodyType,
		decodedBodyTypes: activeVehicle.decodedBodyTypes,
		vin,
		paintCode,
		decodedByBumper: validationResult.decodedByBumper,
		lastCollectionImage: activeVehicle.lastCollectionImage,
		troublesome_format: activeVehicle.troublesome_format,
		troublesome_description: activeVehicle.troublesome_description,
	});

	const searchTerms = getSearchTerms();
	if (window.GarageLogic && window.GarageLogic.updateGaragePopup) {
		window.GarageLogic.updateGaragePopup(searchTerms);
	}

	// Update fitment and navigate
	if (searchTerms.length > 0) {
		const newFirstVehicle = searchTerms[0];
		const fields = [
			newFirstVehicle.year.toString(),
			newFirstVehicle.make,
			newFirstVehicle.model,
			newSubmodel,
			newEngine,
		];
		const fitmentValue = fields
			.filter((f) => f && f?.trim())
			.map((f) => f?.trim() + "easysearch-preselect-delimiter")
			.join("");
		const expires = Date.now() + 365 * 24 * 60 * 60 * 1000;
		setFitmentObject({
			value: fitmentValue,
			expires: expires.toString(),
		});
	}

	const isProductPage = ["/products/", "/product/", "/shop/"].some((path) =>
		window.location.pathname.includes(path),
	);
	if (isProductPage) {
		if (typeof window.fbtRefresh === "function") {
			window.fbtRefresh();
		}
		isMismatch = false;
		return;
	}

	// Navigate to collection
	const partType = document
		.getElementById("product-title")
		?.getAttribute("product-type");
	const vehicle = generateCollectionHandle(searchTerms[0].ymm);

	let baseUrl =
		globalLocale === "en"
			? `/collections/${vehicle}?filter.p.product_type=${partType}`
			: `/es/collections/${vehicle}?filter.p.product_type=${partType}`;

	try {
		const response = await fetch(baseUrl);
		const html = await response.text();
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const productGrid = doc.querySelector(".productgrid--items");

		if (productGrid && productGrid.children.length > 0) {
			window.location.href = baseUrl;
		} else {
			baseUrl =
				globalLocale === "en"
					? `/collections/${vehicle}`
					: `/es/collections/${vehicle}`;
			window.location.href = baseUrl;
		}
	} catch (e) {
		console.error(e);
		alert("Could not check products. Please try again.");
	}

	isMismatch = false;
}

function handleMatchPaintByVinFlow(vin) {
	window.MISMATCH_UNRESOLVED = true;

	const terms = getSearchTerms();
	if (terms.length) {
		const t0 = terms[0];
		t0.vin = t0.vin && t0.vin.length === 17 ? t0.vin : vin || "";
		setSearchTerms(terms);
		window.GarageLogic.updateGaragePopup(getSearchTerms());
	}

	const matchPaintByVinPrice = handleAppendMatchByVin(vin, true);
	handleAutoSelectMatchByVin();

	const localPaintCodeAppContainer = document.getElementById(
		"paintcode-app-container",
	);
	if (localPaintCodeAppContainer?.classList.contains("show")) {
		localPaintCodeAppContainer.classList.remove("show");
	}

	if (!matchPaintByVinPrice) {
		resortToForceSelectCode();
	} else {
		matchingPaintByVinVisible = true;
		storedOptionLabel.style.display = "block";
		const checkboxUseStoredCode = document.getElementById(
			"checkbox-paint-code-local-storage",
		);
		checkboxUseStoredCode.checked = true;
		checkboxStoredCodeText.innerHTML = matchPaintByVinTranslationKey;
		storedOptionLabel.classList.add("match-paint-by-vin-option");

		const paintCodeWrapperGlobal = document.querySelector(
			".paint-code-wrapper",
		);
		if (paintCodeWrapperGlobal?.classList.contains("show")) {
			paintCodeWrapperGlobal.classList.remove("show");
		}

		// const vinSubmitBtn = document.querySelector(".vin-decoder-submit-btn");
		const vinSubmitBtn = document.querySelector(
			'.vin-decoder-submit-btn:not([style*="display: none"])',
		);
		if (vinSubmitBtn) vinSubmitBtn.disabled = true;

		const paintOptionsVinLicense = document.querySelector(
			".paint-options-vin-license",
		);
		if (paintOptionsVinLicense) paintOptionsVinLicense.style.display = "none";

		// const vinWrapper = document.getElementById(
		// 	"vin-decoder-textbox-for-verification-wrapper-byvin"
		// );
		const vinWrapper = document.querySelector(
			'[id^="vin-decoder-textbox-for-verification-wrapper"]:not([style*="display: none"])',
		);
		if (vinWrapper) vinWrapper.style.display = "none";
	}
}

function closeMismatchModal() {
	closeQuestionModal(
		"no-scroll",
		window.scrollY,
		".mismatch-modal-container",
		".mismatch-modal-content",
		".mismatch-modal-overlay",
	);
	isMismatch = false;
}

function getAttr(node, name, fallback = "") {
	return node?.getAttribute(name) || fallback;
}

const VEHICLE_COLLECTION_RECOVERY_KEY =
	"partify:vehicle-collection-recovery:v1";
const VEHICLE_COLLECTION_RECOVERY_HISTORY_KEY = "partifyVehicleRecovery";
const VEHICLE_COLLECTION_RECOVERY_VERSION = 1;
const VEHICLE_COLLECTION_RECOVERY_TTL_MS = 2 * 60 * 1000;
const VEHICLE_COLLECTION_RECOVERY_FIELD_MAX_LENGTH = 80;
const trustedVehicleRecoveryResults = new WeakSet();

function normalizeVehicleRecoveryField(value) {
	if (typeof value !== "string") return "";
	const normalized = value.trim();
	if (
		!normalized ||
		normalized.length > VEHICLE_COLLECTION_RECOVERY_FIELD_MAX_LENGTH ||
		/[\u0000-\u001f\u007f]/.test(normalized)
	) {
		return "";
	}
	return normalized;
}

function getVehicleCollectionRecoveryPath(destination) {
	try {
		const parsed = new URL(destination, window.location.origin);
		if (parsed.origin !== window.location.origin) return "";

		const configuredRoot = toStringValue(window.Shopify?.routes?.root);
		const normalizedRoot = configuredRoot.replace(/^\/+|\/+$/g, "");
		const routeRoot = normalizedRoot ? `/${normalizedRoot}/` : "/";
		const collectionsPrefix = `${routeRoot}collections/`;
		if (!parsed.pathname.startsWith(collectionsPrefix)) return "";

		const handle = parsed.pathname.slice(collectionsPrefix.length);
		if (!handle || handle.includes("/") || /%2f/i.test(handle)) return "";
		return parsed.pathname;
	} catch (error) {
		return "";
	}
}

function hasOnlyVehicleRecoveryKeys(value, allowedKeys) {
	return Object.keys(value).every((key) => allowedKeys.includes(key));
}

function normalizeVehicleRecoveryVin(value) {
	if (typeof value !== "string") return "";
	const vin = value.trim().toUpperCase();
	return /^[A-HJ-NPR-Z0-9]{17}$/.test(vin) ? vin : "";
}

function normalizeVehicleRecoveryVehicle(vehicle) {
	if (
		!vehicle ||
		typeof vehicle !== "object" ||
		Array.isArray(vehicle) ||
		!hasOnlyVehicleRecoveryKeys(vehicle, ["year", "make", "model", "vin"])
	) {
		return null;
	}

	const year = normalizeVehicleRecoveryField(vehicle.year);
	const make = normalizeVehicleRecoveryField(vehicle.make);
	const model = normalizeVehicleRecoveryField(vehicle.model);
	if (!/^\d{4}$/.test(year) || !make || !model) return null;

	const recoveryVehicle = { year, make, model };
	if (Object.prototype.hasOwnProperty.call(vehicle, "vin")) {
		const vin = normalizeVehicleRecoveryVin(vehicle.vin);
		if (!vin) return null;
		recoveryVehicle.vin = vin;
	}
	return recoveryVehicle;
}

function normalizeVehicleRecoveryResult(expectedPath, vehicle, currentPath) {
	if (
		typeof expectedPath !== "string" ||
		expectedPath !== currentPath ||
		getVehicleCollectionRecoveryPath(expectedPath) !== expectedPath
	) {
		return null;
	}

	const recoveryVehicle = normalizeVehicleRecoveryVehicle(vehicle);
	return recoveryVehicle ? { expectedPath, vehicle: recoveryVehicle } : null;
}

function recordVehicleCollectionRecoveryContext(destination, vehicle) {
	try {
		window.sessionStorage.removeItem(VEHICLE_COLLECTION_RECOVERY_KEY);

		if (!vehicle || typeof vehicle !== "object" || Array.isArray(vehicle)) {
			return;
		}

		const expectedPath = getVehicleCollectionRecoveryPath(destination);
		const year = normalizeVehicleRecoveryField(toStringValue(vehicle.year));
		const make = normalizeVehicleRecoveryField(toStringValue(vehicle.make));
		const model = normalizeVehicleRecoveryField(toStringValue(vehicle.model));
		if (!expectedPath || !/^\d{4}$/.test(year) || !make || !model) return;

		const recoveryVehicle = { year, make, model };
		const vin = normalizeVehicleRecoveryVin(toStringValue(vehicle.vin));
		if (vin) recoveryVehicle.vin = vin;

		window.sessionStorage.setItem(
			VEHICLE_COLLECTION_RECOVERY_KEY,
			JSON.stringify({
				version: VEHICLE_COLLECTION_RECOVERY_VERSION,
				createdAt: Date.now(),
				expectedPath,
				vehicle: recoveryVehicle,
			}),
		);
	} catch (error) {
		// Recovery context is best-effort and must never block navigation.
	}
}

function consumeVehicleCollectionRecoveryContext(currentPath) {
	let serializedContext = "";
	try {
		serializedContext = window.sessionStorage.getItem(
			VEHICLE_COLLECTION_RECOVERY_KEY,
		);
		if (serializedContext === null) return null;
		window.sessionStorage.removeItem(VEHICLE_COLLECTION_RECOVERY_KEY);
	} catch (error) {
		return null;
	}

	try {
		const context = JSON.parse(serializedContext);
		if (
			!context ||
			typeof context !== "object" ||
			Array.isArray(context) ||
			!hasOnlyVehicleRecoveryKeys(context, [
				"version",
				"createdAt",
				"expectedPath",
				"vehicle",
			]) ||
			context.version !== VEHICLE_COLLECTION_RECOVERY_VERSION ||
			!Number.isFinite(context.createdAt)
		) {
			return null;
		}

		const age = Date.now() - context.createdAt;
		if (age < 0 || age > VEHICLE_COLLECTION_RECOVERY_TTL_MS) return null;

		const normalized = normalizeVehicleRecoveryResult(
			context.expectedPath,
			context.vehicle,
			currentPath,
		);
		if (normalized) trustedVehicleRecoveryResults.add(normalized);
		return normalized;
	} catch (error) {
		return null;
	}
}

function restoreVehicleCollectionRecoveryHistory(currentPath) {
	try {
		const state = window.history.state;
		if (!state || typeof state !== "object" || Array.isArray(state)) return null;

		const context = state[VEHICLE_COLLECTION_RECOVERY_HISTORY_KEY];
		if (
			!context ||
			typeof context !== "object" ||
			Array.isArray(context) ||
			!hasOnlyVehicleRecoveryKeys(context, [
				"version",
				"expectedPath",
				"vehicle",
			]) ||
			context.version !== VEHICLE_COLLECTION_RECOVERY_VERSION
		) {
			return null;
		}

		return normalizeVehicleRecoveryResult(
			context.expectedPath,
			context.vehicle,
			currentPath,
		);
	} catch (error) {
		return null;
	}
}

function persistVehicleCollectionRecoveryHistory(context) {
	try {
		if (!trustedVehicleRecoveryResults.has(context)) return false;
		trustedVehicleRecoveryResults.delete(context);
		if (
			!context ||
			typeof context !== "object" ||
			Array.isArray(context) ||
			!hasOnlyVehicleRecoveryKeys(context, ["expectedPath", "vehicle"])
		) {
			return false;
		}

		const normalized = normalizeVehicleRecoveryResult(
			context.expectedPath,
			context.vehicle,
			window.location.pathname,
		);
		if (!normalized) return false;

		const currentState = window.history.state;
		const preservedState =
			currentState && typeof currentState === "object" && !Array.isArray(currentState)
				? { ...currentState }
				: {};
		preservedState[VEHICLE_COLLECTION_RECOVERY_HISTORY_KEY] = {
			version: VEHICLE_COLLECTION_RECOVERY_VERSION,
			expectedPath: normalized.expectedPath,
			vehicle: normalized.vehicle,
		};
		window.history.replaceState(preservedState, "");
		return true;
	} catch (error) {
		return false;
	}
}

window.PartifyVehicleCollectionRecovery = Object.freeze({
	record: recordVehicleCollectionRecoveryContext,
	consume: consumeVehicleCollectionRecoveryContext,
	persistToHistory: persistVehicleCollectionRecoveryHistory,
	restoreFromHistory: restoreVehicleCollectionRecoveryHistory,
});
try {
	window.dispatchEvent(
		new Event("partify:vehicle-collection-recovery-ready"),
	);
} catch (error) {
	// The 404 consumer retains its DOM-ready fail-closed fallback.
}

async function checkAndNavigate(url, alterUrl) {
	try {
		const response = await fetch(url, {
			method: "HEAD", // Use HEAD to only request headers, saving bandwidth
		});

		if (response.ok) {
			// Check if the status code is 200-299
			window.location.href = url; // Page exists, navigate the user
		} else {
			console.error(`Page returned status: ${response.status}`);
			window.location.href = alterUrl; // Page not found (404, 500, etc.), redirect to fallback
		}
	} catch (error) {
		// This catches network errors (e.g., DNS failure, server offline)
		console.error("Network or Fetch error:", error);
		window.location.href = "/";
	}
}

async function navigateToGarageVehicleCollection(vehicle, navigationOptions) {
	if (!vehicle || typeof vehicle !== "object") return false;
	const shouldNavigate =
		navigationOptions && typeof navigationOptions.shouldNavigate === "function"
			? navigationOptions.shouldNavigate
			: null;

	function assignCollectionUrl(url) {
		try {
			if (shouldNavigate && shouldNavigate(url) !== true) return false;
		} catch (_) {
			return false;
		}
		recordVehicleCollectionRecoveryContext(url, vehicle);
		window.location.assign(url);
		return true;
	}

	function buildCollectionUrl(ymm) {
		const value = toStringValue(ymm);
		if (!value) return "";
		const configuredRoot = toStringValue(window.Shopify?.routes?.root);
		const normalizedRoot = configuredRoot.replace(/^\/+|\/+$/g, "");
		const routeRoot = normalizedRoot ? `/${normalizedRoot}/` : "/";
		const handle = generateCollectionHandle(value);
		return handle ? `${routeRoot}collections/${handle}` : "";
	}

	const year = toStringValue(vehicle.year);
	const make = toStringValue(vehicle.make);
	const model = toStringValue(vehicle.model);
	const broadYmm = year && make && model ? [year, make, model].join(" ") : "";
	const primaryYmm =
		toStringValue(vehicle.fitmentYmm) || toStringValue(vehicle.ymm) || broadYmm;
	const primaryUrl = buildCollectionUrl(primaryYmm);
	if (!primaryUrl) return false;
	const fallbackUrl = buildCollectionUrl(broadYmm);

	if (!fallbackUrl || primaryUrl === fallbackUrl) {
		return assignCollectionUrl(primaryUrl);
	}

	try {
		const response = await fetch(primaryUrl, {
			method: "HEAD",
			credentials: "same-origin",
		});
		const destinationUrl = response.ok ? primaryUrl : fallbackUrl;
		if (!assignCollectionUrl(destinationUrl)) return false;
		return response.ok;
	} catch (error) {
		console.error("Collection lookup error:", error);
		if (!assignCollectionUrl(fallbackUrl)) return false;
		return false;
	}
}

// add once (tiny promise cache)
let __vinLookupCache, __vinLookupLoading;
function loadVinLookup() {
	if (__vinLookupCache) return Promise.resolve(__vinLookupCache);
	if (Array.isArray(window.VIN_LOOKUP_DATA)) {
		__vinLookupCache = window.VIN_LOOKUP_DATA;
		return Promise.resolve(__vinLookupCache);
	}
	if (!__vinLookupLoading) {
		const url = window.VIN_LOOKUP_URL || "/assets/vinLookupToAces.json";
		__vinLookupLoading = fetch(url)
			.then((r) => {
				if (!r.ok) throw new Error(r.status);
				return r.json();
			})
			.then((json) => (__vinLookupCache = json))
			.finally(() => {
				__vinLookupLoading = null;
			});
	}
	return __vinLookupLoading;
}

function normalizeFullYmmLookupKey(value) {
	return String(value || "")
		.toLowerCase()
		.replace(/[^a-z0-9]/g, "")
		.trim();
}

function isFullYmmLookupValue(value) {
	const parts = String(value || "")
		.trim()
		.split(/\s+/)
		.filter(Boolean);
	return parts.length >= 3 && /^\d{4}$/.test(parts[0]);
}

function buildIoniqEasySearchSubmodel(make, model, submodel) {
	const normalizedMake = String(make || "")
		.trim()
		.toLowerCase();
	const normalizedModel = String(model || "")
		.trim()
		.toLowerCase();
	if (normalizedMake !== "hyundai" || normalizedModel.indexOf("ioniq") !== 0) {
		return "";
	}

	let powertrain = "";
	if (/plug[\s-]*in\s+hybrid/.test(normalizedModel)) {
		powertrain = "Plug-In Hybrid";
	} else if (/\bev\b/.test(normalizedModel) || /electric/.test(normalizedModel)) {
		powertrain = "Electric";
	} else if (/hybrid/.test(normalizedModel)) {
		powertrain = "Hybrid";
	}
	if (!powertrain) return "";

	const trim = toStringValue(submodel);
	if (!trim) return powertrain;
	if (trim.toLowerCase().indexOf(powertrain.toLowerCase()) === 0) return trim;
	return `${powertrain} ${trim}`;
}

async function resolveFullVinLookupMapping(decodedData) {
	const year = toStringValue(decodedData && decodedData.year);
	const make = toStringValue(decodedData && decodedData.make);
	const model = toStringValue(decodedData && decodedData.model);
	const sourceYmm = [year, make, model].filter(Boolean).join(" ");

	if (!isFullYmmLookupValue(sourceYmm)) return null;

	try {
		const rows = await loadVinLookup();
		if (!Array.isArray(rows)) return null;

		const sourceKey = normalizeFullYmmLookupKey(sourceYmm);
		const hit = rows.find((row) => {
			if (!row || typeof row !== "object") return false;
			const source = toStringValue(row.Model);
			const target = toStringValue(row["Easysearch Model"]);
			if (!source || !target) return false;
			if (!isFullYmmLookupValue(source) || !isFullYmmLookupValue(target)) {
				return false;
			}
			return normalizeFullYmmLookupKey(source) === sourceKey;
		});

		if (!hit) return null;

		const fitmentYmm = toStringValue(hit["Easysearch Model"]);
		return fitmentYmm ? { sourceYmm, fitmentYmm } : null;
	} catch (_) {
		return null;
	}
}

// add once for submodel comparison
let __submodelMapCache, __submodelMapLoading;
function loadSubmodelMap() {
	if (__submodelMapCache) return Promise.resolve(__submodelMapCache);
	if (!__submodelMapLoading) {
		const url =
			window.SUBMODEL_MAP_URL || "/assets/subaruForesterSubmodelScrape.json";
		__submodelMapLoading = fetch(url)
			.then((r) => {
				if (!r.ok) throw new Error(r.status);
				return r.json();
			})
			.then((json) => (__submodelMapCache = json))
			.finally(() => {
				__submodelMapLoading = null;
			});
	}
	return __submodelMapLoading;
}

async function resolveMappedSubmodelForAliasedModel(
	make,
	sourceModel,
	normalizedModel,
	sourceSubmodel,
	isValidDestination,
) {
	const makeValue = toStringValue(make);
	const sourceModelValue = toStringValue(sourceModel);
	const normalizedModelValue = toStringValue(normalizedModel);
	const sourceSubmodelValue = toStringValue(sourceSubmodel);
	if (
		!makeValue ||
		!sourceModelValue ||
		!normalizedModelValue ||
		!sourceSubmodelValue ||
		sourceModelValue.toLowerCase() === normalizedModelValue.toLowerCase()
	) {
		return { mappingFound: false, submodel: "" };
	}

	try {
		const submodelMap = await loadSubmodelMap();
		const mappedSubmodels =
			submodelMap[makeValue]?.[sourceModelValue]?.[sourceSubmodelValue]?.[0]
				?.Easysearch?.Submodel || [];
		if (!mappedSubmodels.length) {
			return { mappingFound: false, submodel: "" };
		}

		for (const mappedSubmodel of mappedSubmodels) {
			if (await isValidDestination(mappedSubmodel)) {
				return { mappingFound: true, submodel: mappedSubmodel };
			}
		}

		return { mappingFound: true, submodel: "" };
	} catch (_) {
		return { mappingFound: false, submodel: "" };
	}
}

// Per-year YMM JSON cache — same pattern as loadVinLookup/loadSubmodelMap
const __ymmCache = {};
const __ymmLoading = {};
function loadYmmData(year) {
	if (__ymmCache[year]) {
		return Promise.resolve(__ymmCache[year]);
	}
	if (!__ymmLoading[year]) {
		const url = window.__PARTIFY_YMM_BASE__ + "ymm-" + year + ".json";
		__ymmLoading[year] = fetch(url)
			.then((r) => {
				if (!r.ok)
					return fetch(url).then((r2) => {
						if (!r2.ok) throw new Error(r2.status);
						return r2;
					});
				return r;
			})
			.then((r) => r.json())
			.then((json) => {
				return (__ymmCache[year] = json);
			})
			.finally(() => {
				__ymmLoading[year] = null;
			});
	}
	return __ymmLoading[year];
}

// Native YMM validation — validates decoded vehicle against per-year JSON files
// instead of polling EasySearch dropdown DOM elements.
async function validateVehicleDataNative(decodedData) {
	const { year, make, model, submodel, engine } = decodedData;
	const resolvedFields = {
		year: null,
		make: null,
		model: null,
		submodel: null,
		engine: null,
		canResolveYMM: false,
		canResolveComplete: false,
	};

	const yStr = (year || "").toString().trim();
	if (!yStr) return resolvedFields;

	const base = window.__PARTIFY_YMM_BASE__;
	if (!base) return resolvedFields;

	try {
		const data = await loadYmmData(yStr).catch((e) => {
			console.error(
				"wool validateVehicleDataNative: YMM fetch failed after retry, returning canResolveYMM=false",
				e,
			);
			return null;
		});
		if (!data) return resolvedFields;
		const vehicles = data.vehicles || [];

		// Attempt direct model match first, then vinLookup fallback
		let normalizedModel = (model || "").trim();
		const lMake = (make || "").toLowerCase().trim();
		const lModel = normalizedModel.toLowerCase();

		let modelMatch = vehicles.some(
			(v) => v.make.toLowerCase() === lMake && v.model.toLowerCase() === lModel,
		);

		if (!modelMatch) {
			try {
				const vinLookupData = await loadVinLookup();
				const hit = vinLookupData.find(
					(x) =>
						String(x.Model || "")
							.toLowerCase()
							.replace(/[^a-z0-9]/g, "")
							.trim() === lModel.replace(/[^a-z0-9]/g, ""),
				);
				if (hit && hit["Easysearch Model"]) {
					normalizedModel = hit["Easysearch Model"];
					modelMatch = vehicles.some(
						(v) =>
							v.make.toLowerCase() === lMake &&
							v.model.toLowerCase() === normalizedModel.toLowerCase(),
					);
				}
			} catch (_) {}
		}

		resolvedFields.year = yStr;
		resolvedFields.make = make;
		resolvedFields.model = modelMatch ? normalizedModel : null;

		if (!modelMatch) {
			window.MISMATCH_JSON_LOOKUP_MISS = true;
			return resolvedFields;
		}
		resolvedFields.canResolveYMM = true;

		// Resolve submodel
		let normalizedSubmodel = (submodel || "").trim();
		const preferredIoniqSubmodel = buildIoniqEasySearchSubmodel(
			make,
			model,
			normalizedSubmodel,
		);
		if (normalizedSubmodel || preferredIoniqSubmodel) {
			const matchingVehicles = vehicles.filter(
				(v) =>
					v.make.toLowerCase() === lMake &&
					v.model.toLowerCase() === normalizedModel.toLowerCase() &&
					(v.submodel || "").trim().length > 0,
			);
			const preferredPairAwareSubmodel =
				await resolveMappedSubmodelForAliasedModel(
					make,
					model,
					normalizedModel,
					submodel,
					(mappedSubmodel) =>
						matchingVehicles.some(
							(v) =>
								(v.submodel || "").toLowerCase() ===
								mappedSubmodel.toLowerCase(),
						),
				);
			if (preferredPairAwareSubmodel.submodel) {
				normalizedSubmodel = preferredPairAwareSubmodel.submodel;
			} else if (preferredPairAwareSubmodel.mappingFound) {
				normalizedSubmodel = "";
			}
			// Direct match only — no suffix matching, to match live EasySearch behavior
			const preferredSubMatch =
				preferredIoniqSubmodel &&
				matchingVehicles.some(
					(v) =>
						(v.submodel || "").toLowerCase() ===
						preferredIoniqSubmodel.toLowerCase(),
				);
			if (preferredSubMatch) {
				normalizedSubmodel = preferredIoniqSubmodel;
			}
			const lSub = normalizedSubmodel.toLowerCase();
			const subMatch = matchingVehicles.some(
				(v) => (v.submodel || "").toLowerCase() === lSub,
			);
			if (subMatch) {
				resolvedFields.submodel = normalizedSubmodel;
			} else if (!preferredPairAwareSubmodel.mappingFound) {
				// Try submodelMap fallback
				try {
					const submodelMap = await loadSubmodelMap();
					const makeData = submodelMap[make];
					if (
						makeData &&
						makeData[normalizedModel] &&
						makeData[normalizedModel][submodel]
					) {
						const esSubs =
							makeData[normalizedModel][submodel][0]?.Easysearch?.Submodel ||
							[];
						if (esSubs.length > 0) {
							normalizedSubmodel = esSubs[0];
							const subMatch2 = matchingVehicles.some(
								(v) =>
									(v.submodel || "").toLowerCase() ===
									normalizedSubmodel.toLowerCase(),
							);
							if (subMatch2) resolvedFields.submodel = normalizedSubmodel;
						}
					}
				} catch (_) {}
			}
		}

		// Resolve engine
		const normalizedEngine = (engine || "").trim();
		if (normalizedEngine) {
			const lEng = normalizedEngine.toLowerCase();
			const engMatch = vehicles.some(
				(v) =>
					v.make.toLowerCase() === lMake &&
					v.model.toLowerCase() === normalizedModel.toLowerCase() &&
					(!resolvedFields.submodel ||
						(v.submodel || "").toLowerCase() ===
							resolvedFields.submodel.toLowerCase()) &&
					(v.engine || "").toLowerCase() === lEng,
			);
			if (engMatch) resolvedFields.engine = normalizedEngine;
		}

		resolvedFields.canResolveComplete = !!(
			resolvedFields.submodel && resolvedFields.engine
		);
		return resolvedFields;
	} catch (error) {
		console.error("[NativeVIN] Error validating vehicle data:", error);
		return resolvedFields;
	}
}

// Helper function to normalize and validate against EasySearch

async function validateAndNormalizeVehicleData(decodedData) {
	// When native fitment is primary, validate against YMM JSON files
	// instead of polling EasySearch dropdown DOM elements.
	if (window.__PARTIFY_NATIVE_FITMENT === "primary") {
		return validateVehicleDataNative(decodedData);
	}

	const { year, make, model, submodel, engine } = decodedData;

	let resolvedFields = {
		year: null,
		make: null,
		model: null,
		submodel: null,
		engine: null,
		canResolveYMM: false,
		canResolveComplete: false,
	};

	//Validdate each field
	try {
		// **IMPORTANT**: Wait for each select sequentially since they're hierarchical
		resolvedFields.year = await setSelectValueWhenReady(
			easysearchInputYear,
			(year || "").toString(),
			4000,
		);

		if (!resolvedFields.year) {
			console.warn("Cannot resolve decoded year in EasySearch");
			return resolvedFields;
		}

		// Wait for make dropdown to populate after year selection
		await new Promise((resolve) => setTimeout(resolve, 50));
		resolvedFields.make = await setSelectValueWhenReady(
			easysearchInputMake,
			make || "",
			3000,
		);

		if (!resolvedFields.make) {
			console.warn("Cannot resolve decoded make in EasySearch");
			return resolvedFields;
		}

		// Step 3: Try to resolve Model using vinLookup if direct match fails
		await new Promise((resolve) => setTimeout(resolve, 50));
		let normalizedModel = model;

		// First try direct match
		// Wait for model dropdown to populate after make selection
		let modelVal = await setSelectValueWhenReady(
			easysearchInputModel,
			model || "",
			3000,
		);

		// If direct match fails, try vinLookup
		if (!modelVal) {
			try {
				const vinLookupData = await loadVinLookup();
				const hit = vinLookupData.find((x) => {
					return (
						String(x.Model || "")
							.toLowerCase()
							.replace(/[^a-z0-9]/g, "")
							.trim() ===
						String(model)
							.toLowerCase()
							.replace(/[^a-z0-9]/g, "")
							.trim()
					);
				});
				if (hit && hit["Easysearch Model"]) {
					normalizedModel = hit["Easysearch Model"];
					modelVal = await setSelectValueWhenReady(
						easysearchInputModel,
						normalizedModel,
						3000,
					);
				}
			} catch (error) {
				console.error(error);
				console.warn("VIN lookup failed for model:", error);
			}
		}
		resolvedFields.model = modelVal;

		if (!resolvedFields.model) {
			console.warn("Cannot resolve decoded model in EasySearch");
			window.MISMATCH_JSON_LOOKUP_MISS = true;
			return resolvedFields;
		}
		// At this point, Y/M/M resolved successfully
		resolvedFields.canResolveYMM = true;

		// Now try submodel and engine (optional fields)
		// Step 4: Try to resolve Submodel using submodelMap
		await new Promise((resolve) => setTimeout(resolve, 50));
		let normalizedSubmodel = submodel;
		const preferredIoniqSubmodel = buildIoniqEasySearchSubmodel(
			make,
			model,
			normalizedSubmodel,
		);

		if (submodel || preferredIoniqSubmodel) {
			let submodelVal = "";
			const preferredPairAwareSubmodel =
				await resolveMappedSubmodelForAliasedModel(
					make,
					model,
					normalizedModel,
					submodel,
					async (mappedSubmodel) => {
						const selectedSubmodel = await setSelectValueWhenReady(
							easysearchInputSubmodel,
							mappedSubmodel,
							800,
						);
						if (selectedSubmodel) submodelVal = selectedSubmodel;
						return !!selectedSubmodel;
					},
				);
			if (preferredPairAwareSubmodel.submodel) {
				normalizedSubmodel = preferredPairAwareSubmodel.submodel;
			}
			if (preferredIoniqSubmodel) {
				submodelVal = await setSelectValueWhenReady(
					easysearchInputSubmodel,
					preferredIoniqSubmodel,
					800,
				);
				if (submodelVal) normalizedSubmodel = preferredIoniqSubmodel;
			}

			// Try direct match when there is no preferred Ioniq mapping.
			if (
				!submodelVal &&
				submodel &&
				!preferredPairAwareSubmodel.mappingFound
			) {
				submodelVal = await setSelectValueWhenReady(
					easysearchInputSubmodel,
					submodel || "",
					800,
				);
			}

			// If direct match fails, try submodelLoopup
			if (
				!submodelVal &&
				make &&
				model &&
				year &&
				!preferredPairAwareSubmodel.mappingFound
			) {
				try {
					const submodelMap = await loadSubmodelMap();
					// navigat to Make -> model -> decoded submodel
					const makeData = submodelMap[make];

					if (makeData && makeData[normalizedModel]) {
						const modelData = makeData[normalizedModel];

						//Check if the decoded submodel exists as a key in JSON
						if (modelData[submodel]) {
							// get easysearch submodel
							const easysearchSubmodels =
								modelData[submodel][0]?.Easysearch?.Submodel || [];
							if (easysearchSubmodels.length > 0) {
								normalizedSubmodel = easysearchSubmodels[0];
								// try to set it in easysearch
								submodelVal = await setSelectValueWhenReady(
									easysearchInputSubmodel,
									normalizedSubmodel,
									800,
								);
								console.log(
									`Submodel mapped: "${submodel}" -> "${normalizedSubmodel}"`,
								);
							}
						}
					}
				} catch (error) {
					console.warn("Submodel map lookup failed:", error);
				}
			}
			resolvedFields.submodel = submodelVal;
			console.log("Final submodel resolution:", resolvedFields.submodel);
		}

		// try to resolve engine
		await new Promise((resolve) => setTimeout(resolve, 50));
		if (engine) {
			resolvedFields.engine = await setSelectValueWhenReady(
				easysearchInputEngine,
				engine || "",
				500,
			);
		}

		// Determine if complete vehicle is resolved
		resolvedFields.canResolveComplete = !!(
			resolvedFields.submodel && resolvedFields.engine
		);

		console.log("Vehicle validation complete:", resolvedFields);

		// Rollback selections to avoid side effects
		await setSelectValueWhenReady(easysearchInputYear, "");
		await setSelectValueWhenReady(easysearchInputMake, "");
		await setSelectValueWhenReady(easysearchInputModel, "");
		await setSelectValueWhenReady(easysearchInputSubmodel, "");
		await setSelectValueWhenReady(easysearchInputEngine, "");

		return resolvedFields;
	} catch (error) {
		console.error("Error validating vehicle data:", error);
		return resolvedFields;
	}
}

function openQuestionModal({
	scrollLockClass = "no-scroll-paint-swatch",
	accordionSelector = ".accordion-section",
	overlaySelector = ".product-unfolded-popup-dark-overlay",
	modalSelector = ".unfolded-popup-container",
	modalContentSelector = ".popup-modal-content",
	fetchContentFn = null, // async function to fetch content, if needed
}) {
	// Save scroll position globally
	// window.paintModalScrollPosition = window.scrollY || document.documentElement.scrollTop;

	// Optionally expand accordions
	if (accordionSelector) {
		const accordionSections = document.querySelectorAll(accordionSelector);
		accordionSections.forEach((section) => {
			const content = section.querySelector(".accordion-content");
			const circlePlus = section.querySelector(".circle_plus");
			const circleNone = section.querySelector(".circle_none");
			const isOpen = content.classList.contains("open");
			requestAnimationFrame(() => {
				setTimeout(() => {
					if (!isOpen) {
						content.classList.add("open");
						content.style.maxHeight = content.scrollHeight + "px";
						if (circlePlus) circlePlus.style.display = "none";
						if (circleNone) circleNone.style.display = "block";
					}
				}, 500);
			});
		});
	}

	// Lock scroll
	document.body.classList.add(scrollLockClass);

	// Hide Shopify chat
	if (typeof hideShopifyChat === "function") hideShopifyChat();

	// Show overlay
	if (overlaySelector) {
		const overlay = document.querySelector(overlaySelector);
		if (overlay) overlay.style.display = "block";
	}

	// Show modal and set content
	if (modalSelector) {
		const modal = document.querySelector(modalSelector);
		if (modal) modal.style.display = "block";
	}
	if (fetchContentFn && modalContentSelector) {
		const result = fetchContentFn();
		if (result && typeof result.then === "function") {
			// If fetchContentFn returns a Promise
			result
				.then((pageContent) => {
					const modalContent = document.querySelector(modalContentSelector);
					if (modalContent) modalContent.innerHTML = pageContent;
				})
				.catch((error) => {
					console.error("Error fetching modal content:", error);
				});
		}
		// If fetchContentFn is synchronous, it already handled the content
	}
}

function showQuestionModal(
	content,
	showContent,
	modalElement,
	containerElement,
) {
	const modalContent = document.querySelector(modalElement);
	if (showContent) {
		modalContent.innerHTML = content;
	}
	document.querySelector(containerElement).style.display = "block";
}

function closeQuestionModal(
	noScrollElement,
	scrollPosition,
	containerElement,
	modalElement,
	overlayElement,
) {
	// Remove scroll lock and restore scroll position
	if (noScrollElement) document.body.classList.remove(noScrollElement);
	window.scrollTo(0, scrollPosition || 0); // Restore scroll position

	// Show Shopify chat and hide overlay
	if (!isMismatch) {
		showShopifyChat();
	}
	hideQuestionModalOverlay(overlayElement);
	hideQuestionModal(containerElement, modalElement);
}

function hideQuestionModal(containerElement, modalElement) {
	const container = document.querySelector(containerElement);
	const modal = document.querySelector(modalElement);
	if (!container || !modal) return;

	// Set display immediately instead of waiting for unreliable animationend event
	modal.style.animation = "";
	container.style.display = "none";
}

function hideQuestionModalOverlay(overlayElement) {
	const overlay = document.querySelector(overlayElement);
	if (overlay) overlay.style.display = "none";
}

function showQuestionModalOverlay(overlayElement) {
	const overlay = document.querySelector(overlayElement);
	if (overlay) overlay.style.display = "block";
}

function accordionButtonHandle(button, section) {
	const accordionSection = button.closest(section);
	const content = accordionSection.querySelector(".accordion-content");
	const circlePlus = accordionSection.querySelector(".circle_plus");
	const circleNone = accordionSection.querySelector(".circle_none");

	// Check if the clicked accordion is already open
	const isOpen = content.classList.contains("open");

	if (isOpen) {
		// Open the clicked accordion
		content.classList.remove("open");
		content.style.maxHeight = 0;

		// Show the minus icon and hide the plus icon for the active accordion
		circlePlus.style.display = "block";
		circleNone.style.display = "none";
	}

	if (!isOpen) {
		// Open the clicked accordion
		content.classList.add("open");
		content.style.maxHeight = content.scrollHeight + "px";

		// Show the minus icon and hide the plus icon for the active accordion
		circlePlus.style.display = "none";
		circleNone.style.display = "block";
	}
}

function hideShopifyChat() {
	const shopifyChat = document.getElementById("shopify-chat");
	if (shopifyChat) {
		shopifyChat.style.display = "none";
	}
}

// Function to show Shopify chat widget
function showShopifyChat() {
	const shopifyChat = document.getElementById("shopify-chat");
	if (shopifyChat) {
		shopifyChat.style.display = "block";
	}
}

function pushLicenseToCart() {
	const terms = window.getSearchTerms?.();
	if (!terms || !terms.length) {
		console.warn("No search terms found. Plate/State not added to cart.");
		return;
	}

	const { plate, state } = terms[0]; // always use the first one
	const forms = document.querySelectorAll('form[action^="/cart/add"]');
	if (!forms.length) {
		console.warn("No add-to-cart forms found. Plate/State not attached.");
		return;
	}

	forms.forEach((form) => {
		// Hidden Plate field
		let plateField = form.querySelector('input[name="properties[Plate]"]');
		if (!plateField) {
			plateField = document.createElement("input");
			plateField.type = "hidden";
			plateField.name = "properties[Plate]";
			form.appendChild(plateField);
		}

		// Hidden State field
		let stateField = form.querySelector('input[name="properties[State]"]');
		if (!stateField) {
			stateField = document.createElement("input");
			stateField.type = "hidden";
			stateField.name = "properties[State]";
			form.appendChild(stateField);
		}

		// Sync right before submit
		form.addEventListener(
			"submit",
			(e) => {
				plateField.value = (plate || "").trim().toUpperCase();
				stateField.value = (state || "").trim().toUpperCase();

				// disable duplicates if any exist
				[plateField, stateField].forEach((field) => {
					form.querySelectorAll(`input[name="${field.name}"]`).forEach((el) => {
						if (el !== field) el.disabled = true;
					});
				});
			},
			{ capture: true },
		);
	});
}

// Helper function to find available instance
function getVisibleVinDecoderElements() {
	// Find all wrappers
	const wrappers = document.querySelectorAll(
		'[id^="vin-decoder-textbox-for-verification-wrapper-"]',
	);
	// Find the visible one
	for (const wrapper of wrappers) {
		// Check if wrapper has 'show' class AND is actually visible in DOM
		if (wrapper.classList.contains("show") && wrapper.offsetParent !== null) {
			return {
				wrapper: wrapper,
				input: wrapper.querySelector(
					'[id^="vin-decoder-textbox-for-verification-"]',
				),
				submitBtn: wrapper.querySelector(".vin-decoder-submit-btn"),
				validationError: wrapper.querySelector(".vin-validation-error"),
				vinAfterSelection: wrapper.querySelector(
					'[id^="vin-after-paint-code-selection-"]',
				),
				vinInputContainer: wrapper.querySelector(
					".vin-input-label-and-textbox",
				),
			};
		}
	}

	return null;
}
